import type { Axis } from './hypotheses/dictionaryApi'

export type Lang = 'en' | 'ru' | 'uk'

/** The language switcher's choices. The code tag is `uk`; the switcher reads "UA". */
export const LANG_OPTIONS: Array<{ value: Lang; label: string }> = [
  { value: 'en', label: 'EN' },
  { value: 'ru', label: 'RU' },
  { value: 'uk', label: 'UA' },
]

/**
 * The frontend half of the error-code contract: backend/crates/api/src/auth/error.rs
 * serializes the same nine codes snake_case. A contract test compares the two sets
 * so neither side can omit a case.
 */
export const AUTH_ERROR_CODES = [
  'access_denied',
  'unauthorized',
  'invalid_attempt',
  'unsupported_account',
  'provider_error',
  'forbidden_origin',
  'forbidden',
  'rate_limited',
  'service_unavailable',
] as const

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number]

export function isAuthErrorCode(value: string): value is AuthErrorCode {
  return (AUTH_ERROR_CODES as readonly string[]).includes(value)
}

export const AUTH_COPY: Record<Lang, Record<AuthErrorCode, string>> = {
  en: {
    access_denied: 'Sign-in was cancelled. Try again when ready.',
    unauthorized: 'This account cannot access Grovus. Contact your administrator.',
    invalid_attempt: 'This sign-in attempt is invalid or expired. Start again.',
    unsupported_account: 'Use a Gmail or Google Workspace account.',
    provider_error: 'Google sign-in is temporarily unavailable. Try again.',
    forbidden_origin: 'Reload this page and try again.',
    forbidden: 'Your role in this company does not allow this.',
    rate_limited: 'Too many sign-in attempts. Please wait and try again.',
    service_unavailable: 'Grovus is temporarily unavailable. Try again.',
  },
  ru: {
    access_denied: 'Вход отменён. Попробуйте снова, когда будете готовы.',
    unauthorized: 'У этой учётной записи нет доступа к Grovus. Обратитесь к администратору.',
    invalid_attempt: 'Попытка входа недействительна или истекла. Начните заново.',
    unsupported_account: 'Используйте учётную запись Gmail или Google Workspace.',
    provider_error: 'Вход через Google временно недоступен. Попробуйте снова.',
    forbidden_origin: 'Обновите страницу и попробуйте снова.',
    forbidden: 'Ваша роль в этой компании не позволяет это сделать.',
    rate_limited: 'Слишком много попыток входа. Подождите и попробуйте снова.',
    service_unavailable: 'Grovus временно недоступен. Попробуйте снова.',
  },
  uk: {
    access_denied: 'Вхід скасовано. Спробуйте знову, коли будете готові.',
    unauthorized: 'Цей обліковий запис не має доступу до Grovus. Зверніться до адміністратора.',
    invalid_attempt: 'Ця спроба входу недійсна або застаріла. Почніть знову.',
    unsupported_account: 'Використовуйте обліковий запис Gmail або Google Workspace.',
    provider_error: 'Вхід через Google тимчасово недоступний. Спробуйте знову.',
    forbidden_origin: 'Оновіть сторінку та спробуйте знову.',
    forbidden: 'Ваша роль у цій компанії не дозволяє цього.',
    rate_limited: 'Забагато спроб входу. Зачекайте та спробуйте знову.',
    service_unavailable: 'Grovus тимчасово недоступний. Спробуйте знову.',
  },
}

type AdminRole = 'owner' | 'admin' | 'member' | 'viewer'

/**
 * The platform admin panel (GRO-115, screens 11 and 12). No screen draws `alreadyDecided`,
 * `identityConflict`, `decisionFailed`, `decisionUncertain` or `requestsFailed`: the
 * request queue's decision notices and its failed state.
 */
export interface AdminCopy {
  admin: string; account: string; menu: string; navLabel: string; soon: string
  navUsers: string; navCompanies: string; navIngestion: string; navLog: string
  roles: Record<AdminRole, string>
  months: string[]; monthsShort: string[]
  usersTitle: string; usersSub: string; email: string; state: string; admitted: string
  signin: string; clients: string; admittedOnly: string; noSignin: string; noClients: string
  usersFailed: string; retry: string
  stateApproved: string; stateDisabled: string; statePlatformAdmin: string
  invited: (company: string, role: AdminRole, days: number) => string
  requestsTitle: string; requestsSub: string; waiting: string; requested: string; kind: string
  message: string; approve: string; reject: string; cancel: string
  decidedTitle: string; decision: string; decidedBy: string; when: string; empty: string
  approved: string; rejected: string; kindAdmission: string; kindCreation: string
  memberIn: (role: AdminRole, company: string) => string
  confirmApproveAdmission: (email: string) => string
  confirmApproveCreation: (email: string) => string
  confirmRejectAdmission: (email: string) => string
  confirmRejectCreation: (email: string) => string
  alreadyDecided: (status: 'approved' | 'rejected', by: string, when: string) => string
  identityConflict: string
  decisionFailed: string
  decisionUncertain: string
  requestsFailed: string
  /**
   * E6 (GRO-135): screens 13 and 14, and the glossary of screens 15 to 17, which later
   * slices draw: `allStatuses`, `deletionRequested`, `purgeOn`, `rename` to `noEligibleOwner`.
   */
  navPortfolios: string
  companiesTitle: string; companiesSub: string; countOf: (shown: number, total: number) => string
  searchCompanies: string; showDemo: string; allStatuses: string
  companyName: string; status: string; created: string; owner: string; owners: string; members: string
  accounts: string; portfolios: string; none: string
  active: string; deletionRequested: string; purgeOn: (date: string) => string; demo: string
  /** GRO-138, D7: the frozen states of screens 13 and 14 and the restore flow. */
  purgeOverdue: string; purgeLine: (date: string, email: string) => string
  delText: (requestedOn: string, email: string, purgeAfter: string) => string
  restoreText: (name: string) => string; takenErr: (email: string, company: string) => string
  takenRetry: string; takenNote: string; emptyName: string; restoreFailed: string
  purgeText: (name: string) => string; purgeTyped: string; purgeFailed: string
  origin: Record<'self' | 'admin' | 'signup_demo', (email: string) => string>; originUnknown: string
  createdOn: (date: string) => string
  companiesFailed: string; noMatch: string; demoHidden: string
  role: string; joined: string; membersNote: string
  claimedPortfolios: string; managedOnPortfolios: string; demoNoPortfolio: string
  connected: string; notConnected: string; noLongerShared: string
  companyFailed: string; companyGone: string; back: string
  /** GRO-136: the rename form of screen 14. */
  nameLabel: string; renameTaken: (email: string, name: string) => string; nameTaken: (name: string) => string
  renameFailed: string
  /** Beside the disabled Rename of a frozen company (GRO-138, D7). */
  renameLocked: string
  rename: string; save: string; restore: string; restoreAs: string; purgeNow: string
  typeToConfirm: string; requestedBy: string; createCompany: string; noEligibleOwner: string
  /** Screen 15 (GRO-137): the create form, its refusals and the created banner. */
  namePlaceholder: string; ownerPlaceholder: string; createSubmit: string
  createNote: string; ownerPrereq: string
  duplicateFor: (email: string, name: string) => string; reservedName: string
  ownerIneligible: string; createUncertain: string
  createdBanner: (name: string, email: string) => string; open: string
  unclaimed: string; claim: string; unclaim: string; rediscover: string; lastDiscovery: string
  refreshFacts: string; conversionFacts: string; campaignObjectives: string; mainActionTypes: string
  factsWindow: string; collected: string; completeness: string
  observed: string; absent: string; unknown: string
  queued: string; running: string; waitingState: string; done: string; failed: string; viewResult: string
  /** GRO-141: screen 16, the discovery header, its request card and the portfolio table. */
  portfoliosSub: string; portfolio: string; claimColumn: string; never: string
  rediscoverBusy: string; rediscoverFailed: string; portfoliosFailed: string; noPortfolios: string
  connectedCount: (n: number) => string
  requestedAtBy: (when: string, email: string | null) => string
  waitingForWorker: string
  startedPage: (when: string, page: number, run: number) => string
  waitReasons: Record<'budget' | 'throttled' | 'backoff', string>
  nextAttempt: (when: string) => string
  finishedStaged: (when: string, portfolios: number, accounts: number) => string
  failuresLastError: (failures: number, error: string) => string
  lastCompleteListing: string; retryNote: string
  /** GRO-144: the claim, unclaim and refused panels of screen 17. */
  claimLead: (portfolio: string) => string
  claimCompany: string; noClaimableCompany: string; notCollected: string; manualCheck: string
  /** GRO-145: an account's conversion facts in the claim and facts panels. */
  alreadyRequested: string; collecting: string; campaignsTruncated: string; noCampaigns: string
  daysOf90: (days: number) => string
  dayOf90: (day: number) => string
  collectedOn: (when: string) => string
  windowRange: (since: string, until: string, timezone: string) => string
  factsStarted: (when: string, day: number, run: number) => string
  factsFinished: (when: string, days: number) => string
  factsFailed: string; accountGone: string
  unclaimTitle: (portfolio: string, company: string) => string
  unclaimText: (company: string) => string
  unclaimRefusedTitle: (portfolio: string, company: string) => string
  unclaimRefusedText: string; close: string
  claimRefusals: {
    claimedElsewhere: (company: string) => string
    frozen: string; unknownCompany: string; demo: string; notClaimed: string; notFound: string; failed: string
  }
}

/** 1 день, 2 дня, 5 дней, 21 день. */
function ruDays(n: number): string {
  const tens = n % 100
  const ones = n % 10
  if (ones === 1 && tens !== 11) return `${n} день`
  if (ones >= 2 && ones <= 4 && (tens < 12 || tens > 14)) return `${n} дня`
  return `${n} дней`
}

/** 1 день, 2 дні, 5 днів, 21 день. */
function ukDays(n: number): string {
  const tens = n % 100
  const ones = n % 10
  if (ones === 1 && tens !== 11) return `${n} день`
  if (ones >= 2 && ones <= 4 && (tens < 12 || tens > 14)) return `${n} дні`
  return `${n} днів`
}

/** A name that closes a sentence, without a second period when the name already ends in one. */
function closing(name: string): string {
  return name.endsWith('.') ? name : `${name}.`
}

const ROLES_EN: Record<AdminRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' }
const ROLES_RU: Record<AdminRole, string> = { owner: 'Владелец', admin: 'Администратор', member: 'Участник', viewer: 'Наблюдатель' }
const ROLES_UK: Record<AdminRole, string> = { owner: 'Власник', admin: 'Адміністратор', member: 'Учасник', viewer: 'Спостерігач' }

export const ADMIN_COPY: Record<Lang, AdminCopy> = {
  en: {
    admin: 'Admin', account: 'Account', menu: 'Menu', navLabel: 'Admin navigation', soon: 'Designed later',
    navUsers: 'Users', navCompanies: 'Companies', navIngestion: 'Ingestion health', navLog: 'Platform log',
    roles: ROLES_EN,
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    usersTitle: 'Users', usersSub: 'Everyone who has been admitted. Read-only for now.', email: 'Email',
    state: 'State', admitted: 'Admitted', signin: 'Last sign-in', clients: 'Clients', admittedOnly: 'Admitted',
    noSignin: 'No recorded sign-in', noClients: 'No clients', usersFailed: 'Could not load users.', retry: 'Retry',
    stateApproved: 'Approved to create clients', stateDisabled: 'Disabled', statePlatformAdmin: 'Platform admin',
    invited: (company, role, days) => `Invited to ${company} · ${ROLES_EN[role]} · expires in ${days} ${days === 1 ? 'day' : 'days'}`,
    requestsTitle: 'Access requests', requestsSub: 'Oldest first', waiting: 'Waiting', requested: 'Requested',
    kind: 'Kind', message: 'Message', approve: 'Approve', reject: 'Reject', cancel: 'Cancel',
    decidedTitle: 'Decided · last 10', decision: 'Decision', decidedBy: 'Decided by', when: 'When',
    empty: 'No requests waiting.', approved: 'Approved', rejected: 'Rejected',
    kindAdmission: 'New user', kindCreation: 'Admitted · wants to create clients',
    memberIn: (role, company) => `${ROLES_EN[role]} in ${company}`,
    confirmApproveAdmission: (email) => `Approve ${email}? They will be able to sign in and create clients.`,
    confirmApproveCreation: (email) => `Approve ${email}? They will be able to create clients. Their memberships do not change.`,
    confirmRejectAdmission: (email) => `Reject ${email}? They stay unable to sign in.`,
    confirmRejectCreation: (email) => `Reject ${email}? Their current access is unchanged.`,
    alreadyDecided: (status, by, when) => `Already ${status === 'approved' ? 'approved' : 'rejected'} by ${by}, ${when}.`,
    identityConflict: 'This email is bound to another Google account, so the request cannot be approved.',
    decisionFailed: 'The decision was not confirmed. Try again.',
    decisionUncertain: 'Could not confirm whether the decision was saved.',
    requestsFailed: 'Could not load access requests.',
    navPortfolios: 'Meta portfolios',
    companiesTitle: 'Companies', companiesSub: 'Every company on the platform. Demo companies are hidden unless you show them.',
    countOf: (shown, total) => `${shown} of ${total}`,
    searchCompanies: 'Search by name or Owner email', showDemo: 'Show demo companies', allStatuses: 'All statuses',
    companyName: 'Name', status: 'Status', created: 'Created', owner: 'Owner', owners: 'Owners', members: 'Members',
    accounts: 'Accounts', portfolios: 'Portfolios', none: 'None',
    active: 'Active', deletionRequested: 'Deletion requested', purgeOn: (date) => `Purge after ${date}`, demo: 'Demo',
    purgeOverdue: 'Purge overdue',
    purgeLine: (date, email) => `Purge after ${date} · requested by ${email}`,
    delText: (on, email, after) => `Requested on ${on} by ${email}. The company is hidden from its members and frozen: no ingestion, no changes. It becomes eligible for purge after ${after} unless it is restored.`,
    restoreText: (name) => `Restore ${name}? It returns to all its members' switchers at once, with its accounts, portfolio and data.`,
    takenErr: (email, company) => `${email} already has a client named ${company}.`,
    takenRetry: 'Restore it under a different name.',
    takenNote: 'The name changes for everyone who gets the company back.',
    emptyName: 'Enter a name.', restoreFailed: 'The company was not restored. Try again.',
    purgeText: (name) => `Purge ${name} now? The company, its accounts, its figures and every member's access are removed from Grovus, and its Meta portfolios become free to claim. This cannot be undone. Type the company name to confirm.`,
    purgeTyped: 'Company name', purgeFailed: 'The company was not purged. Try again.',
    origin: {
      self: (email) => `Self-service · ${email}`,
      admin: (email) => `Admin-created · ${email}`,
      signup_demo: (email) => `Signup demo · ${email}`,
    },
    originUnknown: 'Origin unknown',
    createdOn: (date) => `Created ${date}`,
    companiesFailed: 'Could not load companies.', noMatch: 'No companies match.', demoHidden: 'Demo companies are hidden.',
    role: 'Role', joined: 'Joined', membersNote: "Members are managed by the company's Owners and Admins.",
    claimedPortfolios: 'Claimed portfolios', managedOnPortfolios: 'Managed on the Meta portfolios page.',
    demoNoPortfolio: 'A demo company has no portfolio. Its user can delete it at once.',
    connected: 'Connected', notConnected: 'Not connected', noLongerShared: 'No longer shared',
    companyFailed: 'Could not load the company.', companyGone: 'This company no longer exists.', back: 'Back',
    nameLabel: 'Name',
    renameTaken: (email, name) => `${email} already has a client named ${closing(name)} Every member must end up with unique names.`,
    nameTaken: (name) => `A member already has a client named ${closing(name)} Every member must end up with unique names.`,
    renameFailed: 'Could not confirm the rename. Check the name and try again.',
    renameLocked: 'Restore the company to rename it.',
    rename: 'Rename', save: 'Save', restore: 'Restore', restoreAs: 'Restore as', purgeNow: 'Purge now',
    typeToConfirm: 'Type the company name to confirm', requestedBy: 'Requested by', createCompany: 'Create company',
    noEligibleOwner: 'No eligible Owner yet. Approve the client under Access requests first.',
    namePlaceholder: 'Company name', ownerPlaceholder: 'Choose an Owner', createSubmit: 'Create',
    createNote: 'The Owner becomes a member at once, without an invitation. The company is real and starts without a demo account.',
    ownerPrereq: 'Only a user who has signed in, been approved and is enabled can be the Owner. A client without a user signs in first; approve them under Access requests.',
    duplicateFor: (email, name) => `${email} already has a client named ${name}. Choose another name or another Owner.`,
    reservedName: 'The name "Демо" is reserved for this user\'s demo company. Choose another name.',
    ownerIneligible: 'This user can no longer be the Owner. Choose another.',
    createUncertain: 'Could not confirm the create. The company may already exist - check the list below before submitting again.',
    createdBanner: (name, email) => `${name} created. ${email} is its Owner.`, open: 'Open',
    unclaimed: 'Unclaimed', claim: 'Claim', unclaim: 'Unclaim', rediscover: 'Rediscover', lastDiscovery: 'Last discovery',
    refreshFacts: 'Refresh facts', conversionFacts: 'Conversion facts', campaignObjectives: 'Campaign objectives',
    mainActionTypes: 'Main action types', factsWindow: 'Window', collected: 'Collected', completeness: 'Completeness',
    observed: 'Observed', absent: 'Absent', unknown: 'Unknown',
    queued: 'Queued', running: 'Running', waitingState: 'Waiting', done: 'Done', failed: 'Failed', viewResult: 'View result',
    portfoliosSub: "Business portfolios that share ad accounts with the Grovus partner portfolio. Facts are read with Grovus's own credential.",
    portfolio: 'Portfolio', claimColumn: 'Claim', never: 'never',
    rediscoverBusy: 'A rediscovery is already requested', rediscoverFailed: 'Could not request a rediscovery.',
    portfoliosFailed: 'Could not load the portfolios.', noPortfolios: 'Discovery has found no portfolio yet.',
    connectedCount: (n) => `${n} connected`,
    requestedAtBy: (when, email) => (email ? `Requested ${when} by ${email}` : `Requested ${when}`),
    waitingForWorker: 'Waiting for a free worker',
    startedPage: (when, page, run) => `Started ${when} · page ${page} · run ${run}`,
    waitReasons: { budget: 'Waiting for the next run of the worker', throttled: 'Meta is throttling this app', backoff: 'Retrying after a failure' },
    nextAttempt: (when) => `Next attempt at ${when}`,
    finishedStaged: (when, portfolios, accounts) => `Finished ${when} · ${portfolios} portfolios, ${accounts} accounts`,
    failuresLastError: (failures, error) => `${failures} failures · last error: ${error}`,
    lastCompleteListing: 'The table shows the last complete listing.', retryNote: 'Retry creates a new request',
    claimLead: (portfolio) => `Claim ${portfolio} for`,
    claimCompany: 'Company', noClaimableCompany: 'No active company to claim for.', notCollected: 'No complete collection yet',
    manualCheck: "Facts are not collected yet. Check each account's purchase event in Ads Manager before you claim.",
    alreadyRequested: 'Already requested', collecting: 'collecting', campaignsTruncated: '500+ campaigns', noCampaigns: 'No campaigns',
    daysOf90: (days) => `${days} of 90 days`,
    dayOf90: (day) => `day ${day} of 90`,
    collectedOn: (when) => `collected ${when}`,
    windowRange: (since, until, timezone) => `${since} to ${until} · ${timezone}`,
    factsStarted: (when, day, run) => `Started ${when} · day ${day} of 90 · run ${run}`,
    factsFinished: (when, days) => `Finished ${when} · ${days} of 90 days`,
    factsFailed: 'Could not request the facts.', accountGone: 'Discovery no longer lists this account.',
    unclaimTitle: (portfolio, company) => `Unclaim ${portfolio} from ${company}?`,
    unclaimText: (company) => `${company} stops reading its accounts. Its data stays in the company.`,
    unclaimRefusedTitle: (portfolio, company) => `${portfolio} cannot be unclaimed while its accounts are connected to ${company}:`,
    unclaimRefusedText: 'The claim stays while these accounts are connected.', close: 'Close',
    claimRefusals: {
      claimedElsewhere: (company) => `${company} already claims this portfolio.`,
      frozen: 'The company is frozen.', unknownCompany: 'The company no longer exists.',
      demo: 'A demo company cannot claim a portfolio.',
      notClaimed: 'The claim has changed. Reload the page to see who holds it.',
      notFound: 'Discovery no longer lists this portfolio.', failed: 'Could not save the change. Try again.',
    },
  },
  ru: {
    admin: 'Админ', account: 'Аккаунт', menu: 'Меню', navLabel: 'Навигация администратора', soon: 'Будет позже',
    navUsers: 'Пользователи', navCompanies: 'Компании', navIngestion: 'Состояние загрузки данных', navLog: 'Журнал платформы',
    roles: ROLES_RU,
    months: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
    monthsShort: ['янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июн.', 'июл.', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'],
    usersTitle: 'Пользователи', usersSub: 'Все, кого допустили. Пока только чтение.', email: 'Эл. почта',
    state: 'Состояние', admitted: 'Допущен', signin: 'Последний вход', clients: 'Компании', admittedOnly: 'Допущен',
    noSignin: 'Нет записанных входов', noClients: 'Нет компаний', usersFailed: 'Не удалось загрузить пользователей.', retry: 'Повторить',
    stateApproved: 'Одобрен для создания компаний', stateDisabled: 'Отключён', statePlatformAdmin: 'Администратор платформы',
    invited: (company, role, days) => `Приглашение в ${company} · ${ROLES_RU[role]} · истекает через ${ruDays(days)}`,
    requestsTitle: 'Запросы доступа', requestsSub: 'Сначала старые', waiting: 'Ожидают', requested: 'Запрошено',
    kind: 'Тип', message: 'Сообщение', approve: 'Одобрить', reject: 'Отклонить', cancel: 'Отмена',
    decidedTitle: 'Решено · последние 10', decision: 'Решение', decidedBy: 'Кто решил', when: 'Когда',
    empty: 'Нет запросов в ожидании.', approved: 'Одобрено', rejected: 'Отклонено',
    kindAdmission: 'Новый пользователь', kindCreation: 'Допущен · хочет создавать компании',
    memberIn: (role, company) => `${ROLES_RU[role]} в ${company}`,
    confirmApproveAdmission: (email) => `Одобрить ${email}? Человек сможет входить и создавать компании.`,
    confirmApproveCreation: (email) => `Одобрить ${email}? Человек сможет создавать компании. Членство этого человека в компаниях не изменится.`,
    confirmRejectAdmission: (email) => `Отклонить ${email}? Человек по-прежнему не сможет войти.`,
    confirmRejectCreation: (email) => `Отклонить ${email}? Текущий доступ сохранится.`,
    alreadyDecided: (status, by, when) => `Уже ${status === 'approved' ? 'одобрено' : 'отклонено'}: ${by}, ${when}.`,
    identityConflict: 'Эта почта привязана к другому аккаунту Google, поэтому запрос нельзя одобрить.',
    decisionFailed: 'Решение не подтверждено. Попробуйте ещё раз.',
    decisionUncertain: 'Не удалось проверить, сохранено ли решение.',
    requestsFailed: 'Не удалось загрузить запросы доступа.',
    navPortfolios: 'Портфолио Meta',
    companiesTitle: 'Компании', companiesSub: 'Все компании платформы. Демокомпании скрыты, пока вы их не покажете.',
    countOf: (shown, total) => `${shown} из ${total}`,
    searchCompanies: 'Поиск по названию или эл. почте владельца', showDemo: 'Показать демокомпании', allStatuses: 'Все статусы',
    companyName: 'Название', status: 'Статус', created: 'Создана', owner: 'Владелец', owners: 'Владельцы', members: 'Участники',
    accounts: 'Аккаунты', portfolios: 'Портфолио', none: 'Нет',
    active: 'Активна', deletionRequested: 'Запрошено удаление', purgeOn: (date) => `Удаление после ${date}`, demo: 'Демо',
    purgeOverdue: 'Удаление просрочено',
    purgeLine: (date, email) => `Удаление после ${date} · запросил(а) ${email}`,
    delText: (on, email, after) => `Запрошено ${on}: ${email}. Компания скрыта от участников и заморожена: данные не загружаются, изменения невозможны. После ${after} её можно удалить окончательно, если её не восстановить.`,
    restoreText: (name) => `Восстановить ${name}? Компания сразу появится в списках компаний всех её участников вместе с аккаунтами, портфолио и данными.`,
    takenErr: (email, company) => `У ${email} уже есть компания с названием ${company}.`,
    takenRetry: 'Восстановите её под другим названием.',
    takenNote: 'Название изменится у всех, кто получит компанию обратно.',
    emptyName: 'Введите название.', restoreFailed: 'Компания не восстановлена. Попробуйте ещё раз.',
    purgeText: (name) => `Удалить ${name} окончательно? Компания, её аккаунты, показатели и доступ всех участников будут удалены из Grovus, а её портфолио Meta станут доступны для подключения. Это действие нельзя отменить. Введите название компании для подтверждения.`,
    purgeTyped: 'Название компании', purgeFailed: 'Компания не удалена. Попробуйте ещё раз.',
    origin: {
      self: (email) => `Самостоятельно · ${email}`,
      admin: (email) => `Создана администратором · ${email}`,
      signup_demo: (email) => `Демо при регистрации · ${email}`,
    },
    originUnknown: 'Происхождение неизвестно',
    createdOn: (date) => `Создана ${date}`,
    companiesFailed: 'Не удалось загрузить компании.', noMatch: 'Нет подходящих компаний.', demoHidden: 'Демокомпании скрыты.',
    role: 'Роль', joined: 'Присоединился', membersNote: 'Участниками управляют владельцы и администраторы компании.',
    claimedPortfolios: 'Привязанные портфолио', managedOnPortfolios: 'Управляются на странице «Портфолио Meta».',
    demoNoPortfolio: 'У демокомпании нет портфолио. Её пользователь может сразу её удалить.',
    connected: 'Подключён', notConnected: 'Не подключён', noLongerShared: 'Доступ больше не предоставлен',
    companyFailed: 'Не удалось загрузить компанию.', companyGone: 'Этой компании больше нет.', back: 'Назад',
    nameLabel: 'Название',
    renameTaken: (email, name) => `У ${email} уже есть компания с названием ${closing(name)} У каждого участника названия должны быть уникальными.`,
    nameTaken: (name) => `У одного из участников уже есть компания с названием ${closing(name)} У каждого участника названия должны быть уникальными.`,
    renameFailed: 'Не удалось подтвердить переименование. Проверьте название и попробуйте ещё раз.',
    renameLocked: 'Восстановите компанию, чтобы переименовать её.',
    rename: 'Переименовать', save: 'Сохранить', restore: 'Восстановить', restoreAs: 'Восстановить под названием', purgeNow: 'Удалить окончательно',
    typeToConfirm: 'Введите название компании для подтверждения', requestedBy: 'Запросил(а)', createCompany: 'Создать компанию',
    noEligibleOwner: 'Пока нет подходящего владельца. Сначала одобрите клиента в разделе «Запросы доступа».',
    namePlaceholder: 'Название компании', ownerPlaceholder: 'Выберите владельца', createSubmit: 'Создать',
    createNote: 'Владелец сразу становится участником, без приглашения. Компания настоящая и создаётся без демо-аккаунта.',
    ownerPrereq: 'Владельцем может стать только пользователь, который уже входил, был одобрен и не отключён. Если у клиента ещё нет пользователя, он сначала входит сам; одобрите его в разделе «Запросы доступа».',
    duplicateFor: (email, name) => `У ${email} уже есть компания с названием ${name}. Выберите другое название или другого владельца.`,
    reservedName: 'Название «Демо» зарезервировано для демокомпании этого пользователя. Выберите другое название.',
    ownerIneligible: 'Этот пользователь больше не может быть владельцем. Выберите другого.',
    createUncertain: 'Не удалось подтвердить создание. Возможно, компания уже создана - проверьте список ниже, прежде чем отправлять снова.',
    createdBanner: (name, email) => `${name} создана. Владелец - ${email}.`, open: 'Открыть',
    unclaimed: 'Не привязано', claim: 'Привязать', unclaim: 'Отвязать', rediscover: 'Обновить список', lastDiscovery: 'Последнее обновление списка',
    refreshFacts: 'Обновить данные', conversionFacts: 'Данные о конверсиях', campaignObjectives: 'Цели кампаний',
    mainActionTypes: 'Основные типы действий', factsWindow: 'Период', collected: 'Собрано', completeness: 'Полнота',
    observed: 'Есть', absent: 'Нет', unknown: 'Неизвестно',
    queued: 'В очереди', running: 'Выполняется', waitingState: 'Ожидает', done: 'Готово', failed: 'Ошибка', viewResult: 'Открыть результат',
    portfoliosSub: 'Бизнес-портфолио, которые делятся рекламными аккаунтами с партнёрским портфолио Grovus. Факты считываются собственными учётными данными Grovus.',
    portfolio: 'Портфолио', claimColumn: 'Привязка', never: 'не было',
    rediscoverBusy: 'Обновление списка уже запрошено', rediscoverFailed: 'Не удалось запросить обновление списка.',
    portfoliosFailed: 'Не удалось загрузить портфолио.', noPortfolios: 'Обновление списка пока не нашло ни одного портфолио.',
    connectedCount: (n) => `подключено: ${n}`,
    requestedAtBy: (when, email) => (email ? `Запрошено ${when}: ${email}` : `Запрошено ${when}`),
    waitingForWorker: 'Ожидает свободного обработчика',
    startedPage: (when, page, run) => `Начато ${when} · страница ${page} · запуск ${run}`,
    waitReasons: { budget: 'Ожидает следующего запуска обработчика', throttled: 'Meta ограничивает частоту запросов этого приложения', backoff: 'Повтор после сбоя' },
    nextAttempt: (when) => `Следующая попытка: ${when}`,
    finishedStaged: (when, portfolios, accounts) => `Завершено ${when} · портфолио: ${portfolios}, аккаунтов: ${accounts}`,
    failuresLastError: (failures, error) => `Сбоев: ${failures} · последняя ошибка: ${error}`,
    lastCompleteListing: 'В таблице показан последний полный список.', retryNote: 'Повтор создаёт новый запрос',
    claimLead: (portfolio) => `Привязать ${portfolio} к компании`,
    claimCompany: 'Компания', noClaimableCompany: 'Нет активной компании для привязки.', notCollected: 'Полного сбора ещё не было',
    manualCheck: 'Данные ещё не собраны. Перед привязкой проверьте событие покупки каждого аккаунта в Ads Manager.',
    alreadyRequested: 'Уже запрошено', collecting: 'идёт сбор', campaignsTruncated: '500+ кампаний', noCampaigns: 'Нет кампаний',
    daysOf90: (days) => `${days} из 90 дней`,
    dayOf90: (day) => `день ${day} из 90`,
    collectedOn: (when) => `собрано ${when}`,
    windowRange: (since, until, timezone) => `${since} - ${until} · ${timezone}`,
    factsStarted: (when, day, run) => `Начато ${when} · день ${day} из 90 · запуск ${run}`,
    factsFinished: (when, days) => `Завершено ${when} · ${days} из 90 дней`,
    factsFailed: 'Не удалось запросить данные.', accountGone: 'Этого аккаунта больше нет в списке.',
    unclaimTitle: (portfolio, company) => `Отвязать ${portfolio} от ${company}?`,
    unclaimText: (company) => `${company} перестаёт читать его аккаунты. Данные остаются в компании.`,
    unclaimRefusedTitle: (portfolio, company) => `${portfolio} нельзя отвязать, пока его аккаунты подключены к ${company}:`,
    unclaimRefusedText: 'Привязка остаётся, пока эти аккаунты подключены.', close: 'Закрыть',
    claimRefusals: {
      claimedElsewhere: (company) => `Это портфолио уже привязано к ${company}.`,
      frozen: 'Компания заморожена.', unknownCompany: 'Компании больше нет.',
      demo: 'Демо-компанию нельзя привязать к портфолио.',
      notClaimed: 'Привязка изменилась. Обновите страницу, чтобы увидеть, к кому оно привязано.',
      notFound: 'Этого портфолио больше нет в списке.', failed: 'Не удалось сохранить изменение. Попробуйте ещё раз.',
    },
  },
  uk: {
    admin: 'Адмін', account: 'Акаунт', menu: 'Меню', navLabel: 'Навігація адміністратора', soon: 'Буде пізніше',
    navUsers: 'Користувачі', navCompanies: 'Компанії', navIngestion: 'Стан завантаження даних', navLog: 'Журнал платформи',
    roles: ROLES_UK,
    months: ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'],
    monthsShort: ['січ.', 'лют.', 'бер.', 'квіт.', 'трав.', 'черв.', 'лип.', 'серп.', 'вер.', 'жовт.', 'лист.', 'груд.'],
    usersTitle: 'Користувачі', usersSub: 'Усі, кого допущено. Поки лише перегляд.', email: 'Ел. пошта',
    state: 'Стан', admitted: 'Допущено', signin: 'Останній вхід', clients: 'Компанії', admittedOnly: 'Допущено',
    noSignin: 'Немає записаних входів', noClients: 'Немає компаній', usersFailed: 'Не вдалося завантажити користувачів.', retry: 'Повторити',
    stateApproved: 'Схвалено для створення компаній', stateDisabled: 'Вимкнено', statePlatformAdmin: 'Адміністратор платформи',
    invited: (company, role, days) => `Запрошення до ${company} · ${ROLES_UK[role]} · спливає через ${ukDays(days)}`,
    requestsTitle: 'Запити на доступ', requestsSub: 'Спершу старіші', waiting: 'Очікують', requested: 'Запитано',
    kind: 'Тип', message: 'Повідомлення', approve: 'Схвалити', reject: 'Відхилити', cancel: 'Скасувати',
    decidedTitle: 'Вирішено · останні 10', decision: 'Рішення', decidedBy: 'Хто вирішив', when: 'Коли',
    empty: 'Немає запитів в очікуванні.', approved: 'Схвалено', rejected: 'Відхилено',
    kindAdmission: 'Новий користувач', kindCreation: 'Допущено · хоче створювати компанії',
    memberIn: (role, company) => `${ROLES_UK[role]} у ${company}`,
    confirmApproveAdmission: (email) => `Схвалити ${email}? Людина зможе входити та створювати компанії.`,
    confirmApproveCreation: (email) => `Схвалити ${email}? Людина зможе створювати компанії. Її членство в компаніях не зміниться.`,
    confirmRejectAdmission: (email) => `Відхилити ${email}? Людина, як і раніше, не зможе увійти.`,
    confirmRejectCreation: (email) => `Відхилити ${email}? Поточний доступ збережеться.`,
    alreadyDecided: (status, by, when) => `Уже ${status === 'approved' ? 'схвалено' : 'відхилено'}: ${by}, ${when}.`,
    identityConflict: 'Цю пошту прив’язано до іншого акаунта Google, тому запит не можна схвалити.',
    decisionFailed: 'Рішення не підтверджено. Спробуйте ще раз.',
    decisionUncertain: 'Не вдалося перевірити, чи збережено рішення.',
    requestsFailed: 'Не вдалося завантажити запити на доступ.',
    navPortfolios: 'Портфоліо Meta',
    companiesTitle: 'Компанії', companiesSub: 'Усі компанії платформи. Демокомпанії приховано, доки ви їх не покажете.',
    countOf: (shown, total) => `${shown} з ${total}`,
    searchCompanies: 'Пошук за назвою або ел. поштою власника', showDemo: 'Показати демокомпанії', allStatuses: 'Усі статуси',
    companyName: 'Назва', status: 'Статус', created: 'Створено', owner: 'Власник', owners: 'Власники', members: 'Учасники',
    accounts: 'Акаунти', portfolios: 'Портфоліо', none: 'Немає',
    active: 'Активна', deletionRequested: 'Запитано видалення', purgeOn: (date) => `Видалення після ${date}`, demo: 'Демо',
    purgeOverdue: 'Видалення прострочено',
    purgeLine: (date, email) => `Видалення після ${date} · запит від ${email}`,
    delText: (on, email, after) => `Запитано ${on}: ${email}. Компанію приховано від учасників і заморожено: дані не завантажуються, зміни неможливі. Після ${after} її можна видалити остаточно, якщо її не відновити.`,
    restoreText: (name) => `Відновити ${name}? Компанія одразу з'явиться у списках компаній усіх її учасників разом з акаунтами, портфоліо й даними.`,
    takenErr: (email, company) => `У ${email} вже є компанія з назвою ${company}.`,
    takenRetry: 'Відновіть її під іншою назвою.',
    takenNote: 'Назва зміниться в усіх, хто отримає компанію назад.',
    emptyName: 'Введіть назву.', restoreFailed: 'Компанію не відновлено. Спробуйте ще раз.',
    purgeText: (name) => `Видалити ${name} остаточно? Компанію, її акаунти, показники й доступ усіх учасників буде видалено з Grovus, а її портфоліо Meta стануть доступні для підключення. Цю дію не можна скасувати. Введіть назву компанії для підтвердження.`,
    purgeTyped: 'Назва компанії', purgeFailed: 'Компанію не видалено. Спробуйте ще раз.',
    origin: {
      self: (email) => `Самостійно · ${email}`,
      admin: (email) => `Створено адміністратором · ${email}`,
      signup_demo: (email) => `Демо під час реєстрації · ${email}`,
    },
    originUnknown: 'Походження невідоме',
    createdOn: (date) => `Створено ${date}`,
    companiesFailed: 'Не вдалося завантажити компанії.', noMatch: 'Немає відповідних компаній.', demoHidden: 'Демокомпанії приховано.',
    role: 'Роль', joined: 'Приєднався', membersNote: 'Учасниками керують власники та адміністратори компанії.',
    claimedPortfolios: 'Прив’язані портфоліо', managedOnPortfolios: 'Керуються на сторінці «Портфоліо Meta».',
    demoNoPortfolio: 'Демокомпанія не має портфоліо. Її користувач може одразу її видалити.',
    connected: 'Підключено', notConnected: 'Не підключено', noLongerShared: 'Доступ більше не надано',
    companyFailed: 'Не вдалося завантажити компанію.', companyGone: 'Цієї компанії більше немає.', back: 'Назад',
    nameLabel: 'Назва',
    renameTaken: (email, name) => `У ${email} вже є компанія з назвою ${closing(name)} У кожного учасника назви мають бути унікальними.`,
    nameTaken: (name) => `В одного з учасників вже є компанія з назвою ${closing(name)} У кожного учасника назви мають бути унікальними.`,
    renameFailed: 'Не вдалося підтвердити перейменування. Перевірте назву та спробуйте ще раз.',
    renameLocked: 'Відновіть компанію, щоб перейменувати її.',
    rename: 'Перейменувати', save: 'Зберегти', restore: 'Відновити', restoreAs: 'Відновити під назвою', purgeNow: 'Видалити остаточно',
    typeToConfirm: 'Введіть назву компанії для підтвердження', requestedBy: 'Запитав(ла)', createCompany: 'Створити компанію',
    noEligibleOwner: 'Відповідного власника поки немає. Спершу схваліть клієнта в розділі «Запити на доступ».',
    namePlaceholder: 'Назва компанії', ownerPlaceholder: 'Виберіть власника', createSubmit: 'Створити',
    createNote: 'Власник одразу стає учасником, без запрошення. Компанія справжня і створюється без демоакаунта.',
    ownerPrereq: 'Власником може стати лише користувач, який уже входив, був схвалений і не вимкнений. Якщо в клієнта ще немає користувача, він спершу входить сам; схваліть його в розділі «Запити на доступ».',
    duplicateFor: (email, name) => `У ${email} уже є компанія з назвою ${name}. Виберіть іншу назву або іншого власника.`,
    reservedName: 'Назву «Демо» зарезервовано для демокомпанії цього користувача. Виберіть іншу назву.',
    ownerIneligible: 'Цей користувач більше не може бути власником. Виберіть іншого.',
    createUncertain: 'Не вдалося підтвердити створення. Можливо, компанію вже створено - перевірте список нижче, перш ніж надсилати знову.',
    createdBanner: (name, email) => `${name} створено. Власник - ${email}.`, open: 'Відкрити',
    unclaimed: 'Не прив’язано', claim: 'Прив’язати', unclaim: 'Відв’язати', rediscover: 'Оновити список', lastDiscovery: 'Останнє оновлення списку',
    refreshFacts: 'Оновити дані', conversionFacts: 'Дані про конверсії', campaignObjectives: 'Цілі кампаній',
    mainActionTypes: 'Основні типи дій', factsWindow: 'Період', collected: 'Зібрано', completeness: 'Повнота',
    observed: 'Є', absent: 'Немає', unknown: 'Невідомо',
    queued: 'У черзі', running: 'Виконується', waitingState: 'Очікує', done: 'Готово', failed: 'Помилка', viewResult: 'Відкрити результат',
    portfoliosSub: 'Бізнес-портфоліо, які діляться рекламними акаунтами з партнерським портфоліо Grovus. Факти зчитуються власними обліковими даними Grovus.',
    portfolio: 'Портфоліо', claimColumn: 'Прив’язка', never: 'не було',
    rediscoverBusy: 'Оновлення списку вже запитано', rediscoverFailed: 'Не вдалося запитати оновлення списку.',
    portfoliosFailed: 'Не вдалося завантажити портфоліо.', noPortfolios: 'Оновлення списку ще не знайшло жодного портфоліо.',
    connectedCount: (n) => `підключено: ${n}`,
    requestedAtBy: (when, email) => (email ? `Запитано ${when}: ${email}` : `Запитано ${when}`),
    waitingForWorker: 'Очікує вільного обробника',
    startedPage: (when, page, run) => `Розпочато ${when} · сторінка ${page} · запуск ${run}`,
    waitReasons: { budget: 'Очікує наступного запуску обробника', throttled: 'Meta обмежує частоту запитів цього застосунку', backoff: 'Повтор після збою' },
    nextAttempt: (when) => `Наступна спроба: ${when}`,
    finishedStaged: (when, portfolios, accounts) => `Завершено ${when} · портфоліо: ${portfolios}, акаунтів: ${accounts}`,
    failuresLastError: (failures, error) => `Збоїв: ${failures} · остання помилка: ${error}`,
    lastCompleteListing: 'У таблиці показано останній повний список.', retryNote: 'Повтор створює новий запит',
    claimLead: (portfolio) => `Прив’язати ${portfolio} до компанії`,
    claimCompany: 'Компанія', noClaimableCompany: 'Немає активної компанії для прив’язки.', notCollected: 'Повного збору ще не було',
    manualCheck: 'Дані ще не зібрано. Перед прив’язкою перевірте подію покупки кожного акаунта в Ads Manager.',
    alreadyRequested: 'Уже запитано', collecting: 'триває збір', campaignsTruncated: '500+ кампаній', noCampaigns: 'Немає кампаній',
    daysOf90: (days) => `${days} з 90 днів`,
    dayOf90: (day) => `день ${day} з 90`,
    collectedOn: (when) => `зібрано ${when}`,
    windowRange: (since, until, timezone) => `${since} - ${until} · ${timezone}`,
    factsStarted: (when, day, run) => `Розпочато ${when} · день ${day} з 90 · запуск ${run}`,
    factsFinished: (when, days) => `Завершено ${when} · ${days} з 90 днів`,
    factsFailed: 'Не вдалося запитати дані.', accountGone: 'Цього акаунта більше немає у списку.',
    unclaimTitle: (portfolio, company) => `Відв’язати ${portfolio} від ${company}?`,
    unclaimText: (company) => `${company} перестає читати його акаунти. Дані залишаються в компанії.`,
    unclaimRefusedTitle: (portfolio, company) => `${portfolio} не можна відв’язати, поки його акаунти підключені до ${company}:`,
    unclaimRefusedText: 'Прив’язка залишається, поки ці акаунти підключені.', close: 'Закрити',
    claimRefusals: {
      claimedElsewhere: (company) => `Це портфоліо вже прив’язане до ${company}.`,
      frozen: 'Компанію заморожено.', unknownCompany: 'Компанії більше немає.',
      demo: 'Демо-компанію не можна прив’язати до портфоліо.',
      notClaimed: 'Прив’язка змінилася. Оновіть сторінку, щоб побачити, до кого воно прив’язане.',
      notFound: 'Цього портфоліо більше немає у списку.', failed: 'Не вдалося зберегти зміну. Спробуйте ще раз.',
    },
  },
}

/** The request page at `/request-access` and the permission screen (GRO-107, canvas screen 5). */
export interface RequestCopy {
  title: string; lede: string; email: string; use: string; optional: string; submit: string
  signOut: string; loading: string; loadFailed: string; retry: string
  sent: (date: string) => string; sentLede: string
  refused: string; refusedLede: string
  accountChanged: string; signOutFailed: string; submitFailed: string; tooLong: string
  permTitle: string; permLede: string; permSubmit: string; permPendingLede: string
  unchanged: string; back: string; backTo: (client: string) => string
  months: string[]
}

export const REQUEST_COPY: Record<Lang, RequestCopy> = {
  en: {
    title: 'Request access',
    lede: 'Grovus is invite-only. Ask someone at your client for an invitation, or request access.',
    email: 'Google email', use: 'What will you use Grovus for?', optional: 'Optional', submit: 'Request access',
    signOut: 'Sign out', loading: 'Loading...', loadFailed: 'Could not load your request.', retry: 'Try again',
    sent: (date) => `Request sent on ${date}.`, sentLede: 'Sign in again after Grovus approves it.',
    refused: 'Your request was not approved.', refusedLede: 'You can still join a client by invitation.',
    accountChanged: 'The signed-in Google account changed. Check the form and send it again.',
    signOutFailed: 'Could not sign out. Try again.',
    submitFailed: 'Could not send the request. Try again.',
    tooLong: 'The message is longer than 1000 characters.',
    permTitle: 'Request permission to create clients',
    permLede: 'You can join clients by invitation. Creating clients needs permission from Grovus.',
    permSubmit: 'Request permission', permPendingLede: 'You can add clients once Grovus approves it.',
    unchanged: 'Your clients and roles are unchanged.', back: 'Back', backTo: (client) => `Back to ${client}`,
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  },
  ru: {
    title: 'Запросить доступ',
    lede: 'Grovus работает только по приглашениям. Попросите кого-нибудь из вашей компании прислать приглашение или запросите доступ.',
    email: 'Эл. почта Google', use: 'Для чего вы будете использовать Grovus?', optional: 'Необязательно', submit: 'Запросить доступ',
    signOut: 'Выйти', loading: 'Загрузка...', loadFailed: 'Не удалось загрузить ваш запрос.', retry: 'Повторить',
    sent: (date) => `Запрос отправлен ${date}.`, sentLede: 'Войдите снова, когда Grovus его одобрит.',
    refused: 'Ваш запрос не одобрен.', refusedLede: 'Вы по-прежнему можете присоединиться к компании по приглашению.',
    accountChanged: 'Аккаунт Google, с которым вы вошли, изменился. Проверьте форму и отправьте её снова.',
    signOutFailed: 'Не удалось выйти. Попробуйте ещё раз.',
    submitFailed: 'Не удалось отправить запрос. Попробуйте ещё раз.',
    tooLong: 'Сообщение длиннее 1000 символов.',
    permTitle: 'Запросить право создавать компании',
    permLede: 'Вы можете присоединяться к компаниям по приглашению. Для создания компаний нужно разрешение Grovus.',
    permSubmit: 'Запросить право', permPendingLede: 'Вы сможете добавлять компании, когда Grovus его одобрит.',
    unchanged: 'Ваши компании и роли не изменились.', back: 'Назад', backTo: (client) => `Вернуться в ${client}`,
    months: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  },
  uk: {
    title: 'Запит на доступ',
    lede: 'Grovus працює лише за запрошеннями. Попросіть когось із вашої компанії надіслати запрошення або надішліть запит на доступ.',
    email: 'Ел. пошта Google', use: 'Для чого ви використовуватимете Grovus?', optional: 'Необов’язково', submit: 'Надіслати запит',
    signOut: 'Вийти', loading: 'Завантаження...', loadFailed: 'Не вдалося завантажити ваш запит.', retry: 'Повторити',
    sent: (date) => `Запит надіслано ${date}.`, sentLede: 'Увійдіть знову, коли Grovus його схвалить.',
    refused: 'Ваш запит не схвалено.', refusedLede: 'Ви все ще можете приєднатися до компанії за запрошенням.',
    accountChanged: 'Акаунт Google, з яким ви увійшли, змінився. Перевірте форму та надішліть її знову.',
    signOutFailed: 'Не вдалося вийти. Спробуйте ще раз.',
    submitFailed: 'Не вдалося надіслати запит. Спробуйте ще раз.',
    tooLong: 'Повідомлення довше за 1000 символів.',
    permTitle: 'Запит на право створювати компанії',
    permLede: 'Ви можете приєднуватися до компаній за запрошенням. Для створення компаній потрібен дозвіл Grovus.',
    permSubmit: 'Надіслати запит', permPendingLede: 'Ви зможете додавати компанії, коли Grovus його схвалить.',
    unchanged: 'Ваші компанії та ролі не змінилися.', back: 'Назад', backTo: (client) => `Повернутися до ${client}`,
    months: ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'],
  },
}

export interface Dict {
  period: string; adAccounts: string; mergeNote: string
  creatives: string; rollups: string; savedViews: string; reliability: string
  svWeekly: string; svThin: string; svTheme: string
  relReliable: string; relProvisional: string; relDirection: string; relInsufficient: string; legendNote: string
  spend: string; purch: string; pvalue: string; impressions: string
  sortBy: string; format: string; audience: string; showSamples: string; searchName: string
  grid: string; table: string; creative: string; accounts: string; adj: string
  /**
   * The muted figure's label. The key name predates PR #262, which made the sort place
   * every row: the figure is no longer left out of ranking, only less reliable, and the
   * copy says so. The name stays because it is internal and six call sites plus the tests
   * import it (GRO-177).
   */
  cannotRank: string
  account: string; ads: string; merged: string
  allCreatives: string
  sortSpend: string; sortPurch: string; sortPvalue: string; sortRoas: string; sortAdj: string
  light: string; dark: string
  allOpt: string; rankable: string; share: string; valShort: string
  spendShare: string; adjShort: string
  clicks: string; ofSpend: string
  video: string; image: string; /** The Format axis (GRO-174): neither a video nor a still. */ carousel: string
  /** The Audience column (GRO-175): a creative whose ads reach more than one audience. */ audienceMixed: string
  defNLead: string; defNBody: string
  defAdjLead: string
  defStruckLead: string; defStruckBody: string
  subAdsCreatives: string; subOrders: string; subValue: string
  subBelow: string; subCpa: string; subCpm: string
  resultCount: string; accMerged: string; accOf: string; noDelivery: string; changePeriod: string
  bannerThin: string; accNote: string
  sampleAt: string; sampleShort: string; parkedNote: string
  relIns: string; relDir: string; relProv: string; relRel: string
  partialLong: string
  loadingCreatives: string; loadFailed: string; retry: string
  authChecking: string; authTitle: string; authLede: string; authNote: string
  authGoogleButton: string
  signOut: string; authSessionExpired: string; authSignOutFailed: string
  navLabel: string; navToggle: string
  // GRO-66: the header's right side, a help panel and the user menu behind an avatar.
  userMenu: string; signingOut: string; helpLabel: string; settingsLead: string
  periodUnit: string
  // -- Periods (GRO-65) -------------------------------------------------------------
  periodRange: string; periodPicker: string; periodRangeRefused: string
  compareCaption: string; notComparedInProgress: string; notComparedUnverified: string; notComparedGaps: string
  withheldPartial: string; withheldThin: string; withheldNoBase: string
  detailWindowMismatch: string
  calPrevMonth: string; calNextMonth: string; calPrevYear: string; calNextYear: string
  calPrevDecade: string; calNextDecade: string; calMonthLevel: string; calYearLevel: string

  // -- The creatives screen (GRO-16) ------------------------------------------------
  // Values that come from the response carry their own labels; these are the chrome,
  // the rules and the notes. Every key is in both dictionaries or the type check fails.
  hypothesis: string; technique: string; hypUnassigned: string; banner: string
  ideaTagsCol: string; chipAddIdea: string; chipAddVisual: string; chipClear: string
  message: string; offer: string; chipAddMessage: string; chipAddOffer: string
  /** A suggested tag (GRO-173): `%value` is the stored value, shown untranslated. */
  suggestChip: string; suggestAccept: string; suggestReject: string
  suggestionSettled: string; suggestionAxisAssigned: string
  conversions: string; purchValue: string
  minPeriod: string; convAbbr: string; floorClear: string; floorNote: string
  bucketNote: string; bucketBack: string; axisHyp: string; axisVisual: string; axisAud: string
  focusSome: string; focusNone: string; focusScale: string; focusKill: string
  vlScale: string; vlHold: string; vlWatch: string; vlRework: string; vlKill: string; vlInsufficient: string; vlNotStarted: string
  vqHook: string; vqCtr: string; vcCheck: string
  vrBetter: string; vrWithin: string; vrWorseDeliveryFine: string; vrWorseDeliveryWeak: string; vrDeliveryUnchecked: string
  vrBelowProvisional: string; vrBelowDirection: string; vrNoReference: string; vrNoRoas: string; vrDataIncomplete: string; vrNeverDelivered: string; vrStopLoss: string; vrNoConversions: string; vqNoHistory: string; relStopLoss: string
  vrNoCpa: string; vrNoConversionsCpa: string; vrRefTarget: string; vrRefAverage: string; vrRefAverageIn: string; vrBoundRoas: string; vrBoundCpa: string
  hookCol: string; holdCol: string; convCol: string; adsCol: string; verdictCol: string
  ruleLead: string; roasCol: string; cpaCol: string; aovCol: string; cpcCol: string; cpmCol: string
  defBandLead: string; defBandBody: string; defFunLead: string; defFunBody: string
  defShapeLead: string; defShapeBody: string; defAdjPooledBody: string
  // GRO-63: the explanation marks, the collapsed guide and the calmer banner.
  // Every hint is a standalone sentence; the name is the mark control's own
  // accessible name. Both live here so a later help section can reuse them
  // verbatim.
  hintShape: string; hintPurch: string; hintRoas: string; hintAdj: string; hintFun: string
  hintAov: string; hintBand: string; hintRankable: string
  hintNameShape: string; hintNamePurch: string; hintNameRoas: string; hintNameAdj: string
  hintNameFun: string; hintNameBand: string; hintNameVerdict: string; verdictGapLine: string; hintNameAov: string; hintNameRankable: string
  howToRead: string; compareHypo: string
  tableScrollHint: string
  partialMeasure: string
  partialWord: string; partialReasonCreatives: string; partialReasonScope: string; partialHintName: string
  partialLegendMeasure: string; partialLegendLagging: string
  unitOne: string; unitFew: string; unitMany: string
  emptyCompany: string; emptySelection: string; emptyNoCreatives: string; emptyFiltered: string
  errMixedProvenance: string; errUnknownAudience: string; clearAudience: string; errMixedCurrency: string; errUnknownAccount: string; errRequest: string
  widenTo: string; widenTitle: string
  sortName: string; sortAccounts: string; sortHypothesis: string; sortTechnique: string
  sortHook: string; sortHold: string; sortCtr: string; sortCpa: string; sortAov: string
  sortCpc: string; sortAds: string; sortVerdict: string
  sortShare: string; sortCpm: string; sortActiveDays: string; shareCol: string; activeDaysCol: string
  pausedSince: (date: string) => string
  hintShare: string; hintNameShare: string
  rankableSub: string; tableCaption: string; adsMerged: string

  // -- The detail panel (GRO-17) ------------------------------------------------------
  // The panel's chrome and the notes; figures and labels come from the response.
  detailAria: string; detailClose: string; detailScroll: string; ticksAria: string; ticksAriaUnknown: string; detailMeta: string; detailMetaStill: string; shareSub: string
  splitCaption: string; roasNote: string; identityNote: string
  /** The «Объявления» block (GRO-196); the reason copy is provisional until PRD 0008 phase 2. */
  adsBlockTitle: string; adsBlockCaption: string; adStatus: string; adReason: string
  reasonFounder: string; reasonExactBytes: string; reasonNearExact: string
  reasonConfirmedSuggestion: string; reasonMergedByHand: string; reasonDetached: string
  notFoundTitle: string; notFoundBody: string; relPartial: string
  // -- The hypotheses screen (GRO-18) ------------------------------------------------
  // Copy from the wireframe's rollups artboard and journal step; the spike's seven dead
  // rollup keys are removed with the view that was their last reader.
  hypTitle: string; hypLede: string; hypAxisNote: string; hypDrill: string
  hypSub: string; hypOpen: string; hypFoot: string; hypFootNoAud: string
  versionsLine: string
  footWindowLast: string; footWindowRange: string
  groupHypothesis: string; groupVisual: string; groupAudience: string
  techUnassigned: string
  msgUnassigned: string; offerUnassigned: string
  bucketOne: string; bucketFew: string; bucketMany: string
  quoteOpen: string; quoteClose: string; andJoin: string
  footList: string; footNone: string
  unassignedFootHyp: string; unassignedFootVisual: string
  jProposes: string; jBasis: string; jWaiting: string; jProvNote: string
  jvConfirmed: string; jvRefuted: string
  frStepAccount: string; frStepCompany: string; frStepConnect: string; frStepsLabel: string
  frAccountLabel: string; frCompanyLabel: string; frCompanyPlaceholder: string
  frBack: string; frNext: string; frSkip: string; frFinish: string
  frConnectMeta: string; notConnectedYet: string
  accStateWaiting: string; accStateConnected: string; accStateExpiring: string
  accStateExpired: string; accStateRevoked: string; accStateRejected: string; accStateWithdrawn: string
  accShared: string; accConnect: string; accRemove: string; accRemoveConfirm: string; accRemoveKeep: string
  accDemoCompanyHint: string; accDemoPrompt: string; accDemoPromptDelete: string; accDemoPromptKeep: string
  // GRO-66: the connect section, stage 1 of docs/guides/meta-connection.md.
  accConnectPlatform: string; accConnectNeed: string; accConnectThen: string; accConnectGuide: string
  accNotAvailable: string; accAlreadyRemoved: string; accUnreachable: string; accRefreshFailed: string; accSharedFailed: string
  demoMark: string; demoMarkLong: string; demoNotice: string; frDemoHint: string; frDuplicateCheck: string; frReservedName: string
  unpulledNotice: string
  // GRO-44: the scope-level notices (D8) and the detail's note (D9).
  scopeUnplaced: string; scopeUncovered: string; scopeExcluded: string
  scopeExcludedCount: string; scopeExcludedCompared: string; detailAttributionAcross: string
  // GRO-156 D3: one line per reason under the scope notices.
  scopeReason: string; scopeReasonExcluded: string
  unplacedReasons: Record<
    | 'pending' | 'unrecognised_shape' | 'no_creative' | 'creative_unreadable' | 'fetch_failed'
    | 'no_thumbnail' | 'refused_host' | 'download_failed' | 'hash_failed' | 'deferred'
    | 'placeholder_image',
    string
  >
  jBasisDemo: string
  platMeta: string; platTikTok: string; platGoogle: string
  emptyTitle: string; emptyBody: string
  clientPanelLabel: string
  clientLevel: string; clientNone: string; clientUnnamed: string
  clientAdd: string; clientSwitchNote: string
  clientAddName: string; clientAddCreate: string; clientAddCancel: string; clientAddDuplicate: string
  clientAddPending: string; clientAddElsewhere: string; clientAddUnknown: string; clientAddUnknownOld: string
  clientAddUnavailable: string; clientAddNotListed: string; clientAddRefreshFailed: string
  clientAddCheckAgain: string; clientAddAlreadyCreated: string; clientAddCancelled: string
  clientAddKeepOpen: string; clientAddCheckFailed: string; clientAddCheckFailedCreated: string
  clientAddInventoryFailed: string; clientAddAnyway: string; clientAddInventoryRisk: string
  colChooser: string; colAlwaysShown: string; colShowAll: string; colSorted: string
  exportCsv: string; exportCsvShort: string; exportXlsx: string; exportPending: string; xlsxSheetName: string
  accLastOne: string; accSelectAll: string
  csvDataDelayed: string; csvCreativeId: string
}

export const DICT: Record<Lang, Dict> = {
  en: {
    period: 'Period', adAccounts: 'Ad accounts',
    mergeNote: 'Creative identity is the asset hash, not the ad name - the same file is matched across accounts even when every ad naming convention differs.',
    creatives: 'Creatives', rollups: 'Ideas', savedViews: 'Saved views', reliability: 'Reliability',
    svWeekly: 'Weekly kill / scale', svThin: 'Thin-sample watchlist', svTheme: 'Idea comparison',
    relReliable: 'Reliable', relProvisional: 'Provisional', relDirection: 'Direction', relInsufficient: 'Insufficient',
    legendNote: 'Marks carry the sample. Colour is never the only signal.',
    spend: 'Spend', purch: 'Purchases', pvalue: 'Purchase value', impressions: 'Impressions',
    sortBy: 'Sort', format: 'Format', audience: 'Audience', showSamples: 'Sample sizes', searchName: 'Search by name',
    grid: 'Grid', table: 'Table', creative: 'Creative', accounts: 'Accounts', adj: 'Adj. ROAS',
    cannotRank: 'Small sample: less reliable',
    account: 'Account', ads: 'Ads', merged: 'Merged',
    allCreatives: 'All creatives',
    sortSpend: 'Spend (default)', sortPurch: 'Purchases', sortPvalue: 'Purchase value',
    sortRoas: 'ROAS - reliable only', sortAdj: 'Adjusted ROAS',
    light: 'Light', dark: 'Dark',
    allOpt: 'All', rankable: 'Rankable', share: 'Share', valShort: 'value',
    spendShare: 'Spend share', adjShort: 'adj',
    clicks: '%n clicks', ofSpend: 'of account spend',
    video: 'Video', image: 'Image', carousel: 'Carousel', audienceMixed: 'Mixed',
    defNLead: 'n', defNBody: '- purchases attributed in the selected reporting window',
    defAdjLead: 'ADJ. ROAS',
    defStruckLead: 'Small-sample figure', defStruckBody: '- the sample sits below n=%n, so the figure is shown muted with a tilde: the sort still places it, and the figure is less reliable',
    subAdsCreatives: '%a {a:ad|ads} / %c {c:creative|creatives}', subOrders: '%n orders', subValue: '%v value',
    subBelow: '%c below n=%n', subCpa: 'CPA %v', subCpm: 'CPM %v',
    resultCount: '%c {c:creative|creatives} / %a {a:ad|ads}',
    accMerged: '%n {n:account|accounts} merged', accOf: '%n of %t {t:account|accounts}',
    noDelivery: 'No delivery in this period: %n of %t {t:account|accounts}', changePeriod: 'Change period',
    bannerThin: '%c {c:creative has|creatives have} not collected enough data yet (%s).',
    compareHypo: 'Compare at idea level',
    accNote: '%n× / %a ads',
    sampleAt: 'n=%n %u · at threshold', sampleShort: 'n=%n %u · %d short of %h',
    parkedNote: 'n=%n · %s · raw %r → adj %a',
    relIns: 'n=%n on %s spend. Below n=%lo the sample is not ranked: every ROAS, CPA and AOV here is muted. About %d more {d:conversion|conversions} - roughly %c at its current CPA - would reach the ranking floor.',
    relDir: 'n=%n. The sample ranks, but a conclusion needs n=%lo: about %d more {d:conversion|conversions}. Read direction, not decimals.',
    relProv: "n=%n. The sample ranks, but the interval around each ratio is still wide: read direction, not decimals. Past n=%hi the sample stops being the reason to hold back; what remains is the verdict's materiality rules.",
    relRel: "n=%n on %s spend. The sample clears the reliable line (n=%hi): these ratios are eligible to set against %ref, subject to complete outcome figures and the verdict's materiality rules.",
    partialLong: 'Ingestion for this account runs behind; its figures may not yet reflect everything',
    loadingCreatives: 'Loading creatives…', loadFailed: 'Could not load creatives from the API', retry: 'Retry',
    authChecking: 'Checking your session…', authTitle: 'Sign in',
    authLede: 'One account holds every company you work with and the ad accounts under them.',
    authNote: 'Use the Google account approved for your team.',
    authGoogleButton: 'Sign in with Google',
    signOut: 'Sign out',
    authSessionExpired: 'Your session expired. Sign in again.',
    authSignOutFailed: 'Sign-out could not be confirmed. Try again.',
    navLabel: 'Primary navigation', navToggle: 'Menu',
    userMenu: 'Account and settings', signingOut: 'Signing out', helpLabel: 'Help: how to read the figures', settingsLead: 'Settings',
    periodUnit: '%nd',
    periodRange: 'Range', periodPicker: 'Choose a period',
    periodRangeRefused: "The chosen dates are outside this selection's history; showing 14 days.",
    compareCaption: 'Compared with %r',
    notComparedInProgress: 'Not compared: today is not over',
    notComparedUnverified: "Not compared: completeness of this account's data cannot be verified yet",
    notComparedGaps: 'Not compared: some creatives in this view have days without data in one of the periods',
    withheldPartial: 'Not compared: one of the periods is partial',
    withheldThin: 'Not compared: fewer than %n conversions in one of the periods',
    withheldNoBase: 'Not compared: nothing to compare against in the previous period',
    detailWindowMismatch: 'The list and this panel answer different periods. Retry to ask again.',
    calPrevMonth: 'Previous month', calNextMonth: 'Next month', calPrevYear: 'Previous year', calNextYear: 'Next year',
    calPrevDecade: 'Previous decade', calNextDecade: 'Next decade', calMonthLevel: 'Choose a month', calYearLevel: 'Choose a year',

    hypothesis: 'Idea', technique: 'Visual',
    hypUnassigned: 'no idea assigned', banner: 'Banner',
    ideaTagsCol: 'Idea and tags', chipAddIdea: '+ idea', chipAddVisual: '+ visual', chipClear: 'Remove',
    message: 'Message', offer: 'Offer', chipAddMessage: '+ message', chipAddOffer: '+ offer',
    suggestChip: 'Grovus suggests: %value', suggestAccept: 'Accept the suggestion', suggestReject: 'Reject the suggestion',
    suggestionSettled: 'This suggestion was already accepted or rejected.',
    suggestionAxisAssigned: 'This tag was already set by hand.',
    conversions: 'Conversions', purchValue: 'Purchase value',
    minPeriod: 'Minimum · %dd', convAbbr: 'conv.', floorClear: 'Clear',
    floorNote: '%c {c:creative|creatives} hidden by your minimums (%s of spend). The totals above still cover all %t.',
    bucketNote: '"%v" · %c {c:creative|creatives}. Every figure on this screen now describes %a, not the whole account.',
    bucketBack: 'Back to comparison',
    axisHyp: 'this idea', axisVisual: 'this visual', axisAud: 'this audience',
    focusSome: '%n of %t {t:creative|creatives} in this segment {n:meets|meet} the %v rule. {n:It sorts|They sort} to the top.',
    focusNone: 'No creative in this segment meets the %v rule right now. The list is ordered by verdict, closest first.',
    focusScale: 'Scale', focusKill: 'Kill',
    vlScale: 'Scale', vlHold: 'Hold', vlWatch: 'Watch', vlRework: 'Rework', vlKill: 'Kill', vlInsufficient: 'Too little data', vlNotStarted: 'Not launched',
    vqHook: 'hook', vqCtr: 'CTR', vcCheck: '%name %v vs %bm',
    vrBetter: '%metric %r is %p% better, against %ref - past the %b% gap needed at n=%n %u. Rule: Scale.',
    vrWithin: '%metric %r differs by %p% against %ref - within the %b% needed at n=%n %u to differ from noise. Rule: Hold.',
    vrWorseDeliveryFine: '%metric %r is %p% worse, against %ref, at n=%n %u; delivery is fine (%checks). Rule: Rework.',
    vrWorseDeliveryWeak: '%metric %r is %p% worse, against %ref, at n=%n %u; weak delivery: %checks. Rule: Kill.',
    vrDeliveryUnchecked: '%metric %r is %p% worse, against %ref, at n=%n %u; delivery cannot be checked. Rule: Kill.',
    vrBelowProvisional: 'n=%n %u: the creative ranks, but a conclusion needs n=%lo. Rule: Watch.',
    vrBelowDirection: 'n=%n %u is below the n=%dir floor - the sample is not ranked. Rule: Too little data.',
    vrNeverDelivered: 'Not a single impression across the history read.',
    vrNoReference: 'No 90-day average %metric in %cur - nothing to compare against, and no stop-loss to compute. Rule: Too little data.',
    vrNoConversions: 'No conversions in %cur in 90 days: no average ROAS and no average order value, so no stop-loss to compute. Rule: Too little data.',
    vrStopLoss: '%s at %n %u: even in the best case %metric is %bound %bc against %refv (stop-loss %th). Rule: Kill.',
    vrBoundRoas: 'no higher than', vrBoundCpa: 'no lower than',
    vrRefTarget: 'the target', vrRefAverage: 'the 90-day average', vrRefAverageIn: 'the 90-day average · %cur',
    vqNoHistory: 'no conversion history',
    relStopLoss: 'n=%n - too small a sample to judge against %ref, but the stop-loss does not depend on n: even in the best case %metric is worse than %ref.',
    vrNoRoas: 'ROAS cannot be computed: no spend or no conversion value. Rule: Watch.',
    vrNoCpa: 'CPA cannot be computed: no spend. Rule: Watch.',
    vrNoConversionsCpa: 'No conversions in %cur in 90 days: no average CPA, so no stop-loss to compute. Rule: Too little data.',
    vrDataIncomplete: 'The figures for %m are incomplete - partly received conversions are no basis for a conclusion. Rule: Watch.',
    hookCol: 'Hook', holdCol: 'Hold', convCol: 'Conversions', adsCol: 'Ads merged', verdictCol: 'Verdict',
    ruleLead: 'Rule that fired', roasCol: 'ROAS', cpaCol: 'CPA', aovCol: 'AOV', cpcCol: 'CPC', cpmCol: 'CPM',
    defBandLead: 'Materiality band',
    defBandBody: '- the gap from the account average a creative must clear before Scale or Kill fires. It moves with the sample (about 2 standard errors, 200/√n): 28% at n=50, 20% at n=100 - floored at 10%, below which a real difference is still too small to act on. The average is over the %a accounts in scope.',
    defFunLead: 'HOOK / HOLD',
    defFunBody: '- HOOK divides three-second views by impressions; HOLD divides ThruPlays by three-second views. Different denominators, each a percentage; their samples are large, so they rank honestly where purchase ratios cannot. Video only.',
    defShapeLead: 'ASPECT / DURATION',
    defShapeBody: '- the shape of the asset and, for video, how long it runs. Thumbnails arrive with ingestion; until then the kind badge stands in rather than a placeholder that would imply recognition.',
    hintShape: 'The shape of the asset and the kind badge: this column carries the creative itself.',
    hintPurch: 'Purchases attributed in the selected reporting window; the sample behind every outcome ratio.',
    hintRoas: 'ROAS: conversion value divided by spend. A muted figure with a tilde is context, not a ranked comparison.',
    hintAdj: 'Adjusted ROAS: raw ROAS pulled toward the account ROAS of the same period; at 50 purchases the two weigh the same. Thin rows are parked, not ranked.',
    hintFun: 'HOOK divides three-second views by impressions; HOLD divides ThruPlays by three-second views. Different denominators, each a percentage; video only.',
    hintAov: 'Average order value: conversion value over conversions.',
    hintBand: 'The materiality band: the gap a creative must clear before Scale or Kill fires. The average it is measured against is the account average over the accounts in scope, computed from demo figures on a demo client.',
    hintRankable: 'Rankable means the conversions have reached the ranking floor; below it the raw ratio stays context and the comparison moves to the pooled buckets.',
    hintNameShape: 'What is on the Creative column?',
    hintNamePurch: 'What are conversions?',
    hintNameRoas: 'What is ROAS?',
    hintNameAdj: 'What is adjusted ROAS?',
    hintNameFun: 'What are hook and hold?',
    hintNameAov: 'What is AOV?',
    hintNameBand: 'What is the materiality band?',
    hintNameVerdict: 'How to read the verdict?', verdictGapLine: 'At n = %n the gap must exceed %b %.',
    hintNameRankable: 'What does rankable mean?',
    howToRead: 'How to read this table',
    tableScrollHint: 'Scroll horizontally to see all columns ↔',
    defAdjPooledBody: '- raw ROAS pulled toward the account ROAS of the same period and accounts: (n x ROAS + 50 x account ROAS) / (n + 50). At 50 purchases the two weigh the same. Below the ranking floor it is context, not a ranked comparison. The no-value glyph when the creative ROAS or the account benchmark is unavailable.',
    partialMeasure: 'Partial - this figure does not cover everything, or combines reporting that does not fully compare',
    partialWord: 'partial',
    partialReasonCreatives: '%c of %t {t:creative|creatives} with incomplete figures',
    partialReasonScope: 'not all delivery in scope is visible, see the notes above',
    partialHintName: 'Why is this partial?',
    partialLegendMeasure: 'partial: the figure does not cover everything, or combines reporting that does not fully compare',
    partialLegendLagging: 'after an account: its data may be out of date',
    unitOne: 'conversion', unitFew: 'conversions', unitMany: 'conversions',
    emptyCompany: 'No ad accounts are connected to this company yet. Connect one and its creatives, ideas and totals appear here.',
    emptySelection: 'No ad accounts selected. Choose at least one to see its creatives.',
    emptyNoCreatives: 'No creative delivered in this window. The totals read zero because nothing ran, not because nothing worked.',
    emptyFiltered: 'No creative matches these filters. The totals above still describe the whole segment.',
    errUnknownAudience: 'No ad in the selected accounts targeted this audience. Show every audience instead.',
    clearAudience: 'Show all audiences',
    errMixedProvenance: 'The selection mixes demo and real ad accounts, and their figures are never added together. Drop the demo accounts %a from the selection in the account control above.',
    errMixedCurrency: 'The selected ad accounts report in more than one currency. One request covers one, so drop %a from the selection in the account control above.',
    errUnknownAccount: 'This company no longer has the ad account %a. Change the selection in the account control above; a retry cannot fix it.',
    errRequest: 'The request was refused: %c',
    widenTo: 'Widen to %nd', widenTitle: 'At this rate a %nd window would clear the reliable threshold',
    sortName: 'Name', sortAccounts: 'Accounts', sortHypothesis: 'Idea', sortTechnique: 'Visual',
    sortHook: 'Hook', sortHold: 'Hold', sortCtr: 'CTR', sortCpa: 'CPA', sortAov: 'AOV',
    sortCpc: 'CPC', sortAds: 'Ads merged', sortVerdict: 'Verdict',
    sortShare: 'Spend share', sortCpm: 'CPM', sortActiveDays: 'Active days', shareCol: 'Share', activeDaysCol: 'Active days',
    pausedSince: (date) => `paused since ${date}`,
    hintShare: 'Share of the spend of the creatives shown. It adds up to 100% over the rows on screen.',
    hintNameShare: 'What is the share?',
    rankableSub: '%c of %t', tableCaption: 'Creatives, by asset', adsMerged: '%n {n:ad|ads}',
    detailAria: 'Creative detail panel', detailClose: 'Close the detail panel',
    detailScroll: 'Creative detail, scrollable',
    ticksAria: '%b confidence, n=%n',
    ticksAriaUnknown: 'Confidence cannot be judged: no conversion count was reported',
    detailMeta: '%k - %d - %n {n:account|accounts} - %a {a:ad|ads} - n=%p',
    detailMetaStill: '%k - %n {n:account|accounts} - %a {a:ad|ads} - n=%p',
    shareSub: 'of segment spend, as the screen last fetched it',
    adsBlockTitle: 'Ads',
    adsBlockCaption: 'Every ad of the creative in this scope, with the picture that ran under it',
    adStatus: 'Status',
    adReason: 'Why here',
    reasonFounder: 'First ad of this file',
    reasonExactBytes: 'Same file',
    reasonNearExact: 'Near-identical image',
    reasonConfirmedSuggestion: 'Confirmed suggestion',
    reasonMergedByHand: 'Merged by hand',
    reasonDetached: 'Detached',
    splitCaption: 'The split by account: together the rows are the merged figure at the foot',
    roasNote: 'ROAS is total purchase value over total spend, for the merged row as for each account.',
    identityNote: 'Creative group %h - %n {n:account|accounts} - %a {a:ad name|ad names}',
    notFoundTitle: 'No figures for this creative in scope',
    notFoundBody: 'Its ads are all outside the current accounts or audience, or it is gone. Retry, or close the panel and pick another creative.',
    relPartial: 'The figures for %m are not complete for this creative, or combine reporting that does not fully compare. Every ratio resting on them is parked.',
    hypTitle: 'Idea comparison',
    hypLede: 'An idea pools every creative that tests the same theme, so the combined sample clears the threshold individual creatives miss. This is the default comparison surface, not a filter.',
    hypAxisNote: 'The idea is the grouping key. Visual and audience are attributes of the creatives inside an idea, offered here as secondary cuts.',
    hypDrill: 'Open a row to see the creatives it pools, on the creative screen, narrowed to that bucket alone.',
    hypSub: '%c {c:creative|creatives} · n=%n %u',
    versionsLine: 'Versions: %v',
    hypOpen: 'Open the %c {c:creative|creatives} in this bucket',
    hypFoot: 'Buckets inherit the creative rule. %list; hatched bars mark the rest, so their ROAS is directional. Grouped by audience the account pools into %aud - one creative can sit in several audience buckets while its conversions sit in one. %win%un',
    hypFootNoAud: 'Buckets inherit the creative rule. %list; hatched bars mark the rest, so their ROAS is directional. %win%un',
    footWindowLast: 'The window is the last %p days.',
    footWindowRange: 'The window is %r.',
    groupHypothesis: 'Idea', groupVisual: 'Visual', groupAudience: 'Audience',
    bucketOne: 'bucket', bucketFew: 'buckets', bucketMany: 'buckets',
    techUnassigned: 'no visual assigned',
    msgUnassigned: 'no message assigned', offerUnassigned: 'no offer assigned',
    quoteOpen: '"', quoteClose: '"', andJoin: ' and ',
    footList: 'At n=%n %u, %list clear the floor',
    footNone: 'No bucket clears n=%n %u yet',
    unassignedFootHyp: ' Creatives carrying no idea pool into "%v".',
    unassignedFootVisual: ' Creatives carrying no visual pool into "%v".',
    jProposes: 'Grovus proposes',
    jBasis: 'CPA %c against the account average %b, %n %u',
    jWaiting: 'Awaiting evidence - %n more %u',
    jProvNote: 'provisional sample',
    jvConfirmed: 'Confirmed', jvRefuted: 'Refuted',
    frStepAccount: 'Your account', frStepCompany: 'Your first company', frStepConnect: 'Connect an ad account',
    frStepsLabel: 'First-run steps',
    frAccountLabel: 'Your name', frCompanyLabel: 'Company name',
    frCompanyPlaceholder: 'The brand you run ads for.',
    frBack: 'Back', frNext: 'Next', frSkip: 'Skip for now', frFinish: 'Finish',
    frConnectMeta: 'Connect Meta Ads', notConnectedYet: 'Not connected yet',
    accStateWaiting: 'Connected. Waiting for the first data.', accStateConnected: 'Connected',
    accStateExpiring: 'Credential expires in %n {n:day|days}',
    accStateExpired: 'Credential expired. Grovus is renewing it.', accStateRevoked: 'Access revoked',
    accStateRejected: 'Credential rejected. Grovus is looking into it.',
    accStateWithdrawn: 'No longer shared with Grovus',
    accConnectPlatform: 'Connect an ad platform',
    accConnectNeed: 'Grovus needs admin access to your Meta business portfolio. The guide shows each step with a screenshot.',
    accConnectThen: 'Your ad accounts then appear here under "%shared". Connect each one.',
    accConnectGuide: 'Open the guide',
    accShared: 'Shared with Grovus', accConnect: 'Connect', accRemove: 'Remove',
    accRemoveConfirm: 'Remove the demo account and its figures? This cannot be undone.', accRemoveKeep: 'Keep',
    accDemoCompanyHint: 'This is the sample company. Connect your accounts in a real company.',
    accDemoPrompt: 'You are creating your first real account. Do you want to delete the demo account?',
    accDemoPromptDelete: 'Delete', accDemoPromptKeep: 'Keep',
    accNotAvailable: 'This account is no longer available', accAlreadyRemoved: 'Already removed',
    accUnreachable: 'Could not reach Grovus. The list has been refreshed.',
    accRefreshFailed: 'The change was sent, but the account list could not be refreshed.',
    accSharedFailed: 'Could not load the accounts shared with Grovus.',
    demoMark: 'Demo', demoMarkLong: 'Demo account',
    demoNotice: 'This company includes demo accounts. Demo figures are labelled and reported separately.',
    unpulledNotice: 'Metrics have not been pulled for these accounts yet.',
    scopeUnplaced: 'Some ads could not be identified yet, so figures here are incomplete.',
    scopeUncovered: 'Some days have not been read yet, so figures here are incomplete.',
    scopeExcluded: 'Ads that could not be identified are excluded, so these figures cover identified ads only.',
    scopeExcludedCount: 'Excluded ads: %c in this window.',
    scopeExcludedCompared: 'Excluded ads: %c in this window, %p in the previous one.',
    scopeReason: '%r: %c {c:ad|ads}',
    scopeReasonExcluded: '%r, excluded: %c {c:ad|ads}',
    unplacedReasons: {
      pending: 'Not attempted yet',
      unrecognised_shape: 'Unrecognised creative format',
      no_creative: 'No creative on the ad',
      creative_unreadable: 'Creative could not be read',
      fetch_failed: 'Creative could not be fetched',
      no_thumbnail: 'No image or thumbnail',
      refused_host: 'Image host not allowed',
      download_failed: 'Image could not be downloaded',
      hash_failed: 'Image could not be processed',
      deferred: 'Waiting for the next pass',
      placeholder_image: 'Meta shows a placeholder, no image',
    },
    detailAttributionAcross: 'Conversions combine two attribution settings across accounts.',
    frDemoHint: 'The sample figures are in the "Демо" company. Your own company starts empty until you connect an account.',
    frDuplicateCheck: 'This client may already have been created. Could not check your clients yet.',
    frReservedName: 'This name is kept for the sample company. Choose another name.',
    jBasisDemo: 'CPA %c against the demo account average %b, %n %u',
    platMeta: 'Meta', platTikTok: 'TikTok', platGoogle: 'Google',
    emptyTitle: 'No ad account connected yet',
    emptyBody: '%c has no ad account connected. Connect one to start seeing creative performance here.',
    clientPanelLabel: 'Clients',
    clientLevel: 'Client', clientNone: 'No ad accounts connected', clientUnnamed: 'Unnamed client',
    clientAdd: 'Add client', clientSwitchNote: 'Switching a client does not sign you out.',
    clientAddName: 'Client name',
    clientAddDuplicate: 'You already have a client with this name.',
    clientAddCreate: 'Create', clientAddCancel: 'Cancel', clientAddPending: 'Adding…',
    clientAddElsewhere: 'Being added in another window.',
    clientAddUnknown: 'We could not confirm whether the client was created. Trying again will not create it twice.',
    clientAddUnknownOld: 'Adding a client is not available right now; try again later.',
    clientAddUnavailable: 'Adding a client is not available right now.',
    clientAddNotListed: 'The client was created, but the list could not be reloaded.',
    clientAddRefreshFailed: 'The client list could not be reloaded.',
    clientAddCheckAgain: 'Check again', clientAddAlreadyCreated: 'The client had already been created.',
    clientAddCancelled: 'This attempt was cancelled.',
    clientAddKeepOpen: 'Keep this page open until this is settled.',
    clientAddCheckFailed: 'This attempt could not be checked just now, so nothing was sent. Try again in a moment.',
    clientAddCheckFailedCreated: 'The client was created, but this attempt could not be checked yet. Check again to open it.',
    clientAddInventoryFailed: 'Saved attempts could not be read, so adding a client is paused to avoid creating one twice.',
    clientAddAnyway: 'Add anyway',
    clientAddInventoryRisk: 'This browser is not letting the page read what it saved. If an earlier attempt was saved here, adding now could create that client twice.',
    colChooser: 'Columns', colAlwaysShown: 'always shown', colShowAll: 'Show all columns', colSorted: 'sorted',
    exportCsv: 'Export CSV', exportCsvShort: 'CSV', exportXlsx: 'Export XLSX', xlsxSheetName: 'Creatives',
    exportPending: 'Export is available once the table has loaded for the current selection',
    accLastOne: 'At least one account stays selected. Clear them all from the account control above.',
    accSelectAll: 'Select all accounts',
    csvDataDelayed: 'data delayed', csvCreativeId: 'Creative ID',
  },
  ru: {
    period: 'Период', adAccounts: 'Рекламные аккаунты',
    mergeNote: 'Идентичность креатива - это хеш файла, а не название объявления: один и тот же файл сопоставляется между аккаунтами даже при разных правилах именования.',
    creatives: 'Креативы', rollups: 'Идеи', savedViews: 'Сохранённые виды', reliability: 'Достоверность',
    svWeekly: 'Неделя: отключить / масштабировать', svThin: 'Мало данных: под наблюдением', svTheme: 'Сравнение идей',
    relReliable: 'Достоверно', relProvisional: 'Предварительно', relDirection: 'Направление', relInsufficient: 'Недостаточно',
    legendNote: 'Метки передают выборку. Цвет никогда не единственный сигнал.',
    spend: 'Расход', purch: 'Покупки', pvalue: 'Ценность покупок', impressions: 'Показы',
    sortBy: 'Сортировка', format: 'Формат', audience: 'Аудитория', showSamples: 'Объём выборки', searchName: 'Поиск по названию',
    grid: 'Плитка', table: 'Таблица', creative: 'Креатив', accounts: 'Аккаунты', adj: 'Скорр. ROAS',
    cannotRank: 'Малая выборка: менее надёжно',
    account: 'Аккаунт', ads: 'Объявл.', merged: 'Объединено',
    allCreatives: 'Все креативы',
    sortSpend: 'Расход (по умолчанию)', sortPurch: 'Покупки', sortPvalue: 'Ценность покупок',
    sortRoas: 'ROAS - только достоверные', sortAdj: 'Скорректированный ROAS',
    light: 'Светлая', dark: 'Тёмная',
    allOpt: 'Все', rankable: 'Ранжируемо', share: 'Доля', valShort: 'ценности',
    spendShare: 'Доля расхода', adjShort: 'скорр.',
    clicks: '%n кликов', ofSpend: 'от расхода аккаунта',
    video: 'Видео', image: 'Изображение', carousel: 'Карусель', audienceMixed: 'смешанная',
    defNLead: 'n', defNBody: '- покупки, атрибутированные в выбранном окне отчёта',
    defAdjLead: 'СКОРР. ROAS',
    defStruckLead: 'Цифра по малой выборке', defStruckBody: '- выборка ниже n=%n, поэтому цифра показана приглушённой, с тильдой: сортировка всё равно её ставит, но цифра менее надёжна',
    subAdsCreatives: '%a объявл. / %c {c:креатив|креатива|креативов}', subOrders: '%n заказов', subValue: '%v ценности',
    subBelow: '%c ниже n=%n', subCpa: 'CPA %v', subCpm: 'CPM %v',
    resultCount: '%c {c:креатив|креатива|креативов} / %a объявл.',
    accMerged: 'объединено аккаунтов: %n', accOf: '%n из %t {t:аккаунта|аккаунтов|аккаунтов}',
    noDelivery: 'Нет показов за этот период: %n из %t {t:аккаунта|аккаунтов|аккаунтов}', changePeriod: 'Изменить период',
    bannerThin: '%c {c:креатив ещё не собрал|креатива ещё не собрали|креативов ещё не собрали} достаточно данных (%s).',
    compareHypo: 'Сравнить на уровне идей',
    accNote: '%n× / %a объявл.',
    sampleAt: 'n=%n %u · на пороге', sampleShort: 'n=%n %u · не хватает %d до %h',
    parkedNote: 'n=%n · %s · сырой %r → скорр. %a',
    relIns: 'n=%n при расходе %s. Ниже n=%lo выборка не ранжируется: каждый ROAS, CPA и AOV здесь приглушён. Нужно ещё около %d {d:конверсии|конверсий|конверсий} - примерно %c при текущем CPA - чтобы дойти до порога ранжирования.',
    relDir: 'n=%n. Выборка ранжируется, но для вывода нужно n=%lo: ещё около %d {d:конверсии|конверсий|конверсий}. Читайте направление, а не десятичные.',
    relProv: 'n=%n. Выборка ранжируется, но интервал вокруг каждого отношения всё ещё широк: читайте направление, а не десятичные. После n=%hi выборка перестаёт быть причиной сдерживаться; дальше действуют правила существенности вердикта.',
    relRel: 'n=%n при расходе %s. Выборка проходит черту достоверности (n=%hi): эти отношения можно оценивать относительно %ref, при условии полноты итоговых цифр и правил существенности вердикта.',
    partialLong: 'Приём данных по этому аккаунту отстаёт; его цифры могут отражать ещё не всё.',
    loadingCreatives: 'Загрузка креативов…', loadFailed: 'Не удалось загрузить креативы из API', retry: 'Повторить',
    authChecking: 'Проверка сессии…', authTitle: 'Вход',
    authLede: 'Один аккаунт держит все компании, с которыми вы работаете, и их рекламные аккаунты.',
    authNote: 'Используйте учётную запись Google, одобренную для вашей команды.',
    authGoogleButton: 'Войти через Google',
    signOut: 'Выйти',
    authSessionExpired: 'Сессия истекла. Войдите снова.',
    authSignOutFailed: 'Не удалось подтвердить выход. Попробуйте снова.',
    navLabel: 'Основная навигация', navToggle: 'Меню',
    userMenu: 'Аккаунт и настройки', signingOut: 'Выполняется выход', helpLabel: 'Справка: как читать цифры', settingsLead: 'Настройки',
    periodUnit: '%n дн.',
    periodRange: 'Период', periodPicker: 'Выбрать период',
    periodRangeRefused: 'Выбранные даты выходят за историю этой выборки; показаны 14 дней.',
    compareCaption: 'В сравнении с %r',
    notComparedInProgress: 'Без сравнения: сегодняшний день ещё не закончился',
    notComparedUnverified: 'Без сравнения: полноту данных этого аккаунта пока нельзя проверить',
    notComparedGaps: 'Без сравнения: у части креативов в этой выборке есть дни без данных в одном из периодов',
    withheldPartial: 'Без сравнения: один из периодов неполный',
    withheldThin: 'Без сравнения: меньше %n конверсий в одном из периодов',
    withheldNoBase: 'Без сравнения: в предыдущем периоде не с чем сравнивать',
    detailWindowMismatch: 'Список и эта панель показывают разные периоды. Повторите запрос.',
    calPrevMonth: 'Предыдущий месяц', calNextMonth: 'Следующий месяц', calPrevYear: 'Предыдущий год', calNextYear: 'Следующий год',
    calPrevDecade: 'Предыдущее десятилетие', calNextDecade: 'Следующее десятилетие', calMonthLevel: 'Выбрать месяц', calYearLevel: 'Выбрать год',

    hypothesis: 'Идея', technique: 'Визуал',
    hypUnassigned: 'идея не назначена', banner: 'Баннер',
    ideaTagsCol: 'Идея и теги', chipAddIdea: '+ идея', chipAddVisual: '+ визуал', chipClear: 'Убрать',
    message: 'Сообщение', offer: 'Оффер', chipAddMessage: '+ сообщение', chipAddOffer: '+ оффер',
    suggestChip: 'Grovus предлагает: %value', suggestAccept: 'Принять предложение', suggestReject: 'Отклонить предложение',
    suggestionSettled: 'Это предложение уже принято или отклонено.',
    suggestionAxisAssigned: 'Этот тег уже назначен вручную.',
    conversions: 'Конверсии', purchValue: 'Ценность покупок',
    minPeriod: 'Минимум · %d дн.', convAbbr: 'конв.', floorClear: 'Сбросить',
    floorNote: '%c {c:креатив скрыт|креатива скрыто|креативов скрыто} вашими минимумами (%s расхода). Итоги выше по-прежнему учитывают все %t.',
    bucketNote: '«%v» · креативов: %c. Все цифры на этом экране теперь описывают %a, а не весь аккаунт.',
    bucketBack: 'Назад к сравнению',
    axisHyp: 'эту идею', axisVisual: 'этот визуальный приём', axisAud: 'эту аудиторию',
    focusSome: '%n из %t {t:креатива|креативов|креативов} в этом срезе {n:подпадает|подпадают|подпадают} под правило «%v». {n:Он|Они|Они} в начале списка.',
    focusNone: 'Сейчас ни один креатив в этом срезе не подпадает под правило «%v». Список отсортирован по выводу, ближайшие сверху.',
    focusScale: 'Масштабировать', focusKill: 'Отключить',
    vlScale: 'Масштабировать', vlHold: 'Держать', vlWatch: 'Наблюдать', vlRework: 'Переделать', vlKill: 'Отключить', vlInsufficient: 'Данных мало', vlNotStarted: 'Не запущен',
    vqHook: 'хук', vqCtr: 'CTR', vcCheck: '%name %v при %bm',
    vrBetter: '%metric %r на %p% лучше, относительно %ref - разрыв превышает %b%, необходимые при n=%n %u. Правило: Масштабировать.',
    vrWithin: '%metric %r отличается на %p% относительно %ref - в пределах %b%, необходимых при n=%n %u, чтобы отличаться от шума. Правило: Держать.',
    vrWorseDeliveryFine: '%metric %r на %p% хуже, относительно %ref, при n=%n %u; доставка в норме (%checks). Правило: Переделать.',
    vrWorseDeliveryWeak: '%metric %r на %p% хуже, относительно %ref, при n=%n %u; слабая доставка: %checks. Правило: Отключить.',
    vrDeliveryUnchecked: '%metric %r на %p% хуже, относительно %ref, при n=%n %u; доставку не проверить. Правило: Отключить.',
    vrBelowProvisional: 'n=%n %u: ранжируется, но для вывода нужно n=%lo. Правило: Наблюдать.',
    vrBelowDirection: 'n=%n %u ниже порога n=%dir - выборка не ранжируется. Правило: Данных мало.',
    vrNeverDelivered: 'Ни одного показа за всю прочитанную историю.',
    vrNoReference: 'Нет среднего %metric за 90 дней в %cur - не с чем сравнить, стоп-лосс не рассчитать. Правило: Данных мало.',
    vrNoConversions: 'Нет конверсий в %cur за 90 дней: нет среднего ROAS и среднего чека, стоп-лосс не рассчитать. Правило: Данных мало.',
    vrStopLoss: '%s при %n {n:конверсии|конверсиях|конверсиях}: даже при лучшем исходе %metric %bound %bc против %refv (стоп-лосс %th). Правило: Отключить.',
    vrBoundRoas: 'не выше', vrBoundCpa: 'не ниже',
    vrRefTarget: 'цели', vrRefAverage: 'среднего за 90 дней', vrRefAverageIn: 'среднего за 90 дней · %cur',
    vqNoHistory: 'нет истории конверсий',
    relStopLoss: 'n=%n - выборка мала для вывода относительно %ref, но стоп-лосс от n не зависит: даже при лучшем исходе %metric хуже %ref.',
    vrNoRoas: 'ROAS не вычислить: нет расхода или ценности конверсий. Правило: Наблюдать.',
    vrNoCpa: 'CPA не вычислить: нет расхода. Правило: Наблюдать.',
    vrNoConversionsCpa: 'Нет конверсий в %cur за 90 дней: нет среднего CPA, стоп-лосс не рассчитать. Правило: Данных мало.',
    vrDataIncomplete: 'Данные по «%m» неполные - частично полученные конверсии не основание для вывода. Правило: Наблюдать.',
    hookCol: 'Hook', holdCol: 'Hold', convCol: 'Конверсии', adsCol: 'Объявлений', verdictCol: 'Вывод',
    ruleLead: 'Сработавшее правило', roasCol: 'ROAS', cpaCol: 'CPA', aovCol: 'AOV', cpcCol: 'CPC', cpmCol: 'CPM',
    defBandLead: 'Полоса значимости',
    defBandBody: '- разрыв со средним по аккаунту, который креатив должен превысить, прежде чем сработает «Масштабировать» или «Отключить». Он зависит от выборки (примерно 2 стандартные ошибки, 200/√n): 28% при n=50, 20% при n=100 - но не ниже 10%, где реальная разница уже слишком мала, чтобы на неё реагировать. Среднее считается по аккаунтам в срезе: %a.',
    defFunLead: 'HOOK / HOLD',
    defFunBody: '- HOOK делит просмотры 3 сек. на показы; HOLD делит ThruPlays на просмотры 3 сек. Знаменатели разные, каждый - процент; выборки велики, поэтому они честно ранжируются там, где отношения по покупкам - нет. Только видео.',
    defShapeLead: 'ФОРМАТ / ДЛИТЕЛЬНОСТЬ',
    defShapeBody: '- форма ассета и, для видео, его длительность. Миниатюры придут вместе с загрузкой данных; до тех пор вместо них стоит метка типа, а не заглушка, которая изображала бы узнаваемость.',
    hintShape: 'Форма ассета и метка типа: эта колонка несёт сам креатив.',
    hintPurch: 'Покупки, атрибутированные в выбранном окне отчёта; это выборка, на которой стоят все отношения по исходу.',
    hintRoas: 'ROAS: ценность покупок, делённая на расход. Приглушённая цифра с тильдой - контекст, а не ранжируемое сравнение.',
    hintAdj: 'Скорректированный ROAS: сырой ROAS, подтянутый к ROAS аккаунта за тот же период; при 50 покупках у них равный вес. Тонкие строки отложены, а не ранжированы.',
    hintFun: 'HOOK делит просмотры 3 сек. на показы; HOLD делит ThruPlays на просмотры 3 сек. Знаменатели разные, каждый - процент; только видео.',
    hintAov: 'Средний чек (AOV): ценность покупок, делённая на конверсии.',
    hintBand: 'Полоса значимости: разрыв, который креатив должен пройти, прежде чем сработает «Масштабировать» или «Отключить». Среднее, с которым он сравнивается, - среднее по аккаунтам в срезе; на демоклиенте цифры демо.',
    hintRankable: '«Ранжируемо» значит, что конверсии дошли до порога ранжирования; ниже её сырой коэффициент остаётся контекстом, а сравнение уходит в объединённые группы.',
    hintNameShape: 'Что в колонке «Креатив»?',
    hintNamePurch: 'Что такое конверсии?',
    hintNameRoas: 'Что такое ROAS?',
    hintNameAdj: 'Что такое скорректированный ROAS?',
    hintNameFun: 'Что такое Hook и Hold?',
    hintNameAov: 'Что такое AOV?',
    hintNameBand: 'Что такое полоса значимости?',
    hintNameVerdict: 'Как читать вывод?', verdictGapLine: 'При n = %n разрыв должен превышать %b %.',
    hintNameRankable: 'Что значит «ранжируемо»?',
    howToRead: 'Как читать эту таблицу',
    tableScrollHint: 'Прокрутите по горизонтали, чтобы увидеть все колонки ↔',
    defAdjPooledBody: '- сырой ROAS, подтянутый к ROAS аккаунта за тот же период и те же аккаунты: (n x ROAS + 50 x ROAS аккаунта) / (n + 50). При 50 покупках у них равный вес. Ниже порога ранжирования это контекст, а не ранжируемое сравнение. Прочерк, когда ROAS креатива или эталон аккаунта недоступен.',
    partialMeasure: 'Частично - цифра охватывает не всё или объединяет данные, которые не вполне сопоставимы',
    partialWord: 'частично',
    partialReasonCreatives: 'креативов с неполными данными: %c из %t',
    partialReasonScope: 'видна не вся доставка в выборке, см. примечания выше',
    partialHintName: 'Почему данные частичные?',
    partialLegendMeasure: 'частично: цифра охватывает не всё или объединяет не вполне сопоставимые данные',
    partialLegendLagging: 'после аккаунта: его данные могут быть устаревшими',
    unitOne: 'конверсия', unitFew: 'конверсии', unitMany: 'конверсий',
    emptyCompany: 'К этой компании ещё не подключён ни один рекламный аккаунт. Подключите аккаунт, и здесь появятся её креативы, идеи и итоги.',
    emptySelection: 'Не выбран ни один рекламный аккаунт. Выберите хотя бы один, чтобы увидеть его креативы.',
    emptyNoCreatives: 'В этом окне не показывался ни один креатив. Итоги нулевые потому, что ничего не работало, а не потому, что ничего не сработало.',
    emptyFiltered: 'Ни один креатив не подходит под эти фильтры. Итоги выше по-прежнему описывают весь срез.',
    errUnknownAudience: 'Ни одно объявление в выбранных аккаунтах не было нацелено на эту аудиторию. Покажите все аудитории.',
    clearAudience: 'Показать все аудитории',
    errMixedProvenance: 'В выборе смешаны демо- и реальные рекламные аккаунты, а их показатели никогда не складываются. Уберите демоаккаунты %a из выбора в панели аккаунтов выше.',
    errMixedCurrency: 'Выбранные рекламные аккаунты отчитываются более чем в одной валюте. Один запрос охватывает одну, поэтому уберите %a из выбора в панели аккаунтов выше.',
    errUnknownAccount: 'У этой компании больше нет рекламного аккаунта %a. Измените выбор в панели аккаунтов выше; повтор запроса не поможет.',
    errRequest: 'Запрос отклонён: %c',
    widenTo: 'Расширить до %n дн.', widenTitle: 'При текущем темпе окно %n дн. прошло бы порог достоверности',
    sortName: 'Название', sortAccounts: 'Аккаунты', sortHypothesis: 'Идея', sortTechnique: 'Визуал',
    sortHook: 'Hook', sortHold: 'Hold', sortCtr: 'CTR', sortCpa: 'CPA', sortAov: 'AOV',
    sortCpc: 'CPC', sortAds: 'Объявлений', sortVerdict: 'Вывод',
    sortShare: 'Доля расхода', sortCpm: 'CPM', sortActiveDays: 'Дней с показами', shareCol: 'Доля', activeDaysCol: 'Дней с показами',
    pausedSince: (date) => `на паузе с ${date}`,
    hintShare: 'Доля в расходе показанных креативов. По строкам на экране в сумме 100%.',
    hintNameShare: 'Что такое доля?',
    rankableSub: '%c из %t', tableCaption: 'Креативы, по ассетам', adsMerged: 'объявлений: %n',
    detailAria: 'Панель деталей креатива', detailClose: 'Закрыть панель деталей',
    detailScroll: 'Детали креатива, прокручиваемая область',
    ticksAria: 'Достоверность: %b, n=%n',
    ticksAriaUnknown: 'Достоверность оценить нельзя: количество конверсий не сообщено',
    detailMeta: '%k - %d - аккаунтов: %n - объявлений: %a - n=%p',
    detailMetaStill: '%k - аккаунтов: %n - объявлений: %a - n=%p',
    shareSub: 'от расхода сегмента, по последней загрузке списка',
    adsBlockTitle: 'Объявления',
    adsBlockCaption: 'Все объявления креатива в этой выборке, с картинкой, которая шла под каждым',
    adStatus: 'Статус',
    adReason: 'Почему здесь',
    reasonFounder: 'Первое объявление этого файла',
    reasonExactBytes: 'Тот же файл',
    reasonNearExact: 'Почти такое же изображение',
    reasonConfirmedSuggestion: 'Подтверждённое предложение',
    reasonMergedByHand: 'Объединено вручную',
    reasonDetached: 'Отделено',
    splitCaption: 'Разбивка по аккаунтам: вместе строки дают итоговую цифру внизу',
    roasNote: 'ROAS - это вся стоимость покупок, делённая на весь расход: и в итоговой строке, и в каждой строке аккаунта.',
    identityNote: 'Группа креативов %h - аккаунтов: %n - названий объявлений: %a',
    notFoundTitle: 'Нет цифр по этому креативу в этой выборке',
    notFoundBody: 'Его объявления вне текущих аккаунтов или аудитории, либо они удалены. Повторите запрос или закройте панель и выберите другой креатив.',
    relPartial: 'Цифры по «%m» для этого креатива неполные или объединяют данные, которые не вполне сопоставимы. Каждое отношение, опирающееся на них, припарковано.',
    hypTitle: 'Сравнение идей',
    hypLede: 'Идея объединяет все креативы, которые проверяют одну и ту же тему, поэтому суммарная выборка проходит порог, недостижимый для отдельных креативов. Это основная поверхность сравнения, а не фильтр.',
    hypAxisNote: 'Идея - ключ группировки. Визуальный приём и аудитория - атрибуты креативов внутри идеи, здесь они даны как вспомогательные разрезы.',
    hypDrill: 'Откройте строку, чтобы увидеть креативы, которые в неё входят: экран креативов сузится до этой группы.',
    hypSub: 'креативов: %c · n=%n %u',
    versionsLine: 'Версии: %v',
    hypOpen: 'Открыть креативы этой группы: %c',
    hypFoot: 'Группы наследуют правило креативов. %list; штриховка отмечает остальные - их ROAS указывает направление, не более. По аудиториям аккаунт сводится к %aud - один креатив может сидеть в нескольких группах аудиторий, а его конверсии - в одной. %win%un',
    hypFootNoAud: 'Группы наследуют правило креативов. %list; штриховка отмечает остальные - их ROAS указывает направление, не более. %win%un',
    footWindowLast: 'Окно - последние %p дн.',
    footWindowRange: 'Окно - %r.',
    groupHypothesis: 'Идея', groupVisual: 'Визуальный приём', groupAudience: 'Аудитория',
    bucketOne: 'группа', bucketFew: 'группы', bucketMany: 'групп',
    techUnassigned: 'визуал не назначен',
    msgUnassigned: 'сообщение не назначено', offerUnassigned: 'оффер не назначен',
    quoteOpen: '«', quoteClose: '»', andJoin: ' и ',
    footList: 'Порог n=%n %u проходят %list',
    footNone: 'Пока ни одна группа не проходит порог n=%n %u',
    unassignedFootHyp: ' Креативы без назначенной идеи попадают в «%v».',
    unassignedFootVisual: ' Креативы без назначенного визуального приёма попадают в «%v».',
    jProposes: 'Гровус предлагает',
    jBasis: 'CPA %c против среднего по аккаунту %b, %n %u',
    jWaiting: 'Ждёт данных - ещё %n %u',
    jProvNote: 'предварительная выборка',
    jvConfirmed: 'Подтверждена', jvRefuted: 'Опровергнута',
    frStepAccount: 'Ваш аккаунт', frStepCompany: 'Ваша первая компания', frStepConnect: 'Подключите рекламный аккаунт',
    frStepsLabel: 'Шаги первого запуска',
    frAccountLabel: 'Ваше имя', frCompanyLabel: 'Название компании',
    frCompanyPlaceholder: 'Бренд, для которого вы запускаете рекламу.',
    frBack: 'Назад', frNext: 'Далее', frSkip: 'Пропустить пока', frFinish: 'Готово',
    frConnectMeta: 'Подключить Meta Ads', notConnectedYet: 'Пока не подключено',
    accStateWaiting: 'Подключён. Ждём первых данных.', accStateConnected: 'Подключён',
    accStateExpiring: 'Доступ истекает через %n дн.',
    accStateExpired: 'Доступ истёк. Grovus его обновляет.', accStateRevoked: 'Доступ отозван',
    accStateRejected: 'Доступ отклонён. Grovus разбирается.',
    accStateWithdrawn: 'Больше не открыт для Grovus',
    accConnectPlatform: 'Подключить рекламную платформу',
    accConnectNeed: 'Grovus нужен доступ администратора к вашему бизнес-портфолио Meta. В инструкции каждый шаг показан на скриншоте.',
    accConnectThen: 'После этого ваши рекламные аккаунты появятся здесь, в разделе «%shared». Подключите каждый.',
    accConnectGuide: 'Открыть инструкцию',
    accShared: 'Открыто для Grovus', accConnect: 'Подключить', accRemove: 'Удалить',
    accRemoveConfirm: 'Удалить демоаккаунт и его показатели? Это нельзя отменить.', accRemoveKeep: 'Оставить',
    accDemoCompanyHint: 'Это демокомпания. Подключайте свои аккаунты в настоящей компании.',
    accDemoPrompt: 'Вы создаёте первый настоящий аккаунт. Удалить демоаккаунт?',
    accDemoPromptDelete: 'Удалить', accDemoPromptKeep: 'Оставить',
    accNotAvailable: 'Этот аккаунт больше недоступен', accAlreadyRemoved: 'Уже удалён',
    accUnreachable: 'Не удалось связаться с Grovus. Список обновлён.',
    accRefreshFailed: 'Изменение отправлено, но список аккаунтов не удалось обновить.',
    accSharedFailed: 'Не удалось загрузить аккаунты, открытые для Grovus.',
    demoMark: 'Демо', demoMarkLong: 'Демоаккаунт',
    demoNotice: 'В этой компании есть демоаккаунты. Демопоказатели помечены и учитываются отдельно.',
    unpulledNotice: 'Метрики по этим аккаунтам ещё не загружены.',
    scopeUnplaced: 'Некоторые объявления ещё не удалось определить, поэтому данные здесь неполные.',
    scopeUncovered: 'Некоторые дни ещё не загружены, поэтому данные здесь неполные.',
    scopeExcluded: 'Объявления, которые не удалось определить, исключены, поэтому данные охватывают только определённые объявления.',
    scopeExcludedCount: 'Исключено объявлений: %c в этом периоде.',
    scopeExcludedCompared: 'Исключено объявлений: %c в этом периоде, %p в предыдущем.',
    scopeReason: '%r: %c {c:объявление|объявления|объявлений}',
    scopeReasonExcluded: '%r, исключено: %c {c:объявление|объявления|объявлений}',
    unplacedReasons: {
      pending: 'Ещё не обработано',
      unrecognised_shape: 'Нераспознанный формат креатива',
      no_creative: 'У объявления нет креатива',
      creative_unreadable: 'Не удалось прочитать креатив',
      fetch_failed: 'Не удалось получить креатив',
      no_thumbnail: 'Нет изображения или превью',
      refused_host: 'Хост изображения не разрешён',
      download_failed: 'Не удалось скачать изображение',
      hash_failed: 'Не удалось обработать изображение',
      deferred: 'Ожидает следующего прохода',
      placeholder_image: 'Meta отдаёт заглушку, изображения нет',
    },
    detailAttributionAcross: 'Конверсии объединяют две разные настройки атрибуции из разных аккаунтов.',
    frDemoHint: 'Демоданные находятся в компании «Демо». Ваша компания пуста, пока вы не подключите аккаунт.',
    frDuplicateCheck: 'Возможно, эта компания уже создана. Пока не удалось проверить ваши компании.',
    frReservedName: 'Это имя занято демокомпанией. Выберите другое имя.',
    jBasisDemo: 'CPA %c против среднего по демоаккаунту %b, %n %u',
    platMeta: 'Meta', platTikTok: 'TikTok', platGoogle: 'Google',
    emptyTitle: 'Рекламный аккаунт ещё не подключён',
    emptyBody: 'У «%c» нет подключённого рекламного аккаунта. Подключите его, чтобы увидеть эффективность креативов здесь.',
    clientPanelLabel: 'Компании',
    clientLevel: 'Компания', clientNone: 'Рекламные аккаунты не подключены', clientUnnamed: 'Компания без названия',
    clientAdd: 'Добавить компанию', clientSwitchNote: 'Переключение компании не требует повторного входа.',
    clientAddName: 'Название компании',
    clientAddDuplicate: 'У вас уже есть компания с таким названием.',
    clientAddCreate: 'Создать', clientAddCancel: 'Отмена', clientAddPending: 'Добавляем…',
    clientAddElsewhere: 'Добавляется в другом окне.',
    clientAddUnknown: 'Не удалось подтвердить, создана ли компания. Повторная попытка не создаст её дважды.',
    clientAddUnknownOld: 'Добавление компании сейчас недоступно, попробуйте позже.',
    clientAddUnavailable: 'Добавление компании сейчас недоступно.',
    clientAddNotListed: 'Компания создана, но список не удалось обновить.',
    clientAddRefreshFailed: 'Не удалось обновить список компаний.',
    clientAddCheckAgain: 'Проверить снова', clientAddAlreadyCreated: 'Компания уже была создана.',
    clientAddCancelled: 'Эта попытка отменена.',
    clientAddKeepOpen: 'Не закрывайте страницу, пока это не решится.',
    clientAddCheckFailed: 'Не удалось проверить эту попытку, поэтому ничего не отправлено. Повторите чуть позже.',
    clientAddCheckFailedCreated: 'Компания создана, но эту попытку пока не удалось проверить. Проверьте снова, чтобы открыть её.',
    clientAddInventoryFailed: 'Не удалось прочитать сохранённые попытки, поэтому добавление компании приостановлено, чтобы не создать её дважды.',
    clientAddAnyway: 'Всё равно добавить',
    clientAddInventoryRisk: 'Браузер не даёт странице прочитать сохранённое. Если здесь была сохранена прежняя попытка, новое добавление может создать эту компанию дважды.',
    colChooser: 'Столбцы', colAlwaysShown: 'всегда виден', colShowAll: 'Показать все столбцы', colSorted: 'сортировка',
    exportCsv: 'Экспорт CSV', exportCsvShort: 'CSV', exportXlsx: 'Экспорт XLSX', xlsxSheetName: 'Креативы',
    exportPending: 'Экспорт станет доступен, когда таблица загрузится для текущего выбора',
    accLastOne: 'Хотя бы один аккаунт остаётся выбранным. Снять все можно в контроле аккаунтов выше.',
    accSelectAll: 'Выбрать все аккаунты',
    csvDataDelayed: 'данные запаздывают', csvCreativeId: 'ID креатива',
  },
  uk: {
    period: 'Період', adAccounts: 'Рекламні акаунти',
    mergeNote: 'Ідентичність креативу - це хеш файлу, а не назва оголошення: той самий файл зіставляється між акаунтами навіть за різних правил іменування.',
    creatives: 'Креативи', rollups: 'Ідеї', savedViews: 'Збережені вигляди', reliability: 'Достовірність',
    svWeekly: 'Тиждень: вимкнути / масштабувати', svThin: 'Мало даних: під наглядом', svTheme: 'Порівняння ідей',
    relReliable: 'Достовірно', relProvisional: 'Попередньо', relDirection: 'Напрямок', relInsufficient: 'Недостатньо',
    legendNote: 'Мітки передають вибірку. Колір ніколи не є єдиним сигналом.',
    spend: 'Витрати', purch: 'Купівлі', pvalue: 'Цінність купівель', impressions: 'Покази',
    sortBy: 'Сортування', format: 'Формат', audience: 'Аудиторія', showSamples: 'Обсяг вибірки', searchName: 'Пошук за назвою',
    grid: 'Картки', table: 'Таблиця', creative: 'Креатив', accounts: 'Акаунти', adj: 'Скор. ROAS',
    cannotRank: 'Мала вибірка: менш надійно',
    account: 'Акаунт', ads: 'Огол.', merged: 'Об’єднано',
    allCreatives: 'Усі креативи',
    sortSpend: 'Витрати (типово)', sortPurch: 'Купівлі', sortPvalue: 'Цінність купівель',
    sortRoas: 'ROAS - лише достовірні', sortAdj: 'Скоригований ROAS',
    light: 'Світла', dark: 'Темна',
    allOpt: 'Усі', rankable: 'Можна ранжувати', share: 'Частка', valShort: 'цінності',
    spendShare: 'Частка витрат', adjShort: 'скор.',
    clicks: '%n кліків', ofSpend: 'від витрат акаунта',
    video: 'Відео', image: 'Зображення', carousel: 'Карусель', audienceMixed: 'змішана',
    defNLead: 'n', defNBody: '- купівлі, атрибутовані у вибраному вікні звіту',
    defAdjLead: 'СКОР. ROAS',
    defStruckLead: 'Цифра за малою вибіркою', defStruckBody: '- вибірка нижче n=%n, тому цифру показано приглушеною, з тильдою: сортування все одно її ставить, але цифра менш надійна',
    subAdsCreatives: '%a огол. / %c {c:креатив|креативи|креативів}', subOrders: '%n замовлень', subValue: '%v цінності',
    subBelow: '%c нижче n=%n', subCpa: 'CPA %v', subCpm: 'CPM %v',
    resultCount: '%c {c:креатив|креативи|креативів} / %a огол.',
    accMerged: 'об’єднано акаунтів: %n', accOf: '%n з %t {t:акаунта|акаунтів|акаунтів}',
    noDelivery: 'Немає показів за цей період: %n з %t {t:акаунта|акаунтів|акаунтів}', changePeriod: 'Змінити період',
    bannerThin: '%c {c:креатив ще не зібрав|креативи ще не зібрали|креативів ще не зібрали} достатньо даних (%s).',
    compareHypo: 'Порівняти на рівні ідей',
    accNote: '%n× / %a огол.',
    sampleAt: 'n=%n %u · на порозі', sampleShort: 'n=%n %u · бракує %d до %h',
    parkedNote: 'n=%n · %s · сирий %r → скор. %a',
    relIns: 'n=%n за витрат %s. Нижче n=%lo вибірка не ранжується: кожен ROAS, CPA й AOV тут приглушено. Потрібно ще близько %d {d:конверсії|конверсій|конверсій} - приблизно %c за поточного CPA - щоб дійти до порогу ранжування.',
    relDir: 'n=%n. Вибірка ранжується, але для висновку потрібно n=%lo: ще близько %d {d:конверсії|конверсій|конверсій}. Читайте напрямок, а не десяткові.',
    relProv: 'n=%n. Вибірка ранжується, але інтервал навколо кожного відношення все ще широкий: читайте напрямок, а не десяткові. Після n=%hi вибірка перестає бути причиною стримуватися; далі діють правила суттєвості висновку.',
    relRel: 'n=%n за витрат %s. Вибірка проходить межу достовірності (n=%hi): ці відношення можна оцінювати відносно %ref, за умови повноти підсумкових цифр і правил суттєвості висновку.',
    partialLong: 'Приймання даних по цьому акаунту відстає; його цифри можуть відображати ще не все.',
    loadingCreatives: 'Завантаження креативів…', loadFailed: 'Не вдалося завантажити креативи з API', retry: 'Повторити',
    authChecking: 'Перевірка сесії…', authTitle: 'Вхід',
    authLede: 'Один акаунт містить усі компанії, з якими ви працюєте, та їхні рекламні акаунти.',
    authNote: 'Використовуйте обліковий запис Google, схвалений для вашої команди.',
    authGoogleButton: 'Увійти через Google',
    signOut: 'Вийти',
    authSessionExpired: 'Сесія закінчилася. Увійдіть знову.',
    authSignOutFailed: 'Не вдалося підтвердити вихід. Спробуйте знову.',
    navLabel: 'Основна навігація', navToggle: 'Меню',
    userMenu: 'Акаунт і налаштування', signingOut: 'Виконується вихід', helpLabel: 'Довідка: як читати цифри', settingsLead: 'Налаштування',
    periodUnit: '%n дн.',
    periodRange: 'Період', periodPicker: 'Вибрати період',
    periodRangeRefused: 'Вибрані дати виходять за межі історії цієї вибірки; показано 14 днів.',
    compareCaption: 'Порівняно з %r',
    notComparedInProgress: 'Без порівняння: сьогоднішній день ще не закінчився',
    notComparedUnverified: 'Без порівняння: повноту даних цього акаунта поки не можна перевірити',
    notComparedGaps: 'Без порівняння: частина креативів у цій вибірці має дні без даних в одному з періодів',
    withheldPartial: 'Без порівняння: один із періодів неповний',
    withheldThin: 'Без порівняння: менше ніж %n конверсій в одному з періодів',
    withheldNoBase: 'Без порівняння: у попередньому періоді немає з чим порівнювати',
    detailWindowMismatch: 'Список і ця панель показують різні періоди. Повторіть запит.',
    calPrevMonth: 'Попередній місяць', calNextMonth: 'Наступний місяць', calPrevYear: 'Попередній рік', calNextYear: 'Наступний рік',
    calPrevDecade: 'Попереднє десятиліття', calNextDecade: 'Наступне десятиліття', calMonthLevel: 'Вибрати місяць', calYearLevel: 'Вибрати рік',

    hypothesis: 'Ідея', technique: 'Візуал',
    hypUnassigned: 'ідею не призначено', banner: 'Банер',
    ideaTagsCol: 'Ідея та теги', chipAddIdea: '+ ідея', chipAddVisual: '+ візуал', chipClear: 'Прибрати',
    message: 'Повідомлення', offer: 'Офер', chipAddMessage: '+ повідомлення', chipAddOffer: '+ офер',
    suggestChip: 'Grovus пропонує: %value', suggestAccept: 'Прийняти пропозицію', suggestReject: 'Відхилити пропозицію',
    suggestionSettled: 'Цю пропозицію вже прийнято або відхилено.',
    suggestionAxisAssigned: 'Цей тег уже призначено вручну.',
    conversions: 'Конверсії', purchValue: 'Цінність купівель',
    minPeriod: 'Мінімум · %d дн.', convAbbr: 'конв.', floorClear: 'Скинути',
    floorNote: '%c {c:креатив приховано|креативи приховано|креативів приховано} вашими мінімумами (%s витрат). Підсумки вище, як і раніше, враховують усі %t.',
    bucketNote: '«%v» · креативів: %c. Усі цифри на цьому екрані тепер описують %a, а не весь акаунт.',
    bucketBack: 'Назад до порівняння',
    axisHyp: 'цю ідею', axisVisual: 'цей візуальний прийом', axisAud: 'цю аудиторію',
    focusSome: '%n з %t {t:креативу|креативів|креативів} у цьому зрізі {n:підпадає|підпадають|підпадають} під правило «%v». {n:Він|Вони|Вони} на початку списку.',
    focusNone: 'Зараз жоден креатив у цьому зрізі не підпадає під правило «%v». Список відсортовано за висновком, найближчі зверху.',
    focusScale: 'Масштабувати', focusKill: 'Вимкнути',
    vlScale: 'Масштабувати', vlHold: 'Тримати', vlWatch: 'Спостерігати', vlRework: 'Переробити', vlKill: 'Вимкнути', vlInsufficient: 'Замало даних', vlNotStarted: 'Не запущено',
    vqHook: 'хук', vqCtr: 'CTR', vcCheck: '%name %v за %bm',
    vrBetter: '%metric %r на %p% краще, відносно %ref - розрив перевищує %b%, потрібні за n=%n %u. Правило: Масштабувати.',
    vrWithin: '%metric %r відрізняється на %p% відносно %ref - у межах %b%, потрібних за n=%n %u, щоб відрізнятися від шуму. Правило: Тримати.',
    vrWorseDeliveryFine: '%metric %r на %p% гірше, відносно %ref, за n=%n %u; доставка в нормі (%checks). Правило: Переробити.',
    vrWorseDeliveryWeak: '%metric %r на %p% гірше, відносно %ref, за n=%n %u; слабка доставка: %checks. Правило: Вимкнути.',
    vrDeliveryUnchecked: '%metric %r на %p% гірше, відносно %ref, за n=%n %u; доставку не перевірити. Правило: Вимкнути.',
    vrBelowProvisional: 'n=%n %u: ранжується, але для висновку потрібно n=%lo. Правило: Спостерігати.',
    vrBelowDirection: 'n=%n %u нижче порогу n=%dir - вибірка не ранжується. Правило: Замало даних.',
    vrNeverDelivered: 'Жодного показу за всю прочитану історію.',
    vrNoReference: 'Немає середнього %metric за 90 днів у %cur - немає з чим порівняти, стоп-лос не розрахувати. Правило: Замало даних.',
    vrNoConversions: 'Немає конверсій у %cur за 90 днів: немає середнього ROAS і середнього чека, стоп-лос не розрахувати. Правило: Замало даних.',
    vrStopLoss: '%s за %n %u: навіть за найкращого результату %metric %bound %bc проти %refv (стоп-лос %th). Правило: Вимкнути.',
    vrBoundRoas: 'не вище', vrBoundCpa: 'не нижче',
    vrRefTarget: 'цілі', vrRefAverage: 'середнього за 90 днів', vrRefAverageIn: 'середнього за 90 днів · %cur',
    vqNoHistory: 'немає історії конверсій',
    relStopLoss: 'n=%n - вибірка замала для висновку відносно %ref, але стоп-лос від n не залежить: навіть за найкращого результату %metric гірший відносно %ref.',
    vrNoRoas: 'ROAS не обчислити: немає витрат або цінності конверсій. Правило: Спостерігати.',
    vrNoCpa: 'CPA не обчислити: немає витрат. Правило: Спостерігати.',
    vrNoConversionsCpa: 'Немає конверсій у %cur за 90 днів: немає середнього CPA, стоп-лос не розрахувати. Правило: Замало даних.',
    vrDataIncomplete: 'Дані за «%m» неповні - частково отримані конверсії не підстава для висновку. Правило: Спостерігати.',
    hookCol: 'Hook', holdCol: 'Hold', convCol: 'Конверсії', adsCol: 'Оголошень', verdictCol: 'Висновок',
    ruleLead: 'Правило, що спрацювало', roasCol: 'ROAS', cpaCol: 'CPA', aovCol: 'AOV', cpcCol: 'CPC', cpmCol: 'CPM',
    defBandLead: 'Смуга значущості',
    defBandBody: '- розрив із середнім по акаунту, який креатив має перевищити, перш ніж спрацює «Масштабувати» або «Вимкнути». Він залежить від вибірки (приблизно 2 стандартні похибки, 200/√n): 28% за n=50, 20% за n=100 - але не нижче 10%, де реальна різниця вже надто мала, щоб на неї реагувати. Середнє рахується по акаунтах у зрізі: %a.',
    defFunLead: 'HOOK / HOLD',
    defFunBody: '- HOOK ділить перегляди 3 сек. на покази; HOLD ділить ThruPlays на перегляди 3 сек. Знаменники різні, кожен - відсоток; вибірки великі, тому вони чесно ранжуються там, де відношення за купівлями - ні. Лише відео.',
    defShapeLead: 'ФОРМАТ / ТРИВАЛІСТЬ',
    defShapeBody: '- форма асета і, для відео, його тривалість. Мініатюри з’являться разом із завантаженням даних; доти замість них стоїть мітка типу, а не заглушка, яка імітувала б впізнаваність.',
    hintShape: 'Форма асета і мітка типу: ця колонка несе сам креатив.',
    hintPurch: 'Купівлі, атрибутовані у вибраному вікні звіту; це вибірка, на якій стоять усі відношення за результатом.',
    hintRoas: 'ROAS: цінність купівель, поділена на витрати. Приглушена цифра з тильдою - контекст, а не порівняння для ранжування.',
    hintAdj: 'Скоригований ROAS: сирий ROAS, підтягнутий до ROAS акаунта за той самий період; при 50 покупках вони мають рівну вагу. Тонкі рядки відкладено, а не ранжовано.',
    hintFun: 'HOOK ділить перегляди 3 сек. на покази; HOLD ділить ThruPlays на перегляди 3 сек. Знаменники різні, кожен - відсоток; лише відео.',
    hintAov: 'Середній чек (AOV): цінність купівель, поділена на конверсії.',
    hintBand: 'Смуга значущості: розрив, який креатив має пройти, перш ніж спрацює «Масштабувати» або «Вимкнути». Середнє, з яким він порівнюється, - середнє по акаунтах у зрізі; на демоклієнті цифри демо.',
    hintRankable: '«Можна ранжувати» означає, що конверсії дійшли до порогу ранжування; нижче неї сирий коефіцієнт залишається контекстом, а порівняння переходить в об’єднані групи.',
    hintNameShape: 'Що в колонці «Креатив»?',
    hintNamePurch: 'Що таке конверсії?',
    hintNameRoas: 'Що таке ROAS?',
    hintNameAdj: 'Що таке скоригований ROAS?',
    hintNameFun: 'Що таке Hook і Hold?',
    hintNameAov: 'Що таке AOV?',
    hintNameBand: 'Що таке смуга значущості?',
    hintNameVerdict: 'Як читати висновок?', verdictGapLine: 'При n = %n розрив має перевищувати %b %.',
    hintNameRankable: 'Що означає «можна ранжувати»?',
    howToRead: 'Як читати цю таблицю',
    tableScrollHint: 'Прокрутіть горизонтально, щоб побачити всі колонки ↔',
    defAdjPooledBody: '- сирий ROAS, підтягнутий до ROAS акаунта за той самий період і ті самі акаунти: (n x ROAS + 50 x ROAS акаунта) / (n + 50). При 50 покупках вони мають рівну вагу. Нижче порогу ранжування це контекст, а не порівняння для ранжування. Прочерк, коли ROAS креативу або середній ROAS акаунта недоступний.',
    partialMeasure: 'Частково - цифра охоплює не все або об’єднує дані, які не цілком порівнянні',
    partialWord: 'частково',
    partialReasonCreatives: 'креативів із неповними даними: %c з %t',
    partialReasonScope: 'видно не всю доставку у вибірці, див. примітки вище',
    partialHintName: 'Чому дані часткові?',
    partialLegendMeasure: 'частково: цифра охоплює не все або об’єднує не цілком порівнянні дані',
    partialLegendLagging: 'після акаунта: його дані можуть бути застарілими',
    unitOne: 'конверсія', unitFew: 'конверсії', unitMany: 'конверсій',
    emptyCompany: 'До цієї компанії ще не підключено жодного рекламного акаунта. Підключіть акаунт, і тут з’являться її креативи, ідеї та підсумки.',
    emptySelection: 'Не вибрано жодного рекламного акаунта. Виберіть хоча б один, щоб побачити його креативи.',
    emptyNoCreatives: 'У цьому вікні не показувався жоден креатив. Підсумки нульові тому, що нічого не працювало, а не тому, що нічого не спрацювало.',
    emptyFiltered: 'Жоден креатив не відповідає цим фільтрам. Підсумки вище, як і раніше, описують увесь зріз.',
    errUnknownAudience: 'Жодне оголошення у вибраних акаунтах не було націлене на цю аудиторію. Покажіть усі аудиторії.',
    clearAudience: 'Показати всі аудиторії',
    errMixedProvenance: 'У виборі змішано демо- та реальні рекламні акаунти, а їхні показники ніколи не додаються. Приберіть демоакаунти %a з вибору на панелі акаунтів вище.',
    errMixedCurrency: 'Вибрані рекламні акаунти звітують більш ніж в одній валюті. Один запит охоплює одну, тому приберіть %a з вибору на панелі акаунтів вище.',
    errUnknownAccount: 'У цієї компанії більше немає рекламного акаунта %a. Змініть вибір на панелі акаунтів вище; повтор запиту не допоможе.',
    errRequest: 'Запит відхилено: %c',
    widenTo: 'Розширити до %n дн.', widenTitle: 'За поточного темпу вікно %n дн. пройшло б поріг достовірності',
    sortName: 'Назва', sortAccounts: 'Акаунти', sortHypothesis: 'Ідея', sortTechnique: 'Візуал',
    sortHook: 'Hook', sortHold: 'Hold', sortCtr: 'CTR', sortCpa: 'CPA', sortAov: 'AOV',
    sortCpc: 'CPC', sortAds: 'Оголошень', sortVerdict: 'Висновок',
    sortShare: 'Частка витрат', sortCpm: 'CPM', sortActiveDays: 'Днів із показами', shareCol: 'Частка', activeDaysCol: 'Днів із показами',
    pausedSince: (date) => `на паузі з ${date}`,
    hintShare: 'Частка у витратах показаних креативів. За рядками на екрані в сумі 100%.',
    hintNameShare: 'Що таке частка?',
    rankableSub: '%c з %t', tableCaption: 'Креативи, за асетами', adsMerged: 'оголошень: %n',
    detailAria: 'Панель деталей креативу', detailClose: 'Закрити панель деталей',
    detailScroll: 'Деталі креативу, прокручувана область',
    ticksAria: 'Достовірність: %b, n=%n',
    ticksAriaUnknown: 'Достовірність оцінити не можна: кількість конверсій не повідомлено',
    detailMeta: '%k - %d - акаунтів: %n - оголошень: %a - n=%p',
    detailMetaStill: '%k - акаунтів: %n - оголошень: %a - n=%p',
    shareSub: 'від витрат сегмента, за останнім завантаженням списку',
    adsBlockTitle: 'Оголошення',
    adsBlockCaption: 'Усі оголошення креативу в цій вибірці, з картинкою, яка йшла під кожним',
    adStatus: 'Статус',
    adReason: 'Чому тут',
    reasonFounder: 'Перше оголошення цього файлу',
    reasonExactBytes: 'Той самий файл',
    reasonNearExact: 'Майже таке саме зображення',
    reasonConfirmedSuggestion: 'Підтверджена пропозиція',
    reasonMergedByHand: "Об'єднано вручну",
    reasonDetached: 'Відокремлено',
    splitCaption: 'Розбивка за акаунтами: разом рядки дають підсумкову цифру внизу',
    roasNote: 'ROAS - це вся вартість купівель, поділена на всі витрати: і в підсумковому рядку, і в кожному рядку акаунта.',
    identityNote: 'Група креативів %h - акаунтів: %n - назв оголошень: %a',
    notFoundTitle: 'Немає цифр по цьому креативу в цій вибірці',
    notFoundBody: 'Його оголошення поза поточними акаунтами чи аудиторією, або їх видалено. Повторіть запит або закрийте панель і виберіть інший креатив.',
    relPartial: 'Цифри за «%m» для цього креативу неповні або об’єднують дані, які не цілком порівнянні. Кожне відношення, що на них спирається, відкладено.',
    hypTitle: 'Порівняння ідей',
    hypLede: 'Ідея об’єднує всі креативи, які перевіряють одну й ту саму тему, тому сумарна вибірка проходить поріг, недосяжний для окремих креативів. Це основна поверхня порівняння, а не фільтр.',
    hypAxisNote: 'Ідея - ключ групування. Візуальний прийом і аудиторія - атрибути креативів усередині ідеї, тут їх подано як допоміжні розрізи.',
    hypDrill: 'Відкрийте рядок, щоб побачити креативи, які до нього входять: екран креативів звузиться до цієї групи.',
    hypSub: 'креативів: %c · n=%n %u',
    versionsLine: 'Версії: %v',
    hypOpen: 'Відкрити креативи цієї групи: %c',
    hypFoot: 'Групи успадковують правило креативів. %list; штрихування позначає решту - їхній ROAS вказує напрямок, не більше. За аудиторіями акаунт зводиться до %aud - один креатив може бути в кількох групах аудиторій, а його конверсії - в одній. %win%un',
    hypFootNoAud: 'Групи успадковують правило креативів. %list; штрихування позначає решту - їхній ROAS вказує напрямок, не більше. %win%un',
    footWindowLast: 'Вікно - останні %p дн.',
    footWindowRange: 'Вікно - %r.',
    groupHypothesis: 'Ідея', groupVisual: 'Візуальний прийом', groupAudience: 'Аудиторія',
    bucketOne: 'група', bucketFew: 'групи', bucketMany: 'груп',
    techUnassigned: 'візуал не призначено',
    msgUnassigned: 'повідомлення не призначено', offerUnassigned: 'офер не призначено',
    quoteOpen: '«', quoteClose: '»', andJoin: ' і ',
    footList: 'Поріг n=%n %u проходять %list',
    footNone: 'Поки жодна група не проходить поріг n=%n %u',
    unassignedFootHyp: ' Креативи без призначеної ідеї потрапляють до «%v».',
    unassignedFootVisual: ' Креативи без призначеного візуального прийому потрапляють до «%v».',
    jProposes: 'Grovus пропонує',
    jBasis: 'CPA %c проти середнього по акаунту %b, %n %u',
    jWaiting: 'Чекає на дані - ще %n %u',
    jProvNote: 'попередня вибірка',
    jvConfirmed: 'Підтверджено', jvRefuted: 'Спростовано',
    frStepAccount: 'Ваш акаунт', frStepCompany: 'Ваша перша компанія', frStepConnect: 'Підключіть рекламний акаунт',
    frStepsLabel: 'Кроки першого запуску',
    frAccountLabel: 'Ваше ім’я', frCompanyLabel: 'Назва компанії',
    frCompanyPlaceholder: 'Бренд, для якого ви запускаєте рекламу.',
    frBack: 'Назад', frNext: 'Далі', frSkip: 'Поки пропустити', frFinish: 'Готово',
    frConnectMeta: 'Підключити Meta Ads', notConnectedYet: 'Поки не підключено',
    accStateWaiting: 'Підключено. Чекаємо на перші дані.', accStateConnected: 'Підключено',
    accStateExpiring: 'Доступ спливає через %n дн.',
    accStateExpired: 'Доступ сплив. Grovus його оновлює.', accStateRevoked: 'Доступ відкликано',
    accStateRejected: 'Доступ відхилено. Grovus з’ясовує причину.',
    accStateWithdrawn: 'Більше не відкрито для Grovus',
    accConnectPlatform: 'Підключити рекламну платформу',
    accConnectNeed: 'Grovus потрібен доступ адміністратора до вашого бізнес-портфоліо Meta. В інструкції кожен крок показано на скриншоті.',
    accConnectThen: 'Після цього ваші рекламні акаунти з’являться тут, у розділі «%shared». Підключіть кожен.',
    accConnectGuide: 'Відкрити інструкцію',
    accShared: 'Відкрито для Grovus', accConnect: 'Підключити', accRemove: 'Видалити',
    accRemoveConfirm: 'Видалити демоакаунт і його показники? Цю дію не можна скасувати.', accRemoveKeep: 'Залишити',
    accDemoCompanyHint: 'Це демокомпанія. Підключайте свої акаунти в справжній компанії.',
    accDemoPrompt: 'Ви створюєте перший справжній акаунт. Видалити демоакаунт?',
    accDemoPromptDelete: 'Видалити', accDemoPromptKeep: 'Залишити',
    accNotAvailable: 'Цей акаунт більше недоступний', accAlreadyRemoved: 'Уже видалено',
    accUnreachable: 'Не вдалося зв’язатися з Grovus. Список оновлено.',
    accRefreshFailed: 'Зміну надіслано, але список акаунтів не вдалося оновити.',
    accSharedFailed: 'Не вдалося завантажити акаунти, відкриті для Grovus.',
    demoMark: 'Демо', demoMarkLong: 'Демоакаунт',
    demoNotice: 'У цій компанії є демоакаунти. Демопоказники позначено, і вони враховуються окремо.',
    unpulledNotice: 'Метрики за цими акаунтами ще не завантажено.',
    scopeUnplaced: 'Деякі оголошення ще не вдалося визначити, тому дані тут неповні.',
    scopeUncovered: 'Деякі дні ще не завантажено, тому дані тут неповні.',
    scopeExcluded: 'Оголошення, які не вдалося визначити, виключено, тому дані охоплюють лише визначені оголошення.',
    scopeExcludedCount: 'Виключено оголошень: %c у цьому періоді.',
    scopeExcludedCompared: 'Виключено оголошень: %c у цьому періоді, %p у попередньому.',
    scopeReason: '%r: %c {c:оголошення|оголошення|оголошень}',
    scopeReasonExcluded: '%r, виключено: %c {c:оголошення|оголошення|оголошень}',
    unplacedReasons: {
      pending: 'Ще не оброблено',
      unrecognised_shape: 'Нерозпізнаний формат креативу',
      no_creative: 'В оголошенні немає креативу',
      creative_unreadable: 'Не вдалося прочитати креатив',
      fetch_failed: 'Не вдалося отримати креатив',
      no_thumbnail: 'Немає зображення чи прев’ю',
      refused_host: 'Хост зображення не дозволено',
      download_failed: 'Не вдалося завантажити зображення',
      hash_failed: 'Не вдалося обробити зображення',
      deferred: 'Очікує наступного проходу',
      placeholder_image: 'Meta віддає заглушку, зображення немає',
    },
    detailAttributionAcross: 'Конверсії об’єднують два різні налаштування атрибуції з різних акаунтів.',
    frDemoHint: 'Демодані розміщено в компанії «Демо». Ваша компанія порожня, доки ви не підключите акаунт.',
    frDuplicateCheck: 'Можливо, цю компанію вже створено. Поки не вдалося перевірити ваші компанії.',
    frReservedName: 'Цю назву зайнято демокомпанією. Виберіть іншу назву.',
    jBasisDemo: 'CPA %c проти середнього по демоакаунту %b, %n %u',
    platMeta: 'Meta', platTikTok: 'TikTok', platGoogle: 'Google',
    emptyTitle: 'Рекламний акаунт ще не підключено',
    emptyBody: 'У «%c» немає підключеного рекламного акаунта. Підключіть його, щоб бачити ефективність креативів тут.',
    clientPanelLabel: 'Компанії',
    clientLevel: 'Компанія', clientNone: 'Рекламні акаунти не підключено', clientUnnamed: 'Компанія без назви',
    clientAdd: 'Додати компанію', clientSwitchNote: 'Перемикання компанії не потребує повторного входу.',
    clientAddName: 'Назва компанії',
    clientAddDuplicate: 'У вас уже є компанія з такою назвою.',
    clientAddCreate: 'Створити', clientAddCancel: 'Скасувати', clientAddPending: 'Додаємо…',
    clientAddElsewhere: 'Додається в іншому вікні.',
    clientAddUnknown: 'Не вдалося підтвердити, чи створено компанію. Повторна спроба не створить її двічі.',
    clientAddUnknownOld: 'Додавання компанії зараз недоступне, спробуйте пізніше.',
    clientAddUnavailable: 'Додавання компанії зараз недоступне.',
    clientAddNotListed: 'Компанію створено, але список не вдалося оновити.',
    clientAddRefreshFailed: 'Не вдалося оновити список компаній.',
    clientAddCheckAgain: 'Перевірити знову', clientAddAlreadyCreated: 'Компанію вже було створено.',
    clientAddCancelled: 'Цю спробу скасовано.',
    clientAddKeepOpen: 'Не закривайте сторінку, доки це не вирішиться.',
    clientAddCheckFailed: 'Не вдалося перевірити цю спробу, тому нічого не надіслано. Повторіть трохи пізніше.',
    clientAddCheckFailedCreated: 'Компанію створено, але цю спробу поки не вдалося перевірити. Перевірте знову, щоб відкрити її.',
    clientAddInventoryFailed: 'Не вдалося прочитати збережені спроби, тому додавання компанії призупинено, щоб не створити її двічі.',
    clientAddAnyway: 'Усе одно додати',
    clientAddInventoryRisk: 'Браузер не дозволяє сторінці прочитати збережене. Якщо тут було збережено попередню спробу, нове додавання може створити цю компанію двічі.',
    colChooser: 'Стовпці', colAlwaysShown: 'завжди видно', colShowAll: 'Показати всі стовпці', colSorted: 'сортування',
    exportCsv: 'Експорт CSV', exportCsvShort: 'CSV', exportXlsx: 'Експорт XLSX', xlsxSheetName: 'Креативи',
    exportPending: 'Експорт стане доступним, коли таблиця завантажиться для поточного вибору',
    accLastOne: 'Хоча б один акаунт залишається вибраним. Зняти всі можна в контролі акаунтів вище.',
    accSelectAll: 'Вибрати всі акаунти',
    csvDataDelayed: 'дані запізнюються', csvCreativeId: 'ID креативу',
  },
}

/** The company activity log (GRO-108, screen 10 of the user management design). */
export interface ActivityCopy {
  title: string; filter: string
  filterAll: string; filterInvitations: string; filterRoles: string; filterRemovals: string; filterSharing: string
  time: string; who: string; what: string; whom: string
  more: string; loading: string; empty: string; noMatch: string
  loadFailed: string; moreFailed: string; retry: string; newer: string
  roles: Record<'owner' | 'admin' | 'member' | 'viewer', string>
  grovus: string
  invited: string; resent: string; revoked: string
  autoRoleChanged: string; autoRemoved: string; autoLeft: string; autoRecovery: string; autoOther: string
  accepted: string; declined: string; roleChanged: string; removed: string; left: string
  /** GRO-83. `%label` is ` (<label>)` or empty. */
  linkCreated: string; linkRevoked: string
  linkAutoRoleChanged: string; linkAutoRemoved: string; linkAutoLeft: string; linkAutoOperator: string; linkAutoOther: string
  /** GRO-136: a platform admin renamed the company. */
  companyRenamed: string
  /** GRO-188: «История компании» on the company Settings screen. */
  companyTitle: string; companyEmpty: string
  companyCreated: string; deletionRequested: string; companyRestored: string
}

export const ACTIVITY_COPY: Record<Lang, ActivityCopy> = {
  en: {
    title: 'Activity log', filter: 'Event type',
    filterAll: 'All events', filterInvitations: 'Invitations', filterRoles: 'Roles', filterRemovals: 'Removals', filterSharing: 'Sharing',
    time: 'Time', who: 'Who', what: 'What happened', whom: 'To whom',
    more: 'Show more', loading: 'Loading the activity log...',
    empty: 'Nothing has happened yet. Invitations, role changes and removals appear here.',
    noMatch: 'No matching events.',
    loadFailed: 'The activity log could not be loaded.',
    moreFailed: 'Older events could not be loaded.',
    retry: 'Try again',
    newer: 'Some events need a newer version of Grovus to be shown.',
    roles: { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' },
    grovus: 'Grovus',
    invited: 'Invited as %role',
    resent: 'Resent the invitation',
    revoked: 'Revoked the invitation',
    autoRoleChanged: 'The invitation was revoked automatically, because %inviter, who sent it, was changed from %from to %to',
    autoRemoved: 'The invitation was revoked automatically, because %inviter, who sent it, was removed from the client',
    autoLeft: 'The invitation was revoked automatically, because %inviter, who sent it, left the client',
    autoRecovery: 'The invitation was revoked automatically during a database recovery',
    autoOther: 'The invitation was revoked automatically',
    accepted: 'Accepted the invitation as %role',
    declined: 'Declined the invitation',
    roleChanged: 'Changed role from %from to %to',
    removed: 'Removed from the client',
    left: 'Left',
    linkCreated: 'Created a share link%label, expires %date',
    linkRevoked: 'Revoked a share link%label',
    linkAutoRoleChanged: 'A share link%label was revoked when its creator was changed from %from to %to',
    linkAutoRemoved: 'A share link%label was revoked when its creator was removed from the client',
    linkAutoLeft: 'A share link%label was revoked when its creator left the client',
    linkAutoOperator: 'A share link%label was revoked by an operator',
    linkAutoOther: 'A share link%label was revoked automatically',
    companyRenamed: 'Renamed the company from "%from" to "%to"',
    companyTitle: 'Company history',
    companyEmpty: 'Nothing has happened yet. The creation of the company, renames, deletion requests and restores appear here.',
    companyCreated: 'Created the company with an Owner',
    deletionRequested: 'Requested deletion of the company',
    companyRestored: 'Restored the company',
  },
  ru: {
    title: 'Журнал действий', filter: 'Тип события',
    filterAll: 'Все события', filterInvitations: 'Приглашения', filterRoles: 'Роли', filterRemovals: 'Удаления', filterSharing: 'Доступ по ссылке',
    time: 'Время', who: 'Кто', what: 'Что произошло', whom: 'Кому',
    more: 'Показать ещё', loading: 'Загружаем журнал действий...',
    empty: 'Пока ничего не происходило. Здесь появятся приглашения, смены ролей и удаления.',
    noMatch: 'Нет подходящих событий.',
    loadFailed: 'Не удалось загрузить журнал действий.',
    moreFailed: 'Не удалось загрузить более ранние события.',
    retry: 'Повторить',
    newer: 'Некоторые события можно показать только в более новой версии Grovus.',
    roles: { owner: 'Владелец', admin: 'Администратор', member: 'Участник', viewer: 'Наблюдатель' },
    grovus: 'Grovus',
    invited: 'Пригласил(а) с ролью «%role»',
    resent: 'Отправил(а) приглашение заново',
    revoked: 'Отозвал(а) приглашение',
    autoRoleChanged: 'Приглашение отозвано автоматически, потому что %inviter, который его отправил, переведён с роли «%from» на «%to»',
    autoRemoved: 'Приглашение отозвано автоматически, потому что %inviter, который его отправил, удалён из компании',
    autoLeft: 'Приглашение отозвано автоматически, потому что %inviter, который его отправил, покинул компанию',
    autoRecovery: 'Приглашение отозвано автоматически при восстановлении базы данных',
    autoOther: 'Приглашение отозвано автоматически',
    accepted: 'Принял(а) приглашение с ролью «%role»',
    declined: 'Отклонил(а) приглашение',
    roleChanged: 'Изменил(а) роль с «%from» на «%to»',
    removed: 'Удалил(а) из компании',
    left: 'Покинул(а) компанию',
    linkCreated: 'Создал(а) ссылку для просмотра%label, истекает %date',
    linkRevoked: 'Отозвал(а) ссылку для просмотра%label',
    linkAutoRoleChanged: 'Ссылка для просмотра%label отозвана, когда её автора перевели с роли «%from» на «%to»',
    linkAutoRemoved: 'Ссылка для просмотра%label отозвана, когда её автора удалили из компании',
    linkAutoLeft: 'Ссылка для просмотра%label отозвана, когда её автор покинул компанию',
    linkAutoOperator: 'Ссылка для просмотра%label отозвана оператором',
    linkAutoOther: 'Ссылка для просмотра%label отозвана автоматически',
    companyRenamed: 'Переименовал(а) компанию с «%from» на «%to»',
    companyTitle: 'История компании',
    companyEmpty: 'Пока ничего не происходило. Здесь появятся создание компании, переименования, запросы на удаление и восстановления.',
    companyCreated: 'Создал(а) компанию с владельцем',
    deletionRequested: 'Запросил(а) удаление компании',
    companyRestored: 'Восстановил(а) компанию',
  },
  uk: {
    title: 'Журнал дій', filter: 'Тип події',
    filterAll: 'Усі події', filterInvitations: 'Запрошення', filterRoles: 'Ролі', filterRemovals: 'Видалення', filterSharing: 'Доступ за посиланням',
    time: 'Час', who: 'Хто', what: 'Що сталося', whom: 'Кому',
    more: 'Показати ще', loading: 'Завантажуємо журнал дій...',
    empty: 'Поки нічого не відбувалося. Тут з’являться запрошення, зміни ролей і видалення.',
    noMatch: 'Немає відповідних подій.',
    loadFailed: 'Не вдалося завантажити журнал дій.',
    moreFailed: 'Не вдалося завантажити давніші події.',
    retry: 'Повторити',
    newer: 'Деякі події можна показати лише в новішій версії Grovus.',
    roles: { owner: 'Власник', admin: 'Адміністратор', member: 'Учасник', viewer: 'Спостерігач' },
    grovus: 'Grovus',
    invited: 'Запросив(ла) з роллю «%role»',
    resent: 'Надіслав(ла) запрошення повторно',
    revoked: 'Відкликав(ла) запрошення',
    autoRoleChanged: 'Запрошення відкликано автоматично, тому що %inviter, який його надіслав, переведений з ролі «%from» на «%to»',
    autoRemoved: 'Запрошення відкликано автоматично, тому що %inviter, який його надіслав, видалений з компанії',
    autoLeft: 'Запрошення відкликано автоматично, тому що %inviter, який його надіслав, залишив компанію',
    autoRecovery: 'Запрошення відкликано автоматично під час відновлення бази даних',
    autoOther: 'Запрошення відкликано автоматично',
    accepted: 'Прийняв(ла) запрошення з роллю «%role»',
    declined: 'Відхилив(ла) запрошення',
    roleChanged: 'Змінив(ла) роль з «%from» на «%to»',
    removed: 'Видалив(ла) з компанії',
    left: 'Залишив(ла) компанію',
    linkCreated: 'Створив(ла) посилання для перегляду%label, спливає %date',
    linkRevoked: 'Відкликав(ла) посилання для перегляду%label',
    linkAutoRoleChanged: 'Посилання для перегляду%label відкликано, коли його автора перевели з ролі «%from» на «%to»',
    linkAutoRemoved: 'Посилання для перегляду%label відкликано, коли його автора видалили з компанії',
    linkAutoLeft: 'Посилання для перегляду%label відкликано, коли його автор залишив компанію',
    linkAutoOperator: 'Посилання для перегляду%label відкликано оператором',
    linkAutoOther: 'Посилання для перегляду%label відкликано автоматично',
    companyRenamed: 'Перейменував(ла) компанію з «%from» на «%to»',
    companyTitle: 'Історія компанії',
    companyEmpty: 'Поки нічого не відбувалося. Тут з’являться створення компанії, перейменування, запити на видалення і відновлення.',
    companyCreated: 'Створив(ла) компанію з власником',
    deletionRequested: 'Запросив(ла) видалення компанії',
    companyRestored: 'Відновив(ла) компанію',
  },
}

/** Members, invitations and No client access (GRO-106, the user management design). */
export interface MembersCopy {
  roles: Record<'owner' | 'admin' | 'member' | 'viewer', string>
  tagline: string
  noAccess: string; ledeInvites: string; ledeNone: string; removed: string
  create: string; request: string; ask: string
  signedIn: string; signOut: string
  joined: string; switchTo: string; lost: string; dismiss: string
  members: string; invite: string; readOnly: string
  email: string; role: string; joinedCol: string; joinedOn: string; actions: string
  you: string; remove: string; leave: string; ownerNote: string
  loading: string; loadFailed: string; retry: string
  pending: string; invitedBy: string; expiresIn: string; expired: string; newLink: string; revoke: string
  send: string; cancel: string; done: string; adminHint: string
  created: string; linkNote: string; copyLink: string; linkCopied: string; linkLabel: string
  confirmAdmin: string; confirmOwner: string; confirmRole: string
  confirmRemove: string; confirmLeave: string; lastOwner: string; confirmRevoke: string
  changeRoleOk: string; removeOk: string; leaveOk: string; close: string
  invalidEmail: string; alreadyMember: string; alreadyInvited: string; forbidden: string; notOpen: string
  uncertain: string; goneMember: string; goneInvite: string; failed: string
  deleteCompany: string; confirmDelete: string; confirmDeleteHold: string; deleteDemoCompany: string; confirmDeleteDemo: string; deleteOk: string; deleteUnknown: string; deletedRefreshFailed: string
  invitations: string; invitationsLoading: string; accept: string; decline: string
  joinedNotLoaded: string; notConfirmed: string
  invExpired: string; invRevoked: string; invUsed: string
  linkChecking: string; linkInvited: string; linkInvitedLede: string; linkSignIn: string
  linkOther: string; linkOtherLede: string; linkUseAnother: string
  linkExpiredLede: string; linkRevokedLede: string; linkUnknownLede: string
  linkMember: string; open: string; goToGrovus: string
  roleLine: string; waiting: string; google: string; leaveWhy: string; connectWhy: string
}

export const MEMBERS_COPY: Record<Lang, MembersCopy> = {
  en: {
    roles: { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' },
    tagline: 'Creative intelligence',
    noAccess: 'No client access',
    ledeInvites: 'You have pending invitations. Accept one to start.',
    ledeNone: 'You have no pending invitations.',
    removed: 'You no longer have access to %name.',
    create: 'Create a client',
    request: 'Request permission to create clients',
    ask: 'Ask the person who invited you to send a new invitation, or ask Grovus to let you create clients.',
    signedIn: 'Signed in as', signOut: 'Sign out',
    joined: 'You joined %name as %role.',
    switchTo: 'Switch to %name',
    lost: 'You no longer have access to %lost. You are now in %next.',
    dismiss: 'Dismiss',
    members: 'Members', invite: 'Invite people',
    readOnly: 'Owners and Admins invite people and manage roles.',
    email: 'Email', role: 'Role', joinedCol: 'Joined', joinedOn: 'Joined %date', actions: 'Actions',
    you: 'You', remove: 'Remove from client', leave: 'Leave client',
    ownerNote: 'Only an Owner can change an Owner',
    loading: 'Loading members...', loadFailed: 'The member list could not be loaded.', retry: 'Try again',
    pending: 'Pending invitations', invitedBy: 'Invited by %email',
    expiresIn: 'Expires in %n {n:day|days}', expired: 'Expired', newLink: 'New link', revoke: 'Revoke',
    send: 'Invite', cancel: 'Cancel', done: 'Done',
    adminHint: 'Admins can invite up to Admin.',
    created: 'Invitation for %email as %role. It expires in 7 days.',
    linkNote: 'Send this link in any chat. They can also simply sign in with this email, and the invitation will be waiting.',
    copyLink: 'Copy link', linkCopied: 'Link copied', linkLabel: 'Invitation link',
    confirmAdmin: 'Make %email an Admin? Admins can invite people and manage non-Owners.',
    confirmOwner: 'Make %email an Owner? Owners have every permission, including removing you.',
    confirmRole: 'Make %email a %role?',
    confirmRemove: 'Remove %email from %client? They lose access on their next request. What they made stays with the client. Their other clients are not affected.',
    confirmLeave: 'Leave %client? You will need a new invitation to come back.',
    lastOwner: '%client needs at least one Owner. Make someone else Owner first.',
    confirmRevoke: 'Revoke the invitation for %email? The link stops working.',
    changeRoleOk: 'Change role', removeOk: 'Remove', leaveOk: 'Leave', close: 'Close',
    invalidEmail: 'Enter a valid email address.',
    alreadyMember: '%email is already a member of this client.',
    alreadyInvited: '%email already has a pending invitation. New link issues a replacement link.',
    forbidden: 'Your role no longer allows this.',
    notOpen: 'Invitations are not open on this server yet.',
    uncertain: 'The link may have been created but could not be shown. New link replaces it.',
    goneMember: '%email is no longer a member of this client.',
    goneInvite: 'This invitation is no longer pending.',
    failed: 'This could not be confirmed. Try again.',
    deleteCompany: 'Delete client',
    confirmDelete: 'Delete %client for everyone? Its accounts, figures and every member\'s access are removed from Grovus. This cannot be undone.',
    confirmDeleteHold: 'Delete %client for everyone? It disappears for every member at once and stays frozen for at least 30 days. A Grovus admin can restore it in that time. After 30 days it becomes eligible for removal, and it is removed for good when the scheduled purge runs.',
    deleteOk: 'Delete',
    deleteUnknown: 'The delete could not be confirmed. Try again to check.',
    deletedRefreshFailed: '%client was deleted, but your clients could not be loaded.',
    deleteDemoCompany: 'Delete demo company',
    confirmDeleteDemo: 'Delete the demo company? Its sample figures are removed. You can connect real accounts in your own companies.',
    invitations: 'Invitations', invitationsLoading: 'Loading invitations...', accept: 'Accept', decline: 'Decline',
    joinedNotLoaded: 'You joined %name. It could not be loaded yet.',
    notConfirmed: 'Could not confirm. Try again.',
    invExpired: 'This invitation has expired.', invRevoked: 'This invitation was revoked.', invUsed: 'This invitation was already used.',
    linkChecking: 'Checking the invitation...',
    linkInvited: 'You are invited to %client',
    linkInvitedLede: '%inviter invited %email as %role.',
    linkSignIn: 'Sign in with Google to accept',
    linkOther: 'This invitation is for %email.',
    linkOtherLede: 'You are signed in as %email.',
    linkUseAnother: 'Sign out and use another account',
    linkExpiredLede: 'Invitations last 7 days. Ask %inviter to resend it.',
    linkRevokedLede: 'Ask someone at %client for a new invitation.',
    linkUnknownLede: 'Ask the person who invited you for a new link.',
    linkMember: 'You are already a member of %client.',
    open: 'Open %client', goToGrovus: 'Go to Grovus',
    roleLine: '%role · %n {n:ad account|ad accounts}',
    waiting: '%n {n:invitation waiting|invitations waiting}',
    google: 'Signed in with Google',
    leaveWhy: 'You are the only Owner. Make someone else Owner first.',
    connectWhy: 'Only Owners and Admins connect ad accounts.',
  },
  ru: {
    roles: { owner: 'Владелец', admin: 'Администратор', member: 'Участник', viewer: 'Наблюдатель' },
    tagline: 'Creative intelligence',
    noAccess: 'Нет доступа к компаниям',
    ledeInvites: 'У вас есть ожидающие приглашения. Примите одно, чтобы начать.',
    ledeNone: 'У вас нет ожидающих приглашений.',
    removed: 'У вас больше нет доступа к %name.',
    create: 'Создать компанию',
    request: 'Запросить право создавать компании',
    ask: 'Попросите пригласившего вас человека прислать новое приглашение или попросите Grovus разрешить вам создавать компании.',
    signedIn: 'Вы вошли как', signOut: 'Выйти',
    joined: 'Вы присоединились к %name с ролью «%role».',
    switchTo: 'Перейти в %name',
    lost: 'У вас больше нет доступа к %lost. Сейчас вы в %next.',
    dismiss: 'Закрыть',
    members: 'Участники', invite: 'Пригласить людей',
    readOnly: 'Владельцы и администраторы приглашают людей и управляют ролями.',
    email: 'Эл. почта', role: 'Роль', joinedCol: 'В компании с', joinedOn: 'В компании с %date', actions: 'Действия',
    you: 'Вы', remove: 'Удалить из компании', leave: 'Покинуть компанию',
    ownerNote: 'Только владелец может изменять владельца',
    loading: 'Загружаем участников...', loadFailed: 'Не удалось загрузить список участников.', retry: 'Повторить',
    pending: 'Ожидающие приглашения', invitedBy: 'Пригласил(а) %email',
    expiresIn: 'Истекает через %n {n:день|дня|дней}', expired: 'Срок истёк', newLink: 'Новая ссылка', revoke: 'Отозвать',
    send: 'Пригласить', cancel: 'Отмена', done: 'Готово',
    adminHint: 'Администраторы приглашают не выше роли «Администратор».',
    created: 'Приглашение для %email с ролью «%role». Срок действия: 7 дней.',
    linkNote: 'Отправьте эту ссылку в любом чате. Человек также может просто войти с этой почтой, и приглашение будет ждать его.',
    copyLink: 'Скопировать ссылку', linkCopied: 'Ссылка скопирована', linkLabel: 'Ссылка-приглашение',
    confirmAdmin: 'Сделать %email администратором? Администраторы приглашают людей и управляют всеми, кроме владельцев.',
    confirmOwner: 'Сделать %email владельцем? Владельцы имеют все права, включая право удалить вас.',
    confirmRole: 'Назначить %email роль «%role»?',
    confirmRemove: 'Удалить %email из %client? Доступ пропадёт при следующем запросе. Всё, что создал этот человек, останется у компании. Другие компании этого человека не затрагиваются.',
    confirmLeave: 'Покинуть %client? Чтобы вернуться, понадобится новое приглашение.',
    lastOwner: '%client нужен хотя бы один владелец. Сначала назначьте владельцем другого человека.',
    confirmRevoke: 'Отозвать приглашение для %email? Ссылка перестанет работать.',
    changeRoleOk: 'Изменить роль', removeOk: 'Удалить', leaveOk: 'Покинуть', close: 'Закрыть',
    invalidEmail: 'Введите корректный адрес эл. почты.',
    alreadyMember: '%email уже участник этой компании.',
    alreadyInvited: 'У %email уже есть ожидающее приглашение. «Новая ссылка» выпустит ссылку взамен прежней.',
    forbidden: 'Ваша роль больше не позволяет это сделать.',
    notOpen: 'Приглашения на этом сервере пока не открыты.',
    uncertain: 'Ссылка, возможно, создана, но её не удалось показать. «Новая ссылка» заменит её.',
    goneMember: '%email больше не участник этой компании.',
    goneInvite: 'Это приглашение больше не ожидает ответа.',
    failed: 'Не удалось это подтвердить. Повторите попытку.',
    deleteCompany: 'Удалить компанию',
    confirmDelete: 'Удалить %client для всех? Её аккаунты, показатели и доступ всех участников будут удалены из Grovus. Это действие нельзя отменить.',
    confirmDeleteHold: 'Удалить %client для всех? Компания сразу исчезнет у всех участников и будет заморожена как минимум на 30 дней. В это время администратор Grovus может её восстановить. Через 30 дней она станет доступна для удаления и будет удалена окончательно при следующей плановой очистке.',
    deleteOk: 'Удалить',
    deleteUnknown: 'Не удалось подтвердить удаление. Повторите, чтобы проверить.',
    deletedRefreshFailed: '%client удалена, но не удалось загрузить ваши компании.',
    deleteDemoCompany: 'Удалить демокомпанию',
    confirmDeleteDemo: 'Удалить демокомпанию? Её демоданные будут удалены. Настоящие аккаунты можно подключить в ваших компаниях.',
    invitations: 'Приглашения', invitationsLoading: 'Загружаем приглашения...', accept: 'Принять', decline: 'Отклонить',
    joinedNotLoaded: 'Вы присоединились к %name, но её пока не удалось загрузить.',
    notConfirmed: 'Не удалось подтвердить. Повторите попытку.',
    invExpired: 'Срок приглашения истёк.', invRevoked: 'Приглашение отозвано.', invUsed: 'Это приглашение уже использовано.',
    linkChecking: 'Проверяем приглашение...',
    linkInvited: 'Вас пригласили в %client',
    linkInvitedLede: '%inviter пригласил(а) %email с ролью «%role».',
    linkSignIn: 'Войти через Google, чтобы принять',
    linkOther: 'Это приглашение для %email.',
    linkOtherLede: 'Вы вошли как %email.',
    linkUseAnother: 'Выйти и войти с другим аккаунтом',
    linkExpiredLede: 'Приглашения действуют 7 дней. Попросите %inviter отправить его заново.',
    linkRevokedLede: 'Попросите кого-нибудь из %client прислать новое приглашение.',
    linkUnknownLede: 'Попросите пригласившего вас человека прислать новую ссылку.',
    linkMember: 'Вы уже участник %client.',
    open: 'Открыть %client', goToGrovus: 'Перейти в Grovus',
    roleLine: '%role · %n {n:рекламный аккаунт|рекламных аккаунта|рекламных аккаунтов}',
    waiting: '%n {n:приглашение ожидает|приглашения ожидают|приглашений ожидают}',
    google: 'Вход через Google',
    leaveWhy: 'Вы единственный владелец. Сначала назначьте владельцем другого человека.',
    connectWhy: 'Подключать рекламные аккаунты могут только владельцы и администраторы.',
  },
  uk: {
    roles: { owner: 'Власник', admin: 'Адміністратор', member: 'Учасник', viewer: 'Спостерігач' },
    tagline: 'Creative intelligence',
    noAccess: 'Немає доступу до компаній',
    ledeInvites: 'У вас є запрошення, що очікують. Прийміть одне, щоб почати.',
    ledeNone: 'У вас немає запрошень, що очікують.',
    removed: 'У вас більше немає доступу до %name.',
    create: 'Створити компанію',
    request: 'Запит на право створювати компанії',
    ask: 'Попросіть людину, яка вас запросила, надіслати нове запрошення, або попросіть Grovus дозволити вам створювати компанії.',
    signedIn: 'Ви увійшли як', signOut: 'Вийти',
    joined: 'Ви приєдналися до %name з роллю «%role».',
    switchTo: 'Перейти до %name',
    lost: 'У вас більше немає доступу до %lost. Зараз ви в %next.',
    dismiss: 'Закрити',
    members: 'Учасники', invite: 'Запросити людей',
    readOnly: 'Власники та адміністратори запрошують людей і керують ролями.',
    email: 'Ел. пошта', role: 'Роль', joinedCol: 'У компанії з', joinedOn: 'У компанії з %date', actions: 'Дії',
    you: 'Ви', remove: 'Видалити з компанії', leave: 'Залишити компанію',
    ownerNote: 'Лише власник може змінювати власника',
    loading: 'Завантажуємо учасників...', loadFailed: 'Не вдалося завантажити список учасників.', retry: 'Повторити',
    pending: 'Запрошення, що очікують', invitedBy: 'Запросив(ла) %email',
    expiresIn: 'Спливає через %n {n:день|дні|днів}', expired: 'Термін сплив', newLink: 'Нове посилання', revoke: 'Відкликати',
    send: 'Запросити', cancel: 'Скасувати', done: 'Готово',
    adminHint: 'Адміністратори запрошують не вище за роль «Адміністратор».',
    created: 'Запрошення для %email з роллю «%role». Термін дії: 7 днів.',
    linkNote: 'Надішліть це посилання в будь-якому чаті. Людина також може просто увійти з цією поштою, і запрошення чекатиме на неї.',
    copyLink: 'Скопіювати посилання', linkCopied: 'Посилання скопійовано', linkLabel: 'Посилання-запрошення',
    confirmAdmin: 'Зробити %email адміністратором? Адміністратори запрошують людей і керують усіма, крім власників.',
    confirmOwner: 'Зробити %email власником? Власники мають усі права, зокрема право видалити вас.',
    confirmRole: 'Призначити %email роль «%role»?',
    confirmRemove: 'Видалити %email з %client? Доступ зникне під час наступного запиту. Усе, що створила ця людина, залишиться в компанії. Інші компанії цієї людини не зачіпаються.',
    confirmLeave: 'Залишити %client? Щоб повернутися, знадобиться нове запрошення.',
    lastOwner: '%client потрібен хоча б один власник. Спершу призначте власником іншу людину.',
    confirmRevoke: 'Відкликати запрошення для %email? Посилання перестане працювати.',
    changeRoleOk: 'Змінити роль', removeOk: 'Видалити', leaveOk: 'Залишити', close: 'Закрити',
    invalidEmail: 'Введіть коректну адресу ел. пошти.',
    alreadyMember: '%email уже учасник цієї компанії.',
    alreadyInvited: '%email уже має запрошення, що очікує. «Нове посилання» випустить посилання замість попереднього.',
    forbidden: 'Ваша роль більше не дозволяє цього.',
    notOpen: 'Запрошення на цьому сервері поки не відкрито.',
    uncertain: 'Посилання, можливо, створено, але його не вдалося показати. «Нове посилання» замінить його.',
    goneMember: '%email більше не учасник цієї компанії.',
    goneInvite: 'Це запрошення більше не очікує відповіді.',
    failed: 'Не вдалося це підтвердити. Повторіть спробу.',
    deleteCompany: 'Видалити компанію',
    confirmDelete: 'Видалити %client для всіх? Її акаунти, показники й доступ усіх учасників буде видалено з Grovus. Цю дію не можна скасувати.',
    confirmDeleteHold: 'Видалити %client для всіх? Компанія одразу зникне в усіх учасників і буде заморожена щонайменше на 30 днів. У цей час адміністратор Grovus може її відновити. Через 30 днів вона стане доступною для видалення й буде видалена остаточно під час наступного планового очищення.',
    deleteOk: 'Видалити',
    deleteUnknown: 'Не вдалося підтвердити видалення. Повторіть, щоб перевірити.',
    deletedRefreshFailed: '%client видалено, але не вдалося завантажити ваші компанії.',
    deleteDemoCompany: 'Видалити демокомпанію',
    confirmDeleteDemo: 'Видалити демокомпанію? Її демодані буде видалено. Справжні акаунти можна підключити у ваших компаніях.',
    invitations: 'Запрошення', invitationsLoading: 'Завантажуємо запрошення...', accept: 'Прийняти', decline: 'Відхилити',
    joinedNotLoaded: 'Ви приєдналися до %name, але її поки не вдалося завантажити.',
    notConfirmed: 'Не вдалося підтвердити. Повторіть спробу.',
    invExpired: 'Термін запрошення сплив.', invRevoked: 'Запрошення відкликано.', invUsed: 'Це запрошення вже використано.',
    linkChecking: 'Перевіряємо запрошення...',
    linkInvited: 'Вас запросили до %client',
    linkInvitedLede: '%inviter запросив(ла) %email з роллю «%role».',
    linkSignIn: 'Увійти через Google, щоб прийняти',
    linkOther: 'Це запрошення для %email.',
    linkOtherLede: 'Ви увійшли як %email.',
    linkUseAnother: 'Вийти й увійти з іншим акаунтом',
    linkExpiredLede: 'Запрошення діють 7 днів. Попросіть %inviter надіслати його повторно.',
    linkRevokedLede: 'Попросіть когось із %client надіслати нове запрошення.',
    linkUnknownLede: 'Попросіть людину, яка вас запросила, надіслати нове посилання.',
    linkMember: 'Ви вже учасник %client.',
    open: 'Відкрити %client', goToGrovus: 'Перейти до Grovus',
    roleLine: '%role · %n {n:рекламний акаунт|рекламні акаунти|рекламних акаунтів}',
    waiting: '%n {n:запрошення очікує|запрошення очікують|запрошень очікують}',
    google: 'Вхід через Google',
    leaveWhy: 'Ви єдиний власник. Спершу призначте власником іншу людину.',
    connectWhy: 'Підключати рекламні акаунти можуть лише власники та адміністратори.',
  },
}

/** GRO-83: the share dialog, the `/share` page, the ended-guest page and the guest header. */
export interface ShareCopy {
  access: string; share: string; shareWhy: string
  lede: string; expiry: string; days: Record<1 | 7 | 30, string>
  label: string; labelHint: string; create: string
  linkLabel: string; shownOnce: string; copy: string; copied: string
  live: string; none: string; noLabel: string
  createdBy: string; expires: string; lastOpened: string; notOpened: string
  revoke: string; confirmRevoke: string; cancel: string; close: string
  notOpen: string; invalidLabel: string; failed: string
  loading: string; loadFailed: string; retry: string
  opening: string; ended: string; openFailed: string; signedIn: string
  goToGrovus: string; signIn: string
  guestLine: string; leave: string; recheckFailed: string
}

export const SHARE_COPY: Record<Lang, ShareCopy> = {
  en: {
    access: 'Access', share: 'Share read-only', shareWhy: 'Only Owners and Admins can share this client',
    lede: "Anyone with this link can view this client's creatives, read-only, until it expires or you revoke it. They do not need an account.",
    expiry: 'Expires in', days: { 1: '1 day', 7: '7 days', 30: '30 days' },
    label: 'Who is it for?', labelHint: 'Optional, up to 80 characters', create: 'Create link',
    linkLabel: 'Share link', shownOnce: 'This link is shown once. Copy it now.', copy: 'Copy', copied: 'Copied',
    live: 'Live links', none: 'No live links.', noLabel: 'No label',
    createdBy: 'Created by %email', expires: 'Expires %date', lastOpened: 'Last opened %date', notOpened: 'Not opened yet',
    revoke: 'Revoke', confirmRevoke: 'Revoke this link? Anyone using it loses access.', cancel: 'Cancel', close: 'Close',
    notOpen: 'Sharing opens with the next release.',
    invalidLabel: 'The label can be at most 80 characters.',
    failed: 'That did not work. Try again.',
    loading: 'Loading links...', loadFailed: 'The links could not be loaded.', retry: 'Try again',
    opening: 'Opening the link...',
    ended: 'This link no longer works. Ask the person who shared it for a new one.',
    openFailed: 'The link could not be opened just now.',
    signedIn: 'You are signed in, so this link opens your own view. To see the guest view, open the link in a private window.',
    goToGrovus: 'Go to Grovus', signIn: 'Sign in',
    guestLine: 'Guest view, read-only, until %date', leave: 'Leave guest view',
    recheckFailed: 'Grovus could not check this link just now.',
  },
  ru: {
    access: 'Доступ', share: 'Поделиться для просмотра', shareWhy: 'Делиться этим клиентом могут только владельцы и администраторы',
    lede: 'Любой, у кого есть эта ссылка, может смотреть креативы этого клиента, только для чтения, пока срок ссылки не истечёт или вы её не отзовёте. Аккаунт не нужен.',
    expiry: 'Срок действия', days: { 1: '1 день', 7: '7 дней', 30: '30 дней' },
    label: 'Для кого ссылка?', labelHint: 'Необязательно, до 80 символов', create: 'Создать ссылку',
    linkLabel: 'Ссылка для просмотра', shownOnce: 'Ссылка показывается один раз. Скопируйте её сейчас.', copy: 'Копировать', copied: 'Скопировано',
    live: 'Действующие ссылки', none: 'Действующих ссылок нет.', noLabel: 'Без подписи',
    createdBy: 'Создал(а) %email', expires: 'Истекает %date', lastOpened: 'Последнее открытие %date', notOpened: 'Ещё не открывали',
    revoke: 'Отозвать', confirmRevoke: 'Отозвать ссылку? Все, кто ею пользуется, потеряют доступ.', cancel: 'Отмена', close: 'Закрыть',
    notOpen: 'Ссылки для просмотра появятся со следующим релизом.',
    invalidLabel: 'Подпись может быть не длиннее 80 символов.',
    failed: 'Не получилось. Попробуйте ещё раз.',
    loading: 'Загружаем ссылки...', loadFailed: 'Не удалось загрузить ссылки.', retry: 'Повторить',
    opening: 'Открываем ссылку...',
    ended: 'Эта ссылка больше не работает. Попросите у того, кто ею поделился, новую.',
    openFailed: 'Сейчас не удалось открыть ссылку.',
    signedIn: 'Вы вошли в аккаунт, поэтому ссылка открывает ваш собственный вид. Чтобы увидеть гостевой вид, откройте ссылку в приватном окне.',
    goToGrovus: 'Перейти в Grovus', signIn: 'Войти',
    guestLine: 'Гостевой просмотр, только чтение, до %date', leave: 'Выйти из гостевого просмотра',
    recheckFailed: 'Сейчас Grovus не смог проверить эту ссылку.',
  },
  uk: {
    access: 'Доступ', share: 'Поділитися для перегляду', shareWhy: 'Ділитися цим клієнтом можуть лише власники та адміністратори',
    lede: 'Будь-хто з цим посиланням може переглядати креативи цього клієнта, лише для читання, доки строк посилання не спливе або ви його не відкличете. Обліковий запис не потрібен.',
    expiry: 'Строк дії', days: { 1: '1 день', 7: '7 днів', 30: '30 днів' },
    label: 'Для кого посилання?', labelHint: "Необов'язково, до 80 символів", create: 'Створити посилання',
    linkLabel: 'Посилання для перегляду', shownOnce: 'Посилання показується один раз. Скопіюйте його зараз.', copy: 'Скопіювати', copied: 'Скопійовано',
    live: 'Чинні посилання', none: 'Чинних посилань немає.', noLabel: 'Без підпису',
    createdBy: 'Створив(ла) %email', expires: 'Спливає %date', lastOpened: 'Востаннє відкрито %date', notOpened: 'Ще не відкривали',
    revoke: 'Відкликати', confirmRevoke: 'Відкликати посилання? Усі, хто ним користується, втратять доступ.', cancel: 'Скасувати', close: 'Закрити',
    notOpen: "Посилання для перегляду з'являться з наступним релізом.",
    invalidLabel: 'Підпис може бути не довшим за 80 символів.',
    failed: 'Не вдалося. Спробуйте ще раз.',
    loading: 'Завантажуємо посилання...', loadFailed: 'Не вдалося завантажити посилання.', retry: 'Повторити',
    opening: 'Відкриваємо посилання...',
    ended: 'Це посилання більше не працює. Попросіть у того, хто ним поділився, нове.',
    openFailed: 'Зараз не вдалося відкрити посилання.',
    signedIn: 'Ви увійшли в обліковий запис, тому посилання відкриває ваш власний вигляд. Щоб побачити гостьовий перегляд, відкрийте посилання в приватному вікні.',
    goToGrovus: 'Перейти в Grovus', signIn: 'Увійти',
    guestLine: 'Гостьовий перегляд, лише читання, до %date', leave: 'Вийти з гостьового перегляду',
    recheckFailed: 'Зараз Grovus не зміг перевірити це посилання.',
  },
}

/** GRO-157: the hypothesis dictionary screen, its add and edit dialog, and the creatives screen's assign bar. */
export interface DictionaryCopy {
  manage: string; back: string
  title: string; lede: string; count: string; add: string
  colLabel: string; colClaim: string; colCreatives: string; colStatus: string; colActions: string
  active: string; archived: string; sample: string
  edit: string; archive: string; restore: string
  archivedSection: string; empty: string; viewerNote: string
  loading: string; loadFailed: string; retry: string
  addTitle: string; editTitle: string
  name: string; labelEn: string; labelRu: string; labelUk: string; ukHint: string
  claim: string; claimHint: string; save: string; cancel: string; required: string; nameRequired: string
  added: string; saved: string; archivedDone: string; restoredDone: string
  forbidden: string; notFound: string; invalid: string; failed: string
  demoValue: string; archivedValue: string; unknownValue: string; unknownCreatives: string
  /** The refusal lines for a write that names more than one axis; they name none. */
  archivedValueAny: string; unknownValueAny: string
  selectAll: string; selectRow: string; selected: string
  picker: string; pickerPlaceholder: string; noOptions: string; optionsFailed: string
  clear: string; scopeLine: string
  apply: string; clearTags: string; cancelSelection: string; appliedDone: string; tagsClearedDone: string
  assignedDone: string; clearedDone: string
  createOption: string; createdAssigned: string; createdNotAssigned: string
  createUncertain: string; createdAssignUncertain: string
  /**
   * The same lines for the visual axis (GRO-170), the message and the offer (GRO-171); the
   * top-level ones name the idea.
   */
  visual: AxisCopy
  message: AxisCopy
  offer: AxisCopy
  versionPicker: string; setVersion: string; clearVersion: string; versionNeedsHypothesis: string
  versionSet: string; versionCleared: string; badVersion: string; noHypothesis: string
  merge: string; mergeTitle: string; mergeBody: string; mergeChoose: string; mergeKeeps: string
  mergeNone: string; mergeConfirm: string; mergedDone: string
  badSelection: string; demoCreatives: string; mixedKinds: string; assignmentConflict: string
  techniqueConflict: string
  messageConflict: string; offerConflict: string
}

/** The lines of an assignment that name its axis. */
export type AxisCopy = Pick<
  DictionaryCopy,
  | 'assignedDone' | 'clearedDone' | 'createOption' | 'createdAssigned' | 'createdNotAssigned' | 'createUncertain'
  | 'createdAssignUncertain' | 'picker' | 'pickerPlaceholder' | 'optionsFailed' | 'archivedValue' | 'unknownValue'
>

/** The lines that name the axis an assignment wrote. */
export function axisCopy(copy: DictionaryCopy, axis: Axis): AxisCopy {
  return axis === 'idea' ? copy : copy[axis]
}

/**
 * The copy a refused write reads: the refusal lines of the one axis it named, or the
 * neutral ones when it named several. The other lines are the shared ones.
 */
export function refusalCopy(copy: DictionaryCopy, axes: readonly Axis[]): DictionaryCopy {
  if (axes.length === 1) return { ...copy, ...axisCopy(copy, axes[0]) }
  if (axes.length > 1) return { ...copy, archivedValue: copy.archivedValueAny, unknownValue: copy.unknownValueAny }
  return copy
}

export const DICTIONARY_COPY: Record<Lang, DictionaryCopy> = {
  en: {
    manage: 'Manage ideas', back: 'Back to the comparison',
    title: 'Ideas', lede: 'The ideas this client tests. Assign them to creatives on the Creatives screen, in the table view.',
    count: '%n {n:value|values}', add: '+ Add idea',
    colLabel: 'Idea', colClaim: 'Claim', colCreatives: 'Creatives', colStatus: 'Status', colActions: 'Actions',
    active: 'Active', archived: 'Archived', sample: 'Sample data',
    edit: 'Edit', archive: 'Archive', restore: 'Restore',
    archivedSection: 'Archived (%n)', empty: 'No ideas yet.',
    viewerNote: 'Owners, Admins and Members can change this dictionary and assign its values.',
    loading: 'Loading ideas...', loadFailed: 'The ideas could not be loaded.', retry: 'Try again',
    addTitle: 'Add idea', editTitle: 'Edit idea',
    name: 'Name', labelEn: 'Label in English', labelRu: 'Label in Russian', labelUk: 'Label in Ukrainian',
    ukHint: 'Optional. When it is empty, the English label shows.',
    claim: 'Claim', claimHint: 'Optional. The statement the creatives test, for example "Showing the price first raises the purchase rate".',
    save: 'Save', cancel: 'Cancel', required: 'Fill in the English and Russian labels.', nameRequired: 'Fill in the name.',
    added: 'Idea added.', saved: 'Idea saved.', archivedDone: 'Idea archived.', restoredDone: 'Idea restored.',
    forbidden: 'Your role does not allow this change.',
    notFound: 'This idea or client is no longer available. Reload the page and try again.',
    invalid: 'The change was refused. Reload the page and try again.',
    demoValue: 'Sample data cannot be changed or assigned.',
    archivedValue: 'This idea is archived. Restore it in Manage ideas, or pick another.',
    unknownValue: 'This idea no longer exists. Pick another.',
    archivedValueAny: 'One of the chosen values is archived. Pick another.',
    unknownValueAny: 'One of the chosen values no longer exists. Pick another.',
    unknownCreatives: 'Some selected creatives are no longer in this client. Reload the page and select again.',
    failed: 'That did not work. Try again.',
    selectAll: 'Select all shown creatives', selectRow: 'Select %name', selected: '%c selected',
    picker: 'Idea', pickerPlaceholder: 'Search ideas',
    noOptions: 'No ideas yet. Type a name in the picker to create one.',
    optionsFailed: 'The ideas could not be loaded.',
    clear: 'Clear idea',
    scopeLine: 'An idea belongs to the whole creative: every ad of it in every account and every period, past periods included.',
    apply: 'Apply', clearTags: 'Clear tags', cancelSelection: 'Cancel',
    appliedDone: 'Applied to %c {c:creative|creatives}.', tagsClearedDone: 'Tags cleared on %c {c:creative|creatives}.',
    assignedDone: 'Idea assigned to %c {c:creative|creatives}.', clearedDone: 'Idea cleared on %c {c:creative|creatives}.',
    versionPicker: 'Version', setVersion: 'Set version', clearVersion: 'Clear version',
    versionNeedsHypothesis: 'Assign an idea first to set a version',
    versionSet: 'Version %v set on %c {c:creative|creatives}.', versionCleared: 'Version cleared on %c {c:creative|creatives}.',
    badVersion: 'Choose a letter from A to Z.', noHypothesis: 'Every selected creative needs an idea first.',
    merge: 'Merge', mergeTitle: 'Merge %c {c:creative|creatives} into one',
    mergeBody: 'Their ads and figures will be summed in one row. This cannot be undone in the app.',
    mergeChoose: 'These creatives carry different ideas. Choose the one the merged row keeps:',
    mergeKeeps: 'The merged row keeps: %h', mergeNone: 'No idea', mergeConfirm: 'Merge',
    mergedDone: '%c {c:creative|creatives} merged into one.',
    badSelection: 'Select between 2 and 100 creatives to merge.', demoCreatives: 'Demo creatives cannot be merged.',
    mixedKinds: 'A video and an image cannot be merged.',
    assignmentConflict: 'The ideas changed. Reload and choose again.',
    techniqueConflict: 'These creatives carry different visuals and cannot be merged.',
    messageConflict: 'These creatives carry different messages and cannot be merged.',
    offerConflict: 'These creatives carry different offers and cannot be merged.',
    createOption: 'Create idea "%name"',
    createdAssigned: '"%name" created and assigned to %c {c:creative|creatives}.',
    createdNotAssigned: '"%name" created, but the assignment failed. Choose it in the picker and assign again.',
    createUncertain: '"%name" may have been created. Check the picker before you create it again.',
    createdAssignUncertain: '"%name" created; the assignment may have been saved. Check the table before you assign again.',
    visual: {
      picker: 'Visual', pickerPlaceholder: 'Search visuals', optionsFailed: 'The visuals could not be loaded.',
      archivedValue: 'This visual is archived. Pick another.', unknownValue: 'This visual no longer exists. Pick another.',
      assignedDone: 'Visual assigned to %c {c:creative|creatives}.', clearedDone: 'Visual cleared on %c {c:creative|creatives}.',
      createOption: 'Create visual "%name"',
      createdAssigned: '"%name" created and assigned to %c {c:creative|creatives}.',
      createdNotAssigned: '"%name" created, but the assignment failed. Choose it in the picker and assign again.',
      createUncertain: '"%name" may have been created. Check the picker before you create it again.',
      createdAssignUncertain: '"%name" created; the assignment may have been saved. Check the table before you assign again.',
    },
    message: {
      picker: 'Message', pickerPlaceholder: 'Search messages', optionsFailed: 'The messages could not be loaded.',
      archivedValue: 'This message is archived. Pick another.', unknownValue: 'This message no longer exists. Pick another.',
      assignedDone: 'Message assigned to %c {c:creative|creatives}.', clearedDone: 'Message cleared on %c {c:creative|creatives}.',
      createOption: 'Create message "%name"',
      createdAssigned: '"%name" created and assigned to %c {c:creative|creatives}.',
      createdNotAssigned: '"%name" created, but the assignment failed. Choose it in the picker and assign again.',
      createUncertain: '"%name" may have been created. Check the picker before you create it again.',
      createdAssignUncertain: '"%name" created; the assignment may have been saved. Check the table before you assign again.',
    },
    offer: {
      picker: 'Offer', pickerPlaceholder: 'Search offers', optionsFailed: 'The offers could not be loaded.',
      archivedValue: 'This offer is archived. Pick another.', unknownValue: 'This offer no longer exists. Pick another.',
      assignedDone: 'Offer assigned to %c {c:creative|creatives}.', clearedDone: 'Offer cleared on %c {c:creative|creatives}.',
      createOption: 'Create offer "%name"',
      createdAssigned: '"%name" created and assigned to %c {c:creative|creatives}.',
      createdNotAssigned: '"%name" created, but the assignment failed. Choose it in the picker and assign again.',
      createUncertain: '"%name" may have been created. Check the picker before you create it again.',
      createdAssignUncertain: '"%name" created; the assignment may have been saved. Check the table before you assign again.',
    },
  },
  ru: {
    manage: 'Управлять идеями', back: 'Назад к сравнению',
    title: 'Идеи', lede: 'Идеи, которые проверяет этот клиент. Назначайте их креативам на экране «Креативы», в виде таблицы.',
    count: '%n {n:значение|значения|значений}', add: '+ Добавить идею',
    colLabel: 'Идея', colClaim: 'Утверждение', colCreatives: 'Креативы', colStatus: 'Статус', colActions: 'Действия',
    active: 'Активна', archived: 'В архиве', sample: 'Демо-данные',
    edit: 'Изменить', archive: 'В архив', restore: 'Восстановить',
    archivedSection: 'В архиве (%n)', empty: 'Идей пока нет.',
    viewerNote: 'Менять этот справочник и назначать его значения могут владельцы, администраторы и участники.',
    loading: 'Загружаем идеи...', loadFailed: 'Не удалось загрузить идеи.', retry: 'Повторить',
    addTitle: 'Новая идея', editTitle: 'Изменить идею',
    name: 'Название', labelEn: 'Название на английском', labelRu: 'Название на русском', labelUk: 'Название на украинском',
    ukHint: 'Необязательно. Если поле пустое, показывается английское название.',
    claim: 'Утверждение', claimHint: 'Необязательно. Что проверяют креативы, например «Цена в первом кадре повышает долю покупок».',
    save: 'Сохранить', cancel: 'Отмена', required: 'Заполните английское и русское названия.', nameRequired: 'Заполните название.',
    added: 'Идея добавлена.', saved: 'Идея сохранена.', archivedDone: 'Идея перенесена в архив.', restoredDone: 'Идея восстановлена.',
    forbidden: 'Ваша роль не позволяет это изменить.',
    notFound: 'Эта идея или клиент больше недоступны. Обновите страницу и попробуйте ещё раз.',
    invalid: 'Изменение отклонено. Обновите страницу и попробуйте ещё раз.',
    demoValue: 'Демо-данные нельзя менять или назначать.',
    archivedValue: 'Эта идея в архиве. Восстановите её в «Управлять идеями» или выберите другую.',
    unknownValue: 'Этой идеи больше нет. Выберите другую.',
    archivedValueAny: 'Одно из выбранных значений в архиве. Выберите другое.',
    unknownValueAny: 'Одного из выбранных значений больше нет. Выберите другое.',
    unknownCreatives: 'Некоторых выбранных креативов больше нет у этого клиента. Обновите страницу и выберите заново.',
    failed: 'Не получилось. Попробуйте ещё раз.',
    selectAll: 'Выбрать все показанные креативы', selectRow: 'Выбрать %name', selected: 'Выбрано: %c',
    picker: 'Идея', pickerPlaceholder: 'Найти идею',
    noOptions: 'Идей пока нет. Введите название в поле выбора, чтобы создать идею.',
    optionsFailed: 'Не удалось загрузить идеи.',
    clear: 'Снять идею',
    scopeLine: 'Идея относится ко всему креативу: ко всем его объявлениям во всех аккаунтах и за все периоды, включая прошедшие.',
    apply: 'Применить', clearTags: 'Снять теги', cancelSelection: 'Отмена',
    appliedDone: 'Применено к %c {c:креативу|креативам|креативам}.', tagsClearedDone: 'Теги сняты с %c {c:креатива|креативов|креативов}.',
    assignedDone: 'Идея назначена: %c {c:креатив|креатива|креативов}.', clearedDone: 'Идея снята: %c {c:креатив|креатива|креативов}.',
    versionPicker: 'Версия', setVersion: 'Задать версию', clearVersion: 'Сбросить версию',
    versionNeedsHypothesis: 'Сначала назначьте идею, чтобы задать версию',
    versionSet: 'Версия %v задана: %c {c:креатив|креатива|креативов}.', versionCleared: 'Версия сброшена: %c {c:креатив|креатива|креативов}.',
    badVersion: 'Выберите букву от A до Z.', noHypothesis: 'Сначала назначьте идею каждому выбранному креативу.',
    merge: 'Объединить', mergeTitle: 'Объединить %c {c:креатив|креатива|креативов} в один',
    mergeBody: 'Их объявления и показатели будут суммированы в одной строке. Отменить это в приложении нельзя.',
    mergeChoose: 'У этих креативов разные идеи. Выберите, какую сохранит объединённая строка:',
    mergeKeeps: 'Объединённая строка сохранит: %h', mergeNone: 'Без идеи', mergeConfirm: 'Объединить',
    mergedDone: 'Объединено в один: %c {c:креатив|креатива|креативов}.',
    badSelection: 'Выберите от 2 до 100 креативов для объединения.', demoCreatives: 'Демо-креативы нельзя объединить.',
    mixedKinds: 'Видео и изображение нельзя объединить.',
    assignmentConflict: 'Идеи изменились. Обновите страницу и выберите снова.',
    techniqueConflict: 'У этих креативов разные техники, их нельзя объединить.',
    messageConflict: 'У этих креативов разные сообщения - объединить нельзя.',
    offerConflict: 'У этих креативов разные офферы - объединить нельзя.',
    createOption: 'Создать идею «%name»',
    createdAssigned: 'Идея «%name» создана и назначена: %c {c:креатив|креатива|креативов}.',
    createdNotAssigned: 'Идея «%name» создана, но назначить её не удалось. Выберите её в списке и назначьте снова.',
    createUncertain: 'Идея «%name», возможно, создана. Проверьте список, прежде чем создавать её снова.',
    createdAssignUncertain: 'Идея «%name» создана; назначение, возможно, сохранено. Проверьте таблицу, прежде чем назначать снова.',
    visual: {
      picker: 'Визуал', pickerPlaceholder: 'Найти визуал', optionsFailed: 'Не удалось загрузить визуалы.',
      archivedValue: 'Этот визуал в архиве. Выберите другой.', unknownValue: 'Этого визуала больше нет. Выберите другой.',
      assignedDone: 'Визуал назначен: %c {c:креатив|креатива|креативов}.', clearedDone: 'Визуал снят: %c {c:креатив|креатива|креативов}.',
      createOption: 'Создать визуал «%name»',
      createdAssigned: 'Визуал «%name» создан и назначен: %c {c:креатив|креатива|креативов}.',
      createdNotAssigned: 'Визуал «%name» создан, но назначить его не удалось. Выберите его в списке и назначьте снова.',
      createUncertain: 'Визуал «%name», возможно, создан. Проверьте список, прежде чем создавать его снова.',
      createdAssignUncertain: 'Визуал «%name» создан; назначение, возможно, сохранено. Проверьте таблицу, прежде чем назначать снова.',
    },
    message: {
      picker: 'Сообщение', pickerPlaceholder: 'Найти сообщение', optionsFailed: 'Не удалось загрузить сообщения.',
      archivedValue: 'Это сообщение в архиве. Выберите другое.', unknownValue: 'Этого сообщения больше нет. Выберите другое.',
      assignedDone: 'Сообщение назначено: %c {c:креатив|креатива|креативов}.', clearedDone: 'Сообщение снято: %c {c:креатив|креатива|креативов}.',
      createOption: 'Создать сообщение «%name»',
      createdAssigned: 'Сообщение «%name» создано и назначено: %c {c:креатив|креатива|креативов}.',
      createdNotAssigned: 'Сообщение «%name» создано, но назначить его не удалось. Выберите его в списке и назначьте снова.',
      createUncertain: 'Сообщение «%name», возможно, создано. Проверьте список, прежде чем создавать его снова.',
      createdAssignUncertain: 'Сообщение «%name» создано; назначение, возможно, сохранено. Проверьте таблицу, прежде чем назначать снова.',
    },
    offer: {
      picker: 'Оффер', pickerPlaceholder: 'Найти оффер', optionsFailed: 'Не удалось загрузить офферы.',
      archivedValue: 'Этот оффер в архиве. Выберите другой.', unknownValue: 'Этого оффера больше нет. Выберите другой.',
      assignedDone: 'Оффер назначен: %c {c:креатив|креатива|креативов}.', clearedDone: 'Оффер снят: %c {c:креатив|креатива|креативов}.',
      createOption: 'Создать оффер «%name»',
      createdAssigned: 'Оффер «%name» создан и назначен: %c {c:креатив|креатива|креативов}.',
      createdNotAssigned: 'Оффер «%name» создан, но назначить его не удалось. Выберите его в списке и назначьте снова.',
      createUncertain: 'Оффер «%name», возможно, создан. Проверьте список, прежде чем создавать его снова.',
      createdAssignUncertain: 'Оффер «%name» создан; назначение, возможно, сохранено. Проверьте таблицу, прежде чем назначать снова.',
    },
  },
  uk: {
    manage: 'Керувати ідеями', back: 'Назад до порівняння',
    title: 'Ідеї', lede: 'Ідеї, які перевіряє цей клієнт. Призначайте їх креативам на екрані «Креативи», у вигляді таблиці.',
    count: '%n {n:значення|значення|значень}', add: '+ Додати ідею',
    colLabel: 'Ідея', colClaim: 'Твердження', colCreatives: 'Креативи', colStatus: 'Стан', colActions: 'Дії',
    active: 'Активна', archived: 'В архіві', sample: 'Демодані',
    edit: 'Змінити', archive: 'До архіву', restore: 'Відновити',
    archivedSection: 'В архіві (%n)', empty: 'Ідей поки немає.',
    viewerNote: 'Змінювати цей довідник і призначати його значення можуть власники, адміністратори та учасники.',
    loading: 'Завантажуємо ідеї...', loadFailed: 'Не вдалося завантажити ідеї.', retry: 'Повторити',
    addTitle: 'Нова ідея', editTitle: 'Змінити ідею',
    name: 'Назва', labelEn: 'Назва англійською', labelRu: 'Назва російською', labelUk: 'Назва українською',
    ukHint: "Необов'язково. Якщо поле порожнє, показується англійська назва.",
    claim: 'Твердження', claimHint: "Необов'язково. Що перевіряють креативи, наприклад «Ціна в першому кадрі підвищує частку покупок».",
    save: 'Зберегти', cancel: 'Скасувати', required: 'Заповніть англійську та російську назви.', nameRequired: 'Заповніть назву.',
    added: 'Ідею додано.', saved: 'Ідею збережено.', archivedDone: 'Ідею перенесено до архіву.', restoredDone: 'Ідею відновлено.',
    forbidden: 'Ваша роль не дозволяє цю зміну.',
    notFound: 'Ця ідея або клієнт більше недоступні. Оновіть сторінку та спробуйте ще раз.',
    invalid: 'Зміну відхилено. Оновіть сторінку та спробуйте ще раз.',
    demoValue: 'Демодані не можна змінювати чи призначати.',
    archivedValue: 'Ця ідея в архіві. Відновіть її в «Керувати ідеями» або виберіть іншу.',
    unknownValue: 'Цієї ідеї більше немає. Виберіть іншу.',
    archivedValueAny: 'Одне з вибраних значень в архіві. Виберіть інше.',
    unknownValueAny: 'Одного з вибраних значень більше немає. Виберіть інше.',
    unknownCreatives: 'Деяких вибраних креативів більше немає в цього клієнта. Оновіть сторінку та виберіть знову.',
    failed: 'Не вдалося. Спробуйте ще раз.',
    selectAll: 'Вибрати всі показані креативи', selectRow: 'Вибрати %name', selected: 'Вибрано: %c',
    picker: 'Ідея', pickerPlaceholder: 'Знайти ідею',
    noOptions: 'Ідей поки немає. Введіть назву в полі вибору, щоб створити ідею.',
    optionsFailed: 'Не вдалося завантажити ідеї.',
    clear: 'Зняти ідею',
    scopeLine: 'Ідея належить усьому креативу: усім його оголошенням в усіх акаунтах і за всі періоди, включно з минулими.',
    apply: 'Застосувати', clearTags: 'Зняти теги', cancelSelection: 'Скасувати',
    appliedDone: 'Застосовано до %c {c:креативу|креативів|креативів}.', tagsClearedDone: 'Теги знято з %c {c:креативу|креативів|креативів}.',
    assignedDone: 'Ідею призначено: %c {c:креатив|креативи|креативів}.', clearedDone: 'Ідею знято: %c {c:креатив|креативи|креативів}.',
    versionPicker: 'Версія', setVersion: 'Задати версію', clearVersion: 'Скинути версію',
    versionNeedsHypothesis: 'Спершу призначте ідею, щоб задати версію',
    versionSet: 'Версію %v задано: %c {c:креатив|креативи|креативів}.', versionCleared: 'Версію скинуто: %c {c:креатив|креативи|креативів}.',
    badVersion: 'Виберіть літеру від A до Z.', noHypothesis: 'Спершу призначте ідею кожному вибраному креативу.',
    merge: "Об'єднати", mergeTitle: "Об'єднати %c {c:креатив|креативи|креативів} в один",
    mergeBody: 'Їхні оголошення та показники буде підсумовано в одному рядку. Скасувати це в застосунку не можна.',
    mergeChoose: "У цих креативів різні ідеї. Виберіть, яку збереже об'єднаний рядок:",
    mergeKeeps: "Об'єднаний рядок збереже: %h", mergeNone: 'Без ідеї', mergeConfirm: "Об'єднати",
    mergedDone: "Об'єднано в один: %c {c:креатив|креативи|креативів}.",
    badSelection: "Виберіть від 2 до 100 креативів для об'єднання.", demoCreatives: "Демо-креативи не можна об'єднати.",
    mixedKinds: "Відео та зображення не можна об'єднати.",
    assignmentConflict: 'Ідеї змінилися. Оновіть сторінку й виберіть знову.',
    techniqueConflict: "У цих креативів різні техніки, їх не можна об'єднати.",
    messageConflict: "Ці креативи мають різні повідомлення - об'єднати не можна.",
    offerConflict: "Ці креативи мають різні офери - об'єднати не можна.",
    createOption: 'Створити ідею «%name»',
    createdAssigned: 'Ідею «%name» створено та призначено: %c {c:креатив|креативи|креативів}.',
    createdNotAssigned: 'Ідею «%name» створено, але призначити її не вдалося. Виберіть її у списку та призначте знову.',
    createUncertain: 'Ідею «%name», можливо, створено. Перевірте список, перш ніж створювати її знову.',
    createdAssignUncertain: 'Ідею «%name» створено; призначення, можливо, збережено. Перевірте таблицю, перш ніж призначати знову.',
    visual: {
      picker: 'Візуал', pickerPlaceholder: 'Знайти візуал', optionsFailed: 'Не вдалося завантажити візуали.',
      archivedValue: 'Цей візуал в архіві. Виберіть інший.', unknownValue: 'Цього візуалу більше немає. Виберіть інший.',
      assignedDone: 'Візуал призначено: %c {c:креатив|креативи|креативів}.', clearedDone: 'Візуал знято: %c {c:креатив|креативи|креативів}.',
      createOption: 'Створити візуал «%name»',
      createdAssigned: 'Візуал «%name» створено та призначено: %c {c:креатив|креативи|креативів}.',
      createdNotAssigned: 'Візуал «%name» створено, але призначити його не вдалося. Виберіть його у списку та призначте знову.',
      createUncertain: 'Візуал «%name», можливо, створено. Перевірте список, перш ніж створювати його знову.',
      createdAssignUncertain: 'Візуал «%name» створено; призначення, можливо, збережено. Перевірте таблицю, перш ніж призначати знову.',
    },
    message: {
      picker: 'Повідомлення', pickerPlaceholder: 'Знайти повідомлення', optionsFailed: 'Не вдалося завантажити повідомлення.',
      archivedValue: 'Це повідомлення в архіві. Виберіть інше.', unknownValue: 'Цього повідомлення більше немає. Виберіть інше.',
      assignedDone: 'Повідомлення призначено: %c {c:креатив|креативи|креативів}.', clearedDone: 'Повідомлення знято: %c {c:креатив|креативи|креативів}.',
      createOption: 'Створити повідомлення «%name»',
      createdAssigned: 'Повідомлення «%name» створено та призначено: %c {c:креатив|креативи|креативів}.',
      createdNotAssigned: 'Повідомлення «%name» створено, але призначити його не вдалося. Виберіть його у списку та призначте знову.',
      createUncertain: 'Повідомлення «%name», можливо, створено. Перевірте список, перш ніж створювати його знову.',
      createdAssignUncertain: 'Повідомлення «%name» створено; призначення, можливо, збережено. Перевірте таблицю, перш ніж призначати знову.',
    },
    offer: {
      picker: 'Офер', pickerPlaceholder: 'Знайти офер', optionsFailed: 'Не вдалося завантажити офери.',
      archivedValue: 'Цей офер в архіві. Виберіть інший.', unknownValue: 'Цього офера більше немає. Виберіть інший.',
      assignedDone: 'Офер призначено: %c {c:креатив|креативи|креативів}.', clearedDone: 'Офер знято: %c {c:креатив|креативи|креативів}.',
      createOption: 'Створити офер «%name»',
      createdAssigned: 'Офер «%name» створено та призначено: %c {c:креатив|креативи|креативів}.',
      createdNotAssigned: 'Офер «%name» створено, але призначити його не вдалося. Виберіть його у списку та призначте знову.',
      createUncertain: 'Офер «%name», можливо, створено. Перевірте список, перш ніж створювати його знову.',
      createdAssignUncertain: 'Офер «%name» створено; призначення, можливо, збережено. Перевірте таблицю, перш ніж призначати знову.',
    },
  },
}

/** The company settings screen and the no-target hint (GRO-185). */
export interface SettingsCopy {
  nav: string; title: string; sub: string
  secRef: string; metric: string; metricHelp: string
  targetRoas: string; targetCpa: string; targetPlaceholder: string
  targetHelpEmpty: string; targetHelpSet: string; targetHelpStopLoss: string; targetHelpNoCur: string
  currency: string; currencyDerived: string; currencyHelp: string; currencyClears: string; noAccounts: string
  staleNote: string
  secStat: string; tiers: string; tiersHelp: string; tierDirection: string; tierProvisional: string; tierReliable: string
  tierHint: string; tierLowNote: string
  stopLoss: string; stopLossHelp: string; stopLossValue: string; wait: string; waitHelp: string; waitValue: string; days: string
  save: string; cancel: string; saved: string; lastChanged: string
  readonly: string; loading: string; loadFailed: string; retry: string
  invalidMetric: string; invalidTarget: string; invalidCurrency: string; cpaNeedsCurrency: string
  invalidTiers: string; invalidMultiple: string; invalidWaiting: string
  forbidden: string; failed: string; notStored: string
  hint: string; hintLink: string
}

export const SETTINGS_COPY: Record<Lang, SettingsCopy> = {
  en: {
    nav: 'Settings', title: 'Company settings', sub: 'What Grovus compares creatives against when it issues a verdict.',
    secRef: 'Point of reference', metric: 'Primary metric', metricHelp: 'ROAS for e-commerce, CPA for lead generation.',
    targetRoas: 'Target ROAS', targetCpa: 'Target CPA', targetPlaceholder: 'e.g. 2.50',
    targetHelpEmpty: 'Not set: verdicts are relative to the 90-day average.',
    targetHelpSet: 'Verdicts: better / worse than target.',
    targetHelpStopLoss: 'Stop-loss: %m × target = %v.',
    targetHelpNoCur: 'A target CPA is set in the reporting currency. Until there is one, verdicts are relative to the 90-day average.',
    currency: 'Reporting currency', currencyDerived: 'by 90-day spend',
    currencyHelp: 'The target is in this currency. Accounts in another currency compare with their own average.',
    currencyClears: 'Changing the currency clears the target CPA.',
    noAccounts: 'Connect an ad account to choose the reporting currency.',
    staleNote: 'No ad account reports in %c any more, so the target does not apply. Choose a reporting currency and save; the target CPA was cleared.',
    secStat: 'Tiers and stop-loss',
    tiers: 'Confidence tiers', tiersHelp: 'How many conversions a creative needs before a verdict leans on it.',
    tierDirection: 'direction', tierProvisional: 'provisional', tierReliable: 'reliable',
    tierHint: 'At n = %n a difference counts only from a gap of %g %',
    tierLowNote: 'With a provisional tier of 4 or less, the confidence band can hold back a stop-loss verdict.',
    stopLoss: 'Stop-loss', stopLossHelp: 'The stop-loss threshold is this multiple of the CPA the target implies, or of the 90-day CPA without a target.',
    stopLossValue: '%m × CPA', wait: 'Hypothesis waiting period', waitHelp: 'Read by the hypothesis lifecycle, which comes in a later release.',
    waitValue: '%n days', days: 'days',
    save: 'Save', cancel: 'Cancel', saved: 'Settings saved.', lastChanged: 'Last changed: %d',
    readonly: 'Owners and Admins change settings. You see them read-only.',
    loading: 'Loading settings…', loadFailed: 'Could not load the settings.', retry: 'Retry',
    invalidMetric: 'Choose ROAS or CPA.',
    invalidTarget: 'Enter a number above zero, or leave the field empty.',
    invalidCurrency: 'Choose a currency one of the ad accounts reports in.',
    cpaNeedsCurrency: 'A target CPA needs a reporting currency.',
    forbidden: 'Only an Owner or an Admin can change the settings.',
    invalidTiers: 'Tiers are whole numbers from 1 to 10000, with direction ≤ provisional ≤ reliable.',
    invalidMultiple: 'Enter a multiple from 1 to 100.',
    invalidWaiting: 'Enter a whole number of days from 1 to 365.',
    failed: 'The settings were not saved. Try again.',
    notStored: 'The server did not store the new values. Try again later.',
    hint: 'Set a target %metric - verdicts will be about money, not about the average', hintLink: 'Open settings',
  },
  ru: {
    nav: 'Настройки', title: 'Настройки компании', sub: 'С чем Grovus сравнивает креативы, когда выносит вывод.',
    secRef: 'Точка отсчёта', metric: 'Основная метрика', metricHelp: 'ROAS для e-commerce, CPA для лидогенерации.',
    targetRoas: 'Целевой ROAS', targetCpa: 'Целевой CPA', targetPlaceholder: 'напр. 2,50',
    targetHelpEmpty: 'Не задана: выводы относительно среднего за 90 дней.',
    targetHelpSet: 'Выводы: лучше цели / хуже цели.',
    targetHelpStopLoss: 'Стоп-лосс: %m × цель = %v.',
    targetHelpNoCur: 'Целевой CPA задаётся в валюте отчётов. Пока её нет, выводы относительно среднего за 90 дней.',
    currency: 'Валюта отчётов', currencyDerived: 'по расходам за 90 дней',
    currencyHelp: 'Цель задаётся в этой валюте. Аккаунты в другой валюте сравниваются со своим средним.',
    currencyClears: 'Смена валюты сбросит целевой CPA.',
    noAccounts: 'Подключите рекламный аккаунт, чтобы выбрать валюту отчётов.',
    staleNote: 'Ни один рекламный аккаунт больше не отчитывается в %c, поэтому цель не применяется. Выберите валюту отчётов и сохраните; целевой CPA сброшен.',
    secStat: 'Пороги и стоп-лосс',
    tiers: 'Ступени уверенности', tiersHelp: 'Сколько конверсий нужно креативу, чтобы вывод на него опирался.',
    tierDirection: 'направление', tierProvisional: 'предварительно', tierReliable: 'достоверно',
    tierHint: 'При n = %n различие признаётся только при разрыве от %g %',
    tierLowNote: 'При ступени «предварительно» 4 и меньше полоса уверенности может удержать вывод «стоп-лосс».',
    stopLoss: 'Стоп-лосс', stopLossHelp: 'Порог стоп-лосса - это кратное CPA, которое следует из цели, а без цели - кратное CPA за 90 дней.',
    stopLossValue: '%m × CPA', wait: 'Срок ожидания гипотезы', waitHelp: 'Его прочитает жизненный цикл гипотез, который появится в следующем выпуске.',
    waitValue: '%n дн.', days: 'дн.',
    save: 'Сохранить', cancel: 'Отменить', saved: 'Настройки сохранены.', lastChanged: 'Последнее изменение: %d',
    readonly: 'Настройки меняют Владелец и Администратор. Вы видите их только для чтения.',
    loading: 'Загружаем настройки…', loadFailed: 'Не удалось загрузить настройки.', retry: 'Повторить',
    invalidMetric: 'Выберите ROAS или CPA.',
    invalidTarget: 'Введите число больше нуля или оставьте поле пустым.',
    invalidCurrency: 'Выберите валюту, в которой отчитывается один из рекламных аккаунтов.',
    cpaNeedsCurrency: 'Для целевого CPA нужна валюта отчётов.',
    forbidden: 'Настройки меняют только Владелец и Администратор.',
    invalidTiers: 'Ступени - целые числа от 1 до 10000, направление ≤ предварительно ≤ достоверно.',
    invalidMultiple: 'Введите кратное от 1 до 100.',
    invalidWaiting: 'Введите целое число дней от 1 до 365.',
    failed: 'Настройки не сохранены. Попробуйте ещё раз.',
    notStored: 'Сервер не сохранил новые значения. Попробуйте позже.',
    hint: 'Задайте целевой %metric - выводы станут про деньги, а не про среднее', hintLink: 'Открыть настройки',
  },
  uk: {
    nav: 'Налаштування', title: 'Налаштування компанії', sub: 'З чим Grovus порівнює креативи, коли робить висновок.',
    secRef: 'Точка відліку', metric: 'Основна метрика', metricHelp: 'ROAS для e-commerce, CPA для лідогенерації.',
    targetRoas: 'Цільовий ROAS', targetCpa: 'Цільовий CPA', targetPlaceholder: 'наприклад, 2,50',
    targetHelpEmpty: 'Не задано: висновки відносно середнього за 90 днів.',
    targetHelpSet: 'Висновки: краще за ціль / гірше за ціль.',
    targetHelpStopLoss: 'Стоп-лос: %m × ціль = %v.',
    targetHelpNoCur: 'Цільовий CPA задається у валюті звітів. Поки її немає, висновки відносно середнього за 90 днів.',
    currency: 'Валюта звітів', currencyDerived: 'за витратами за 90 днів',
    currencyHelp: 'Ціль задається в цій валюті. Акаунти в іншій валюті порівнюються зі своїм середнім.',
    currencyClears: 'Зміна валюти скине цільовий CPA.',
    noAccounts: 'Підключіть рекламний акаунт, щоб вибрати валюту звітів.',
    staleNote: 'Жоден рекламний акаунт більше не звітує в %c, тому ціль не застосовується. Виберіть валюту звітів і збережіть; цільовий CPA скинуто.',
    secStat: 'Пороги і стоп-лос',
    tiers: 'Ступені впевненості', tiersHelp: 'Скільки конверсій потрібно креативу, щоб висновок на нього спирався.',
    tierDirection: 'напрямок', tierProvisional: 'попередньо', tierReliable: 'достовірно',
    tierHint: 'При n = %n різниця визнається лише за розриву від %g %',
    tierLowNote: 'Зі ступенем «попередньо» 4 і менше смуга впевненості може утримати висновок «стоп-лос».',
    stopLoss: 'Стоп-лос', stopLossHelp: 'Поріг стоп-лосу - це кратне CPA, яке випливає з цілі, а без цілі - кратне CPA за 90 днів.',
    stopLossValue: '%m × CPA', wait: 'Термін очікування гіпотези', waitHelp: 'Його прочитає життєвий цикл гіпотез, який з’явиться в наступному випуску.',
    waitValue: '%n днів', days: 'днів',
    save: 'Зберегти', cancel: 'Скасувати', saved: 'Налаштування збережено.', lastChanged: 'Остання зміна: %d',
    readonly: 'Налаштування змінюють Власник і Адміністратор. Ви бачите їх лише для читання.',
    loading: 'Завантажуємо налаштування…', loadFailed: 'Не вдалося завантажити налаштування.', retry: 'Повторити',
    invalidMetric: 'Виберіть ROAS або CPA.',
    invalidTarget: 'Введіть число більше нуля або залиште поле порожнім.',
    invalidCurrency: 'Виберіть валюту, у якій звітує один із рекламних акаунтів.',
    cpaNeedsCurrency: 'Для цільового CPA потрібна валюта звітів.',
    forbidden: 'Налаштування змінюють лише Власник і Адміністратор.',
    invalidTiers: 'Ступені - цілі числа від 1 до 10000, напрямок ≤ попередньо ≤ достовірно.',
    invalidMultiple: 'Введіть кратне від 1 до 100.',
    invalidWaiting: 'Введіть ціле число днів від 1 до 365.',
    failed: 'Налаштування не збережено. Спробуйте ще раз.',
    notStored: 'Сервер не зберіг нові значення. Спробуйте пізніше.',
    hint: 'Задайте цільовий %metric - висновки стануть про гроші, а не про середнє', hintLink: 'Відкрити налаштування',
  },
}
